import type {
  FieldDefinition,
  PdfConsumerMetadata,
  RuleExpression,
} from '../fieldDefinition';
import { defineFieldId } from '../ids';
import widgetMappings from './aosV1WidgetMappings.json';

// Bumped from 'aos-v1.0.0' to 'aos-v1.1.0' on the
// commit that wires `Answer` persistence (per phase-1-plan.md
// §"Out-of-scope" item on `Answer` schema migrations and the matching
// `packages/domain/README.md` §"Reading a `FieldDefinition`" item 2).
// The catalog's field shape did not change; only the persistence
// contract did. Consumers loading an `aos-v1.0.0` snapshot must
// receive a `schema_version_mismatch` warning from the persistence
// adapter (see packages/form-engine/src/persistence/).
export const AOS_V1_SCHEMA_VERSION = 'aos-v1.1.0';

// Phase 0.5b: hydrate `widgetNames` per consumer from the inventory-derived
// JSON map. The aggregator at the bottom of this file applies this wrapper to
// every field's pdfConsumers at module init. Synthetic / cross-consumer-deferred
// fields (sponsor.signature.contract_acknowledged, deferred I-131 advance-parole
// projections) carry no inventory binding and therefore retain
// `widgetNames === undefined` — see docs/rebuild/phase-0-plan.md §"Phase 0.5b".
type WidgetMappingTable = Record<string, Record<string, readonly string[]>>;
const widgetMappingsTyped: WidgetMappingTable =
  widgetMappings as WidgetMappingTable;

function consumerWithWidgets(
  fieldId: string,
  consumer: PdfConsumerMetadata,
): PdfConsumerMetadata {
  const widgets = widgetMappingsTyped[fieldId]?.[consumer.formId];
  if (!widgets || widgets.length === 0) return consumer;
  // Don't overwrite an inline widgetNames; back-compat for any future
  // hand-authored entry that intentionally narrows widget binding.
  if (consumer.widgetNames && consumer.widgetNames.length > 0) return consumer;
  return { ...consumer, widgetNames: widgets };
}

function hydrateFieldWidgets(field: FieldDefinition): FieldDefinition {
  return {
    ...field,
    pdfConsumers: field.pdfConsumers.map(c => consumerWithWidgets(field.id, c)),
  };
}

export const I_485_2024_10_MAPPING_VERSION = 'i-485-2024-10-v0';
export const I_765_2024_04_MAPPING_VERSION = 'i-765-2024-04-v0';
export const I_130_2024_04_MAPPING_VERSION = 'i-130-2024-04-v0';
export const I_130A_2024_04_MAPPING_VERSION = 'i-130a-2024-04-v0';
export const I_864_2024_10_MAPPING_VERSION = 'i-864-2024-10-v0';
export const I_131_2025_01_MAPPING_VERSION = 'i-131-2025-01-v0';
export const G_1145_2014_09_MAPPING_VERSION = 'g-1145-2014-09-v0';

const i485Consumer = {
  formId: 'i-485',
  editionDate: '2024-10',
  mappingVersion: I_485_2024_10_MAPPING_VERSION,
  required: true,
};

const i765Consumer = {
  formId: 'i-765',
  editionDate: '2024-04',
  mappingVersion: I_765_2024_04_MAPPING_VERSION,
  required: true,
};

const i130Consumer = {
  formId: 'i-130',
  editionDate: '2024-04',
  mappingVersion: I_130_2024_04_MAPPING_VERSION,
  required: true,
};

const i130aConsumer = {
  formId: 'i-130a',
  editionDate: '2024-04',
  mappingVersion: I_130A_2024_04_MAPPING_VERSION,
  required: true,
};

const i864Consumer = {
  formId: 'i-864',
  editionDate: '2024-10',
  mappingVersion: I_864_2024_10_MAPPING_VERSION,
  required: true,
};

const i131Consumer = {
  formId: 'i-131',
  editionDate: '2025-01',
  mappingVersion: I_131_2025_01_MAPPING_VERSION,
  required: true,
};

// G-1145 is the e-Notification opt-in slip every applicant clips on top
// of their packet. Only 5 user-facing widgets (last/first/middle name,
// email, mobile phone) and they reuse the beneficiary identity + contact
// catalog fields verbatim.
const g1145Consumer = {
  formId: 'g-1145',
  editionDate: '2014-09',
  mappingVersion: G_1145_2014_09_MAPPING_VERSION,
  required: false,
};

// ----- Repeatable-group bounds -------------------------------------------
// Each field tagged with a `repeatableGroupId` carries a `repeatableBound`
// that records the hard upper bound the form engine must enforce. Bounds are
// derived from the underlying USCIS PDF row counts (e.g., I-485 Pt3 has 3
// address-history slots; I-130 Pt2 has 5; I-485 Pt14 has 7 additional-info
// rows). Overflow strategy is `'additional_info'` for fields that cascade to
// the form's Additional Information part, `'truncate'` when the engine drops
// extras silently, and `'reject'` when the count is exact (e.g., 3 tax years).

const NAME_HISTORY_BOUND = {
  max: 5,
  overflowStrategy: 'additional_info',
} as const;

const ADDITIONAL_BIRTH_DATES_BOUND = {
  max: 5,
  overflowStrategy: 'additional_info',
} as const;

const OTHER_A_NUMBERS_BOUND = {
  max: 5,
  overflowStrategy: 'additional_info',
} as const;

// Bumped beneficiary address + employment from
// max:3 → max:5 to cover the I-485 instructions' explicit 5-year
// history requirement. The user audit surfaced this as the "major
// ache past years" — applicants frequently have 4–5 addresses or jobs
// in 5 years, but the form only accepted 3 catalog instances. The
// Cascade-to-Additional-Information path (`overflowStrategy:
// 'additional_info'`) handles instances 4+ on forms whose row widget
// caps are tighter than 5 (e.g., I-485 Pt3 has 3 prior-address rows;
// rows 4 and 5 cascade to AdditionalInfo slots). Sponsor bounds left
// unchanged: sponsor address is already 5; sponsor employment is 2
// per the I-130 petitioner-employment widget structure (current +
// previous).
const BENEFICIARY_ADDRESS_HISTORY_BOUND = {
  min: 1,
  max: 5,
  overflowStrategy: 'additional_info',
} as const;

const SPONSOR_ADDRESS_HISTORY_BOUND = {
  min: 1,
  max: 5,
  overflowStrategy: 'additional_info',
} as const;

const BENEFICIARY_EMPLOYMENT_HISTORY_BOUND = {
  max: 5,
  overflowStrategy: 'additional_info',
} as const;

const SPONSOR_EMPLOYMENT_HISTORY_BOUND = {
  max: 2,
  overflowStrategy: 'additional_info',
} as const;

const BENEFICIARY_CHILDREN_BOUND = {
  min: 0,
  max: 8,
  overflowStrategy: 'additional_info',
} as const;

const PRIOR_MARRIAGES_BOUND = {
  max: 5,
  overflowStrategy: 'additional_info',
} as const;

// Per Form I-864 Page 6 Item 16 instructions: "You MUST attach a
// photocopy or transcript of your Federal income tax return for
// ONLY the most recent tax year… If you believe additional returns
// may help you to establish your ability to maintain sufficient
// income, you may submit transcripts… for the three most recent
// years." → only the most recent year is required; years 2 and 3
// are optional supplementary evidence the sponsor can add.
const SPONSOR_TAX_HISTORY_BOUND = {
  min: 1,
  max: 3,
  overflowStrategy: 'reject',
} as const;

const SPONSOR_HOUSEHOLD_INCOME_BOUND = {
  max: 4,
  overflowStrategy: 'additional_info',
} as const;

const ELIGIBILITY_ORG_MEMBERSHIPS_BOUND = {
  max: 5,
  overflowStrategy: 'additional_info',
} as const;

const ELIGIBILITY_PUBLIC_BENEFITS_BOUND = {
  // I-485 Pt8Line68c is a 4×4 table — one row per applicant period.
  max: 4,
  overflowStrategy: 'additional_info',
} as const;

const ELIGIBILITY_LONG_TERM_INSTITUTIONALIZATION_BOUND = {
  // I-485 Pt8Line68d is a 4-row table.
  max: 4,
  overflowStrategy: 'additional_info',
} as const;

const DOCUMENT_GROUP_BOUND = {
  // Conservative default for document-evidence repeatable groups
  // (criminal dispositions, conviction dispositions, prior marriage docs,
  // joint sponsor packets, per-child documents).
  max: 5,
  overflowStrategy: 'additional_info',
} as const;

const ADDITIONAL_INFO_BOUND = {
  // I-485 Pt14 has 7 slots; I-864 Pt11 has 6; I-765 Pt6 has 7; I-130A has 4.
  // The semantic field carries the lowest common bound; per-form caps are
  // resolved by the Phase 1 PDF writer.
  max: 4,
  overflowStrategy: 'additional_info',
} as const;

export const beneficiaryLegalNameFields = [
  {
    id: defineFieldId('beneficiary.name.legal.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Legal first name',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [i485Consumer, i131Consumer,
      { ...i130Consumer, required: false },
      { ...i130aConsumer, required: false },
      { ...i765Consumer, required: false },
      { ...i864Consumer, required: false },
      g1145Consumer,
    ],
  },
  {
    id: defineFieldId('beneficiary.name.legal.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Legal middle name',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [i485Consumer, { ...i131Consumer, required: false },
      { ...i130Consumer, required: false },
      { ...i130aConsumer, required: false },
      { ...i765Consumer, required: false },
      { ...i864Consumer, required: false },
      g1145Consumer,
    ],
  },
  {
    id: defineFieldId('beneficiary.name.legal.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Legal last name',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [i485Consumer, i131Consumer,
      { ...i130Consumer, required: false },
      { ...i130aConsumer, required: false },
      { ...i765Consumer, required: false },
      { ...i864Consumer, required: false },
      g1145Consumer,
    ],
  },
] as const satisfies readonly FieldDefinition[];

const hasUsedOtherNamesFieldId = defineFieldId(
  'beneficiary.name.other.has_used',
);

const requiredWhenOtherNamesUsed = {
  op: 'equals',
  fieldId: hasUsedOtherNamesFieldId,
  value: true,
} as const;

export const beneficiaryOtherNamesFields = [
  {
    id: hasUsedOtherNamesFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Has used other names',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
  },
  {
    id: defineFieldId('beneficiary.name.other.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Other legal first name',
    valueType: 'text',
    required: requiredWhenOtherNamesUsed,
    visibility: requiredWhenOtherNamesUsed,
    repeatableGroupId: 'beneficiary.other_names',
    repeatableBound: NAME_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i131Consumer, required: false },
      { ...i130Consumer, required: false },
      { ...i765Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.name.other.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Other legal middle name',
    valueType: 'text',
    required: false,
    visibility: requiredWhenOtherNamesUsed,
    repeatableGroupId: 'beneficiary.other_names',
    repeatableBound: NAME_HISTORY_BOUND,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i131Consumer, required: false },
      { ...i130Consumer, required: false },
      { ...i765Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.name.other.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Other legal last name',
    valueType: 'text',
    required: requiredWhenOtherNamesUsed,
    visibility: requiredWhenOtherNamesUsed,
    repeatableGroupId: 'beneficiary.other_names',
    repeatableBound: NAME_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i131Consumer, required: false },
      { ...i130Consumer, required: false },
      { ...i765Consumer, required: false },
    ],
  },
] as const satisfies readonly FieldDefinition[];

// ─── beneficiary.name.at_birth ───────────────────────────────────
// Beneficiary's birth name (separate from .legal — most users have
// the same legal name as their birth name, but immigrants whose name
// changed during naturalization, adoption, or marriage need both).
// Closes a previously-unmodeled concept flagged in the 2026-05-09
// architecture review. Empty pdfConsumers for now — I-485 doesn't
// have a birth-name widget, and the I-130 Pt2 binding lives in the
// deferred Pt4 inventory work tracked in Slice 9 ratchet.
const hasDifferentBirthNameFieldId = defineFieldId(
  'beneficiary.name.at_birth.has_different',
);
const requiredWhenBirthNameDiffers = {
  op: 'equals',
  fieldId: hasDifferentBirthNameFieldId,
  value: true,
} as const;

export const beneficiaryBirthNameFields = [
  {
    id: hasDifferentBirthNameFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Is your name at birth different from your current legal name?',
    helpText:
      'Check if your legal name changed after birth (e.g., through marriage, naturalization, or adoption). Most applicants answer No.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
  },
  {
    id: defineFieldId('beneficiary.name.at_birth.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'First name at birth',
    valueType: 'text',
    required: requiredWhenBirthNameDiffers,
    visibility: requiredWhenBirthNameDiffers,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [],
  },
  {
    id: defineFieldId('beneficiary.name.at_birth.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Middle name at birth',
    valueType: 'text',
    required: false,
    visibility: requiredWhenBirthNameDiffers,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [],
  },
  {
    id: defineFieldId('beneficiary.name.at_birth.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Last name at birth',
    valueType: 'text',
    required: requiredWhenBirthNameDiffers,
    visibility: requiredWhenBirthNameDiffers,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [],
  },
] as const satisfies readonly FieldDefinition[];

// ─── beneficiary.intended_address ────────────────────────────────
// Where the beneficiary intends to live in the U.S. after adjustment.
// Distinct from `beneficiary.current_address` — current is "where I am
// today", intended is "where I'll be after the green card lands". For
// most applicants the two are the same. The I-130 Pt4 Line 12 widget
// set binds to this; binding is deferred to the Pt4 inventory work.
const hasIntendedAddressDiffersFieldId = defineFieldId(
  'beneficiary.intended_address.differs_from_current',
);
const requiredWhenIntendedDiffers = {
  op: 'equals',
  fieldId: hasIntendedAddressDiffersFieldId,
  value: true,
} as const;

const intendedAddressUnitTypeFieldId = defineFieldId(
  'beneficiary.intended_address.unit_type',
);

const requiredWhenIntendedUnitTypeSelected: RuleExpression = {
  op: 'and',
  rules: [
    requiredWhenIntendedDiffers,
    {
      op: 'exists',
      fieldId: intendedAddressUnitTypeFieldId,
    },
  ],
};

export const beneficiaryIntendedAddressFields = [
  {
    id: hasIntendedAddressDiffersFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label:
      'Do you intend to live at a different U.S. address after your green card is approved?',
    helpText:
      'If you plan to move when your green card arrives, give the new address here. If you plan to stay put, leave this No.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
  },
  {
    id: defineFieldId('beneficiary.intended_address.street_number_and_name'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Intended U.S. street number and name',
    valueType: 'text',
    required: requiredWhenIntendedDiffers,
    visibility: requiredWhenIntendedDiffers,
    validation: [{ type: 'required' }, { type: 'max_length', value: 25 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.intended_address.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Intended U.S. city or town',
    valueType: 'text',
    required: requiredWhenIntendedDiffers,
    visibility: requiredWhenIntendedDiffers,
    validation: [{ type: 'required' }, { type: 'max_length', value: 20 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.intended_address.state'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Intended U.S. state',
    valueType: 'select',
    required: requiredWhenIntendedDiffers,
    visibility: requiredWhenIntendedDiffers,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.intended_address.zip_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Intended U.S. ZIP code',
    valueType: 'text',
    required: requiredWhenIntendedDiffers,
    visibility: requiredWhenIntendedDiffers,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 5 },
      { type: 'pattern', pattern: '^\\d{5}$' },
    ],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: intendedAddressUnitTypeFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Intended U.S. unit type',
    helpText: 'Supported PDF values are Apt, Ste, and Flr.',
    valueType: 'select',
    required: false,
    visibility: requiredWhenIntendedDiffers,
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  // i-130 Pt4Line12b unit number — shown only when unit_type is selected.
  {
    id: defineFieldId('beneficiary.intended_address.unit_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Intended U.S. apt/ste/flr number',
    valueType: 'text',
    required: requiredWhenIntendedUnitTypeSelected,
    visibility: requiredWhenIntendedUnitTypeSelected,
    validation: [{ type: 'required' }, { type: 'max_length', value: 8 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

// ─── beneficiary.foreign_address ─────────────────────────────────
// Beneficiary's address abroad (if any). For applicants whose current
// address is U.S.-based but who maintain a foreign residence — common
// for K-3/CR-1 transitions and consular processing pre-adjustment.
// The I-130 Pt4 Line 13 widget set binds to this; binding deferred
// to the Pt4 inventory work.
const hasForeignAddressFieldId = defineFieldId(
  'beneficiary.foreign_address.has',
);
const requiredWhenForeignAddressPresent = {
  op: 'equals',
  fieldId: hasForeignAddressFieldId,
  value: true,
} as const;

const foreignAddressCountryFieldId = defineFieldId(
  'beneficiary.foreign_address.country',
);
const foreignAddressUnitTypeFieldId = defineFieldId(
  'beneficiary.foreign_address.unit_type',
);

const requiredWhenForeignAddressUnitTypeSelected: RuleExpression = {
  op: 'and',
  rules: [
    requiredWhenForeignAddressPresent,
    {
      op: 'exists',
      fieldId: foreignAddressUnitTypeFieldId,
    },
  ],
};

// ─── beneficiary.most_recent_foreign_address ─────────────────────
// I-485 01/20/25 Pt1Line18_Recent* — "Most Recent Address Outside
// the United States where you lived for more than one year (if not
// already listed above)". Optional; only filled when the prior-
// address list does NOT already include a qualifying foreign
// address (e.g., applicant moved foreign country → US-A → US-B,
// and the 5-year prior history is entirely US). USCIS instruction
// allows the field to stay blank when the foreign address is in
// the prior-address list.
const MOST_RECENT_FOREIGN_HELP =
  'Skip if your most recent foreign address (where you lived > 1 year) is already in your prior-address list above. Fill in only when it is NOT listed elsewhere — e.g., you have multiple US prior addresses but the foreign one is older.';

const mostRecentForeignAddressCountryFieldId = defineFieldId(
  'beneficiary.most_recent_foreign_address.country',
);

const mostRecentForeignAddressCountryIsUs: RuleExpression = {
  op: 'equals',
  fieldId: mostRecentForeignAddressCountryFieldId,
  value: 'US',
};

export const beneficiaryMostRecentForeignAddressFields = [
  {
    id: defineFieldId(
      'beneficiary.most_recent_foreign_address.street_number_and_name',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Most recent foreign street number and name',
    helpText: MOST_RECENT_FOREIGN_HELP,
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 25 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId(
      'beneficiary.most_recent_foreign_address.unit_type',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Most recent foreign unit type',
    helpText: 'Apt / Ste / Flr.',
    valueType: 'select',
    required: false,
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          apt: 'Pt1Line18_RecentUnit[0]',
          ste: 'Pt1Line18_RecentUnit[1]',
          flr: 'Pt1Line18_RecentUnit[2]',
        },
      },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId(
      'beneficiary.most_recent_foreign_address.unit_number',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Most recent foreign apt/suite/floor number',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 8 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.most_recent_foreign_address.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Most recent foreign city or town',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.most_recent_foreign_address.state'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Most recent foreign state (rare — only when applicable)',
    valueType: 'text',
    required: false,
    visibility: mostRecentForeignAddressCountryIsUs,
    validation: [{ type: 'max_length', value: 30 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId(
      'beneficiary.most_recent_foreign_address.zip_code',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Most recent foreign ZIP code (US territory addresses only)',
    valueType: 'text',
    required: false,
    visibility: mostRecentForeignAddressCountryIsUs,
    validation: [{ type: 'max_length', value: 10 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId(
      'beneficiary.most_recent_foreign_address.province',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Most recent foreign province',
    valueType: 'text',
    required: false,
    visibility: { op: 'not', rule: mostRecentForeignAddressCountryIsUs },
    validation: [{ type: 'max_length', value: 40 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId(
      'beneficiary.most_recent_foreign_address.postal_code',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Most recent foreign postal code',
    valueType: 'text',
    required: false,
    visibility: { op: 'not', rule: mostRecentForeignAddressCountryIsUs },
    validation: [{ type: 'max_length', value: 12 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: mostRecentForeignAddressCountryFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Most recent foreign country',
    valueType: 'select',
    required: false,
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId(
      'beneficiary.most_recent_foreign_address.date_from',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Most recent foreign — date moved in',
    valueType: 'date',
    required: false,
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.most_recent_foreign_address.date_to'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Most recent foreign — date moved out',
    valueType: 'date',
    required: false,
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
] as const satisfies readonly FieldDefinition[];

export const beneficiaryForeignAddressFields = [
  {
    id: hasForeignAddressFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Do you currently have an address outside the United States?',
    helpText:
      'Check Yes if you maintain a residence in another country (e.g., a family home you return to). Most applicants who live in the U.S. answer No.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
  },
  {
    id: defineFieldId('beneficiary.foreign_address.street_number_and_name'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Foreign street number and name',
    valueType: 'text',
    required: requiredWhenForeignAddressPresent,
    visibility: requiredWhenForeignAddressPresent,
    validation: [{ type: 'required' }, { type: 'max_length', value: 25 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.foreign_address.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Foreign city or town',
    valueType: 'text',
    required: requiredWhenForeignAddressPresent,
    visibility: requiredWhenForeignAddressPresent,
    validation: [{ type: 'required' }, { type: 'max_length', value: 20 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: foreignAddressCountryFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Foreign country',
    valueType: 'text',
    required: requiredWhenForeignAddressPresent,
    visibility: requiredWhenForeignAddressPresent,
    validation: [{ type: 'required' }, { type: 'max_length', value: 60 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: foreignAddressUnitTypeFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Foreign address unit type',
    helpText: 'Apt / Ste / Flr.',
    valueType: 'select',
    required: false,
    visibility: requiredWhenForeignAddressPresent,
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.foreign_address.unit_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Foreign apt/ste/flr number',
    valueType: 'text',
    required: requiredWhenForeignAddressUnitTypeSelected,
    visibility: requiredWhenForeignAddressUnitTypeSelected,
    validation: [{ type: 'required' }, { type: 'max_length', value: 8 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.foreign_address.province'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Foreign province',
    valueType: 'text',
    required: false,
    visibility: requiredWhenForeignAddressPresent,
    validation: [{ type: 'max_length', value: 40 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.foreign_address.postal_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Foreign postal code',
    valueType: 'text',
    required: false,
    visibility: requiredWhenForeignAddressPresent,
    validation: [{ type: 'max_length', value: 12 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

const hasUsedAdditionalBirthDatesFieldId = defineFieldId(
  'beneficiary.birth.additional.has_used',
);

const requiredWhenAdditionalBirthDatesUsed = {
  op: 'equals',
  fieldId: hasUsedAdditionalBirthDatesFieldId,
  value: true,
} as const;

export const beneficiaryBirthFields = [
  {
    id: defineFieldId('beneficiary.birth.date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Date of birth',
    valueType: 'date',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer, i131Consumer,
      { ...i130Consumer, required: false },
      { ...i765Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: hasUsedAdditionalBirthDatesFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Has used additional dates of birth',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  {
    id: defineFieldId('beneficiary.birth.additional.count'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Number of additional dates of birth used',
    helpText:
      'Legacy applicant UI allowed up to two additional dates of birth.',
    valueType: 'number',
    required: requiredWhenAdditionalBirthDatesUsed,
    visibility: requiredWhenAdditionalBirthDatesUsed,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
  },
  {
    id: defineFieldId('beneficiary.birth.additional.date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Additional date of birth',
    valueType: 'date',
    required: requiredWhenAdditionalBirthDatesUsed,
    visibility: requiredWhenAdditionalBirthDatesUsed,
    repeatableGroupId: 'beneficiary.additional_birth_dates',
    repeatableBound: ADDITIONAL_BIRTH_DATES_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.birth.place.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'City or town of birth',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [i485Consumer,
      { ...i130Consumer, required: false },
      { ...i765Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.birth.place.region'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'State or province of birth',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i765Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.birth.place.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Country of birth',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer, i131Consumer,
      { ...i130Consumer, required: false },
      { ...i765Consumer, required: false },
    ],
  },
] as const satisfies readonly FieldDefinition[];

const hasSecondaryCitizenshipFieldId = defineFieldId(
  'beneficiary.citizenship.has_secondary_country',
);

const requiredWhenSecondaryCitizenshipExists = {
  op: 'equals',
  fieldId: hasSecondaryCitizenshipFieldId,
  value: true,
} as const;

export const beneficiaryDemographicFields = [
  {
    id: defineFieldId('beneficiary.demographics.sex'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Sex',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        // Projection keys MUST match the catalog enum keys exposed by
        // optionsResolver SEX_OPTIONS (`male`/`female`/`nonbinary`).
        // Earlier `F`/`M` keys leaked into the UI dropdown as separate
        // options ("Male" + "M", "Female" + "F"). `nonbinary` has no
        // USCIS widget — engine silently no-ops it.
        widgetValueProjection: {
          female: 'Pt1Line6_CB_Sex[0]',
          male: 'Pt1Line6_CB_Sex[1]',
        },
      },
      {
        ...i131Consumer,
        widgetValueProjection: {
          female: 'Part2_Line8_Gender[0]',
          male: 'Part2_Line8_Gender[1]',
        },
      },
      {
        ...i130Consumer,
        required: false,
        widgetValueProjection: {
          female: 'Pt4Line9_Female[0]',
          male: 'Pt4Line9_Male[0]',
        },
      },
      {
        ...i765Consumer,
        required: false,
        // 2026-05-25 — was bound to Line10_Checkbox (marital-status
        // widgets on i-765); the label-vs-binding invariant caught
        // it. Real sex widgets are Line9_Checkbox: [0]=Female, [1]=Male.
        widgetValueProjection: {
          male: 'Line9_Checkbox[1]',
          female: 'Line9_Checkbox[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('beneficiary.citizenship.primary_country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Primary country of citizenship or nationality',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer, i131Consumer,
      { ...i765Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: hasSecondaryCitizenshipFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Has more than one country of citizenship or nationality',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
  },
  {
    id: defineFieldId('beneficiary.citizenship.secondary_country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Secondary country of citizenship or nationality',
    valueType: 'select',
    required: requiredWhenSecondaryCitizenshipExists,
    visibility: requiredWhenSecondaryCitizenshipExists,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

export const beneficiaryIdentifierFields = [
  // Phase A — Do you have an A-Number? (Yes/No, Item 4)
  {
    id: defineFieldId(
      'beneficiary.identifiers.has_alien_registration_number',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Do you have an Alien Registration Number (A-Number)?',
    helpText:
      'I-485 01/20/25 Pt1Line4_YN. Gates the A-Number text input below. Default No for first-time applicants without a prior USCIS encounter.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  {
    id: defineFieldId('beneficiary.identifiers.alien_registration_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Alien registration number (A-Number)',
    helpText: 'Legacy applicant UI stored this as aNumber.',
    valueType: 'text',
    required: false,
    // Only shown when the applicant has answered Yes to "Do you
    // have an A-Number?" above. Without this gate, the empty text
    // box rendered immediately below a No answer — confusing UX
    // because the question's prompt offered "No" as a valid path.
    visibility: {
      op: 'equals',
      fieldId: defineFieldId(
        'beneficiary.identifiers.has_alien_registration_number',
      ),
      value: true,
    },
    validation: [
      { type: 'max_length', value: 9 },
      { type: 'pattern', pattern: '^\\d{7,9}$' },
    ],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i131Consumer, required: false },
      { ...i130Consumer, required: false },
      { ...i130aConsumer, required: false },
      { ...i765Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId(
      'beneficiary.identifiers.has_other_alien_registration_numbers',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Have you ever used, or been assigned, any other A-Number?',
    helpText:
      'I-485 01/20/25 Pt1Line5_YN. Gates the additional A-Number rows below.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  {
    id: defineFieldId(
      'beneficiary.identifiers.other_alien_registration_number',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Other alien registration number used',
    helpText:
      'I-485 01/20/25 Pt1Line5A/5B_ANumber rows (item 5; up to 2 additional A-Numbers). Additional rows past 2 cascade to Part 14.',
    valueType: 'text',
    required: false,
    // Gated on the Yes/No above — applicants who answered "No" to
    // "Have you ever used any other A-Number?" don't see the rows.
    visibility: {
      op: 'equals',
      fieldId: defineFieldId(
        'beneficiary.identifiers.has_other_alien_registration_numbers',
      ),
      value: true,
    },
    repeatableGroupId: 'beneficiary.other_alien_registration_numbers',
    repeatableBound: OTHER_A_NUMBERS_BOUND,
    validation: [
      { type: 'max_length', value: 9 },
      { type: 'pattern', pattern: '^\\d{7,9}$' },
    ],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.identifiers.uscis_online_account_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'USCIS online account number',
    helpText: 'Legacy applicant UI stored this as uscisNumber.',
    valueType: 'text',
    required: false,
    validation: [
      { type: 'max_length', value: 12 },
      { type: 'pattern', pattern: '^\\d{12}$' },
    ],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i131Consumer, required: false },
      { ...i130Consumer, required: false },
      { ...i130aConsumer, required: false },
      { ...i765Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
] as const satisfies readonly FieldDefinition[];

const mailingAddressSameAsCurrentPhysicalFieldId = defineFieldId(
  'beneficiary.mailing_address.same_as_current_physical',
);

const requiredWhenMailingAddressDiffers: RuleExpression = {
  op: 'equals',
  fieldId: mailingAddressSameAsCurrentPhysicalFieldId,
  value: false,
};

const mailingAddressUnitTypeFieldId = defineFieldId(
  'beneficiary.mailing_address.unit_type',
);

const requiredWhenMailingUnitTypeSelected: RuleExpression = {
  op: 'and',
  rules: [
    requiredWhenMailingAddressDiffers,
    {
      op: 'exists',
      fieldId: mailingAddressUnitTypeFieldId,
    },
  ],
};

// Country-aware visibility for the mailing-address state/ZIP vs
// province/postal-code pair. Mailing differs AND country IS US →
// show state + zip. Mailing differs AND country is NOT US → show
// province + postal. Without this gate, ALL FOUR widgets render
// simultaneously on the mailing-differs page (the user flagged
// "Mailing Address shows both (State + ZIP) and (State/Province +
// Postal code)").
const mailingAddressCountryFieldId = defineFieldId(
  'beneficiary.mailing_address.country',
);
const requiredWhenMailingDiffersAndCountryIsUs: RuleExpression = {
  op: 'and',
  rules: [
    requiredWhenMailingAddressDiffers,
    {
      op: 'equals',
      fieldId: mailingAddressCountryFieldId,
      value: 'US',
    },
  ],
};
const requiredWhenMailingDiffersAndCountryIsNotUs: RuleExpression = {
  op: 'and',
  rules: [
    requiredWhenMailingAddressDiffers,
    {
      op: 'not',
      rule: {
        op: 'equals',
        fieldId: mailingAddressCountryFieldId,
        value: 'US',
      },
    },
  ],
};

export const beneficiaryMailingAddressFields = [
  {
    id: mailingAddressSameAsCurrentPhysicalFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'U.S. mailing address is the current physical address',
    helpText: 'Legacy applicant UI stored the inverse as mailingDifferent.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i765Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.mailing_address.in_care_of_name'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'U.S. mailing in care of name',
    valueType: 'text',
    required: false,
    visibility: requiredWhenMailingAddressDiffers,
    validation: [{ type: 'max_length', value: 34 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i131Consumer, required: false },
      { ...i864Consumer, required: false },
      { ...i765Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.mailing_address.street_number_and_name'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'U.S. mailing street number and name',
    valueType: 'text',
    required: requiredWhenMailingAddressDiffers,
    visibility: requiredWhenMailingAddressDiffers,
    validation: [{ type: 'required' }, { type: 'max_length', value: 25 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i131Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: mailingAddressUnitTypeFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'U.S. mailing unit type',
    helpText: 'Supported PDF values are Apt, Ste, and Flr.',
    valueType: 'select',
    required: false,
    visibility: requiredWhenMailingAddressDiffers,
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i131Consumer, required: false },
      { ...i864Consumer, required: false },
      { ...i765Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.mailing_address.unit_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'U.S. mailing apartment, suite, or floor number',
    valueType: 'text',
    required: requiredWhenMailingUnitTypeSelected,
    visibility: requiredWhenMailingUnitTypeSelected,
    validation: [{ type: 'required' }, { type: 'max_length', value: 6 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i131Consumer, required: false },
      { ...i864Consumer, required: false },
      { ...i765Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.mailing_address.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'U.S. mailing city or town',
    valueType: 'text',
    required: requiredWhenMailingAddressDiffers,
    visibility: requiredWhenMailingAddressDiffers,
    validation: [{ type: 'required' }, { type: 'max_length', value: 20 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i131Consumer, required: false },
      { ...i864Consumer, required: false },
      { ...i765Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.mailing_address.state'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'U.S. mailing state',
    valueType: 'select',
    required: requiredWhenMailingDiffersAndCountryIsUs,
    visibility: requiredWhenMailingDiffersAndCountryIsUs,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i131Consumer, required: false },
      { ...i864Consumer, required: false },
      { ...i765Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.mailing_address.zip_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'U.S. mailing ZIP code',
    valueType: 'text',
    required: requiredWhenMailingDiffersAndCountryIsUs,
    visibility: requiredWhenMailingDiffersAndCountryIsUs,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 5 },
      { type: 'pattern', pattern: '^\\d{5}$' },
    ],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i131Consumer, required: false },
      { ...i864Consumer, required: false },
      { ...i765Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.mailing_address.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Mailing country (non-U.S.)',
    helpText:
      'I-131 Part2 Line 3 / I-864 P2_Line2 — country of the beneficiary mailing address when it is outside the United States. The I-485 2024-10 mailing address is U.S.-only and has no country widget.',
    valueType: 'text',
    required: false,
    visibility: requiredWhenMailingAddressDiffers,
    validation: [{ type: 'max_length', value: 60 }],
    pdfConsumers: [
      {
        ...i131Consumer,
        required: false,
        widgetNames: ['Part2_Line3_Country[0]'],
      },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.mailing_address.province'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Mailing province (non-U.S.)',
    helpText:
      'I-131 Part2 Line 3 / I-864 P2_Line2 — province / state of the beneficiary mailing address when it is outside the United States. Optional companion of `beneficiary.mailing_address.country`.',
    valueType: 'text',
    required: false,
    visibility: requiredWhenMailingDiffersAndCountryIsNotUs,
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [
      {
        ...i131Consumer,
        required: false,
        widgetNames: ['Part2_Line3_Province[0]'],
      },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.mailing_address.postal_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Mailing postal code (non-U.S.)',
    helpText:
      'I-131 Part2 Line 3 / I-864 P2_Line2 — postal code of the beneficiary mailing address when it is outside the United States. Distinct from the U.S. ZIP-code widget (`Part2_Line3_ZipCode[0]`) which is bound to `beneficiary.mailing_address.zip_code`.',
    valueType: 'text',
    required: false,
    visibility: requiredWhenMailingDiffersAndCountryIsNotUs,
    validation: [{ type: 'max_length', value: 10 }],
    pdfConsumers: [
      {
        ...i131Consumer,
        required: false,
        widgetNames: ['Part2_Line3_PostalCode[0]'],
      },
      { ...i864Consumer, required: false },
    ],
  },
] as const satisfies readonly FieldDefinition[];

const currentPhysicalCountryFieldId = defineFieldId(
  'beneficiary.current_physical_address.country',
);

const currentPhysicalUnitTypeFieldId = defineFieldId(
  'beneficiary.current_physical_address.unit_type',
);

const requiredWhenCurrentPhysicalCountryIsUs: RuleExpression = {
  op: 'equals',
  fieldId: currentPhysicalCountryFieldId,
  value: 'US',
};

const requiredWhenCurrentPhysicalUnitTypeSelected: RuleExpression = {
  op: 'exists',
  fieldId: currentPhysicalUnitTypeFieldId,
};

export const beneficiaryCurrentPhysicalAddressFields = [
  {
    id: defineFieldId(
      'beneficiary.current_physical_address.street_number_and_name',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Current physical street number and name',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 25 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i765Consumer, required: true },
      { ...i130Consumer, required: true },
      { ...i131Consumer, required: false },
    ],
  },
  {
    id: currentPhysicalUnitTypeFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Current physical unit type',
    helpText: 'Supported PDF values are Apt, Ste, and Flr.',
    valueType: 'select',
    required: false,
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i765Consumer, required: false },
      { ...i130Consumer, required: false },
      { ...i131Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.current_physical_address.unit_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Current physical apartment, suite, or floor number',
    valueType: 'text',
    required: requiredWhenCurrentPhysicalUnitTypeSelected,
    visibility: requiredWhenCurrentPhysicalUnitTypeSelected,
    validation: [{ type: 'required' }, { type: 'max_length', value: 6 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i765Consumer, required: false },
      { ...i130Consumer, required: false },
      { ...i131Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.current_physical_address.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Current physical city or town',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 20 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i765Consumer, required: true },
      { ...i130Consumer, required: true },
      { ...i131Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.current_physical_address.state'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Current physical state (U.S.)',
    valueType: 'select',
    required: requiredWhenCurrentPhysicalCountryIsUs,
    visibility: requiredWhenCurrentPhysicalCountryIsUs,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i765Consumer, required: false },
      { ...i130Consumer, required: false },
      { ...i131Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.current_physical_address.zip_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Current physical ZIP code (U.S.)',
    valueType: 'text',
    required: requiredWhenCurrentPhysicalCountryIsUs,
    visibility: requiredWhenCurrentPhysicalCountryIsUs,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 5 },
      { type: 'pattern', pattern: '^\\d{5}$' },
    ],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i765Consumer, required: false },
      { ...i130Consumer, required: false },
      { ...i131Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.current_physical_address.province'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Current physical province (non-U.S.)',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'not',
      rule: requiredWhenCurrentPhysicalCountryIsUs,
    },
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
      { ...i131Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.current_physical_address.postal_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Current physical postal code (non-U.S.)',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'not',
      rule: requiredWhenCurrentPhysicalCountryIsUs,
    },
    validation: [{ type: 'max_length', value: 10 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
      { ...i131Consumer, required: false },
    ],
  },
  {
    id: currentPhysicalCountryFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Current physical country',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130Consumer, required: true },
      { ...i131Consumer, required: false },
    ],
  },
  {
    id: defineFieldId(
      'beneficiary.current_physical_address.residence.date_from',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Current physical address date of residence, from',
    helpText:
      'I-485 Part 3 line 6b (date to) is auto-populated to "Present" by the PDF engine.',
    valueType: 'date',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  // Phase A7 — i-485 01/20/25 Item 18: have you resided at your
  // current address for at least 5 years?
  {
    id: defineFieldId(
      'beneficiary.current_physical_address.resided_5yr',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Have you resided at your current address for at least 5 years?',
    helpText:
      'I-485 01/20/25 Pt1Line18_last5yrs_YN. Gates the "most recent prior address" block (Item 18 prior subsection). False → show prior-address inputs.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
] as const satisfies readonly FieldDefinition[];

export const beneficiaryContactFields = [
  {
    id: defineFieldId('beneficiary.contact.daytime_phone'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Daytime telephone number',
    valueType: 'text',
    required: true,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 25 },
    ],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i765Consumer, required: true },
      { ...i130Consumer, required: true },
      { ...i130aConsumer, required: true },
      { ...i131Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.contact.mobile_phone'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Mobile telephone number',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 25 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i765Consumer, required: false },
      { ...i130Consumer, required: false },
      { ...i130aConsumer, required: false },
      { ...i131Consumer, required: false },
      g1145Consumer,
    ],
  },
  {
    id: defineFieldId('beneficiary.contact.email'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'identity',
    partyRole: 'beneficiary',
    label: 'Email address',
    valueType: 'text',
    required: false,
    validation: [
      { type: 'max_length', value: 254 },
      {
        type: 'pattern',
        pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
      },
    ],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i765Consumer, required: false },
      { ...i130Consumer, required: false },
      { ...i130aConsumer, required: false },
      { ...i131Consumer, required: false },
      g1145Consumer,
    ],
  },
] as const satisfies readonly FieldDefinition[];

export const sponsorLegalNameFields = [
  {
    id: defineFieldId('sponsor.name.legal.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Sponsor legal first name',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i130Consumer, required: true },
      { ...i864Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('sponsor.name.legal.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Sponsor legal middle name',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('sponsor.name.legal.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Sponsor legal last name',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i130Consumer, required: true },
      { ...i864Consumer, required: true },
    ],
  },
] as const satisfies readonly FieldDefinition[];

const sponsorHasUsedOtherNamesFieldId = defineFieldId(
  'sponsor.name.other.has_used',
);

const requiredWhenSponsorOtherNamesUsed: RuleExpression = {
  op: 'equals',
  fieldId: sponsorHasUsedOtherNamesFieldId,
  value: true,
};

export const sponsorOtherNamesFields = [
  {
    id: sponsorHasUsedOtherNamesFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Sponsor has used other names',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
  },
  {
    id: defineFieldId('sponsor.name.other.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Sponsor other first name',
    valueType: 'text',
    required: requiredWhenSponsorOtherNamesUsed,
    visibility: requiredWhenSponsorOtherNamesUsed,
    repeatableGroupId: 'sponsor.other_names',
    repeatableBound: NAME_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.name.other.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Sponsor other middle name',
    valueType: 'text',
    required: false,
    visibility: requiredWhenSponsorOtherNamesUsed,
    repeatableGroupId: 'sponsor.other_names',
    repeatableBound: NAME_HISTORY_BOUND,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.name.other.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Sponsor other last name',
    valueType: 'text',
    required: requiredWhenSponsorOtherNamesUsed,
    visibility: requiredWhenSponsorOtherNamesUsed,
    repeatableGroupId: 'sponsor.other_names',
    repeatableBound: NAME_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

export const sponsorBirthFields = [
  {
    id: defineFieldId('sponsor.birth.date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Sponsor date of birth',
    valueType: 'date',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i130Consumer, required: true },
      { ...i864Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('sponsor.birth.place.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Sponsor city or town of birth',
    helpText: 'I-864 does not collect city of birth for the sponsor.',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.birth.place.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Sponsor country of birth',
    helpText:
      'I-864 widget P4_Line7_CityofBirth is mislabeled; it asks for country of birth.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i130Consumer, required: true },
      { ...i864Consumer, required: true },
    ],
  },
] as const satisfies readonly FieldDefinition[];

export const sponsorDemographicFields = [
  {
    id: defineFieldId('sponsor.demographics.sex'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Sponsor sex',
    helpText: 'I-864 does not collect sponsor sex.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.citizenship.status'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Sponsor immigration status',
    helpText:
      'Allowed values: us_citizen, us_national, lawful_permanent_resident. I-130 Part 2 item 36 does not offer us_national; form engine constrains per consumer.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i130Consumer, required: true },
      { ...i864Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('sponsor.domicile.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Sponsor country of domicile',
    helpText: 'I-864 Part 2 item 5. Collected only by I-864.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i864Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.citizenship.acquired_via'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'How did the U.S.-citizen petitioner acquire citizenship?',
    helpText:
      'I-130 Pt2Line23a/b/c (printed Item 37). Pick the single basis: birth_in_us / naturalization / parents. Auto-derived in injectSystemAnswers when birth-place country = US or naturalization certificate number is set; the applicant can override in intake.',
    valueType: 'select',
    required: false,
    pdfConsumers: [
      {
        ...i130Consumer,
        required: false,
        widgetValueProjection: {
          birth_in_us: 'Pt2Line23a_checkbox[0]',
          naturalization: 'Pt2Line23b_checkbox[0]',
          parents: 'Pt2Line23c_checkbox[0]',
        },
      },
    ],
  },
] as const satisfies readonly FieldDefinition[];

export const sponsorIdentifierFields = [
  {
    id: defineFieldId('sponsor.identifiers.ssn'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Sponsor U.S. Social Security Number',
    helpText:
      'Required by I-864 Part 2 item 8 (P4_Line10_SocialSecurityNumber). I-130 item 3 (Pt2Line11_SSN) marks the same value optional.',
    valueType: 'text',
    required: true,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 11 },
      { type: 'pattern', pattern: '^\\d{3}-?\\d{2}-?\\d{4}$' },
    ],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('sponsor.identifiers.alien_registration_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Sponsor alien registration number (A-Number)',
    valueType: 'text',
    required: false,
    validation: [
      { type: 'max_length', value: 9 },
      { type: 'pattern', pattern: '^\\d{7,9}$' },
    ],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('sponsor.identifiers.uscis_online_account_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Sponsor USCIS online account number',
    valueType: 'text',
    required: false,
    validation: [
      { type: 'max_length', value: 12 },
      { type: 'pattern', pattern: '^\\d{12}$' },
    ],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
] as const satisfies readonly FieldDefinition[];

const sponsorMailingSameAsCurrentPhysicalFieldId = defineFieldId(
  'sponsor.mailing_address.same_as_current_physical',
);

const sponsorMailingCountryFieldId = defineFieldId(
  'sponsor.mailing_address.country',
);

const sponsorMailingUnitTypeFieldId = defineFieldId(
  'sponsor.mailing_address.unit_type',
);

const requiredWhenSponsorMailingCountryIsUs: RuleExpression = {
  op: 'equals',
  fieldId: sponsorMailingCountryFieldId,
  value: 'US',
};

const requiredWhenSponsorMailingUnitTypeSelected: RuleExpression = {
  op: 'exists',
  fieldId: sponsorMailingUnitTypeFieldId,
};

export const sponsorMailingAddressFields = [
  {
    id: sponsorMailingSameAsCurrentPhysicalFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor mailing address is the current physical address',
    helpText:
      'I-130 Pt2Line11 (Yes/No) and I-864 P1_Line3_Checkbox (Yes/No). Sponsor mailing is always collected; physical is collected only if this is false.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i130Consumer, required: true },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('sponsor.mailing_address.in_care_of_name'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor mailing in care of name',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 34 }],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('sponsor.mailing_address.street_number_and_name'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor mailing street number and name',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 25 }],
    pdfConsumers: [
      { ...i130Consumer, required: true },
      { ...i864Consumer, required: true },
    ],
  },
  {
    id: sponsorMailingUnitTypeFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor mailing unit type',
    helpText: 'Supported PDF values are Apt, Ste, and Flr.',
    valueType: 'select',
    required: false,
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('sponsor.mailing_address.unit_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor mailing apartment, suite, or floor number',
    valueType: 'text',
    required: requiredWhenSponsorMailingUnitTypeSelected,
    visibility: requiredWhenSponsorMailingUnitTypeSelected,
    validation: [{ type: 'required' }, { type: 'max_length', value: 6 }],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('sponsor.mailing_address.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor mailing city or town',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 20 }],
    pdfConsumers: [
      { ...i130Consumer, required: true },
      { ...i864Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('sponsor.mailing_address.state'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor mailing state (U.S.)',
    valueType: 'select',
    required: requiredWhenSponsorMailingCountryIsUs,
    visibility: requiredWhenSponsorMailingCountryIsUs,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('sponsor.mailing_address.zip_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor mailing ZIP code (U.S.)',
    valueType: 'text',
    required: requiredWhenSponsorMailingCountryIsUs,
    visibility: requiredWhenSponsorMailingCountryIsUs,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 5 },
      { type: 'pattern', pattern: '^\\d{5}$' },
    ],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('sponsor.mailing_address.province'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor mailing province (non-U.S.)',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'not',
      rule: requiredWhenSponsorMailingCountryIsUs,
    },
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('sponsor.mailing_address.postal_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor mailing postal code (non-U.S.)',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'not',
      rule: requiredWhenSponsorMailingCountryIsUs,
    },
    validation: [{ type: 'max_length', value: 10 }],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: sponsorMailingCountryFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor mailing country',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i130Consumer, required: true },
      { ...i864Consumer, required: true },
    ],
  },
] as const satisfies readonly FieldDefinition[];

const sponsorCurrentPhysicalCountryFieldId = defineFieldId(
  'sponsor.current_physical_address.country',
);

const sponsorCurrentPhysicalUnitTypeFieldId = defineFieldId(
  'sponsor.current_physical_address.unit_type',
);

const requiredWhenSponsorPhysicalDiffers: RuleExpression = {
  op: 'equals',
  fieldId: sponsorMailingSameAsCurrentPhysicalFieldId,
  value: false,
};

const requiredWhenSponsorPhysicalCountryIsUs: RuleExpression = {
  op: 'and',
  rules: [
    requiredWhenSponsorPhysicalDiffers,
    {
      op: 'equals',
      fieldId: sponsorCurrentPhysicalCountryFieldId,
      value: 'US',
    },
  ],
};

const requiredWhenSponsorPhysicalUnitTypeSelected: RuleExpression = {
  op: 'and',
  rules: [
    requiredWhenSponsorPhysicalDiffers,
    {
      op: 'exists',
      fieldId: sponsorCurrentPhysicalUnitTypeFieldId,
    },
  ],
};

export const sponsorCurrentPhysicalAddressFields = [
  {
    id: defineFieldId(
      'sponsor.current_physical_address.street_number_and_name',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor current physical street number and name',
    valueType: 'text',
    required: requiredWhenSponsorPhysicalDiffers,
    visibility: requiredWhenSponsorPhysicalDiffers,
    validation: [{ type: 'required' }, { type: 'max_length', value: 25 }],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: sponsorCurrentPhysicalUnitTypeFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor current physical unit type',
    helpText: 'Supported PDF values are Apt, Ste, and Flr.',
    valueType: 'select',
    required: false,
    visibility: requiredWhenSponsorPhysicalDiffers,
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('sponsor.current_physical_address.unit_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor current physical apartment, suite, or floor number',
    valueType: 'text',
    required: requiredWhenSponsorPhysicalUnitTypeSelected,
    visibility: requiredWhenSponsorPhysicalUnitTypeSelected,
    validation: [{ type: 'required' }, { type: 'max_length', value: 6 }],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('sponsor.current_physical_address.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor current physical city or town',
    valueType: 'text',
    required: requiredWhenSponsorPhysicalDiffers,
    visibility: requiredWhenSponsorPhysicalDiffers,
    validation: [{ type: 'required' }, { type: 'max_length', value: 20 }],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('sponsor.current_physical_address.state'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor current physical state (U.S.)',
    valueType: 'select',
    required: requiredWhenSponsorPhysicalCountryIsUs,
    visibility: requiredWhenSponsorPhysicalCountryIsUs,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('sponsor.current_physical_address.zip_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor current physical ZIP code (U.S.)',
    valueType: 'text',
    required: requiredWhenSponsorPhysicalCountryIsUs,
    visibility: requiredWhenSponsorPhysicalCountryIsUs,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 5 },
      { type: 'pattern', pattern: '^\\d{5}$' },
    ],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('sponsor.current_physical_address.province'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor current physical province (non-U.S.)',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'and',
      rules: [
        requiredWhenSponsorPhysicalDiffers,
        {
          op: 'not',
          rule: {
            op: 'equals',
            fieldId: sponsorCurrentPhysicalCountryFieldId,
            value: 'US',
          },
        },
      ],
    },
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('sponsor.current_physical_address.postal_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor current physical postal code (non-U.S.)',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'and',
      rules: [
        requiredWhenSponsorPhysicalDiffers,
        {
          op: 'not',
          rule: {
            op: 'equals',
            fieldId: sponsorCurrentPhysicalCountryFieldId,
            value: 'US',
          },
        },
      ],
    },
    validation: [{ type: 'max_length', value: 10 }],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: sponsorCurrentPhysicalCountryFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor current physical country',
    valueType: 'select',
    required: requiredWhenSponsorPhysicalDiffers,
    visibility: requiredWhenSponsorPhysicalDiffers,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i130Consumer, required: false },
      { ...i864Consumer, required: false },
    ],
  },
  {
    id: defineFieldId(
      'sponsor.current_physical_address.residence.date_from',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor current physical address date of residence, from',
    helpText:
      'I-130 Pt2Line13a. I-130 Pt2Line13b (date to) is auto-populated to "PRESENT" by the PDF engine. I-864 does not collect the date of residence at the current physical address.',
    valueType: 'date',
    required: requiredWhenSponsorPhysicalDiffers,
    visibility: requiredWhenSponsorPhysicalDiffers,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

const sponsorAddressHistoryCountryFieldId = defineFieldId(
  'sponsor.address_history.country',
);

const sponsorAddressHistoryUnitTypeFieldId = defineFieldId(
  'sponsor.address_history.unit_type',
);

const requiredWhenSponsorAddressHistoryCountryIsUs: RuleExpression = {
  op: 'equals',
  fieldId: sponsorAddressHistoryCountryFieldId,
  value: 'US',
};

const requiredWhenSponsorAddressHistoryUnitTypeSelected: RuleExpression = {
  op: 'exists',
  fieldId: sponsorAddressHistoryUnitTypeFieldId,
};

export const sponsorAddressHistoryFields = [
  {
    id: defineFieldId('sponsor.address_history.street_number_and_name'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor prior address street number and name',
    helpText:
      'I-130 Pt2Line14 is the first prior-address row; additional five-year history entries overflow to Part 10.',
    valueType: 'text',
    required: true,
    repeatableGroupId: 'sponsor.address_history',
    repeatableBound: SPONSOR_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 25 }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: sponsorAddressHistoryUnitTypeFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor prior address unit type',
    helpText: 'Supported PDF values are Apt, Ste, and Flr.',
    valueType: 'select',
    required: false,
    repeatableGroupId: 'sponsor.address_history',
    repeatableBound: SPONSOR_ADDRESS_HISTORY_BOUND,
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.address_history.unit_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor prior address apartment, suite, or floor number',
    valueType: 'text',
    required: requiredWhenSponsorAddressHistoryUnitTypeSelected,
    visibility: requiredWhenSponsorAddressHistoryUnitTypeSelected,
    repeatableGroupId: 'sponsor.address_history',
    repeatableBound: SPONSOR_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 6 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.address_history.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor prior address city or town',
    valueType: 'text',
    required: true,
    repeatableGroupId: 'sponsor.address_history',
    repeatableBound: SPONSOR_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 20 }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.address_history.state'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor prior address state (U.S.)',
    valueType: 'select',
    required: requiredWhenSponsorAddressHistoryCountryIsUs,
    visibility: requiredWhenSponsorAddressHistoryCountryIsUs,
    repeatableGroupId: 'sponsor.address_history',
    repeatableBound: SPONSOR_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.address_history.zip_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor prior address ZIP code (U.S.)',
    valueType: 'text',
    required: requiredWhenSponsorAddressHistoryCountryIsUs,
    visibility: requiredWhenSponsorAddressHistoryCountryIsUs,
    repeatableGroupId: 'sponsor.address_history',
    repeatableBound: SPONSOR_ADDRESS_HISTORY_BOUND,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 5 },
      { type: 'pattern', pattern: '^\\d{5}$' },
    ],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.address_history.province'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor prior address province (non-U.S.)',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'not',
      rule: requiredWhenSponsorAddressHistoryCountryIsUs,
    },
    repeatableGroupId: 'sponsor.address_history',
    repeatableBound: SPONSOR_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.address_history.postal_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor prior address postal code (non-U.S.)',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'not',
      rule: requiredWhenSponsorAddressHistoryCountryIsUs,
    },
    repeatableGroupId: 'sponsor.address_history',
    repeatableBound: SPONSOR_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'max_length', value: 10 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: sponsorAddressHistoryCountryFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor prior address country',
    valueType: 'select',
    required: true,
    repeatableGroupId: 'sponsor.address_history',
    repeatableBound: SPONSOR_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.address_history.date_from'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor prior address date of residence, from',
    valueType: 'date',
    required: true,
    repeatableGroupId: 'sponsor.address_history',
    repeatableBound: SPONSOR_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.address_history.date_to'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_physical_address',
    partyRole: 'sponsor',
    label: 'Sponsor prior address date of residence, to',
    valueType: 'date',
    required: true,
    repeatableGroupId: 'sponsor.address_history',
    repeatableBound: SPONSOR_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
] as const satisfies readonly FieldDefinition[];

const beneficiaryMaritalStatusFieldId = defineFieldId(
  'beneficiary.marital.status',
);

const beneficiaryIsCurrentlyMarriedRule: RuleExpression = {
  op: 'one_of',
  fieldId: beneficiaryMaritalStatusFieldId,
  values: ['married', 'legally_separated'],
};

const currentSpouseAddressCountryFieldId = defineFieldId(
  'beneficiary.current_spouse.physical_address.country',
);

const currentSpouseAddressCountryIsUs: RuleExpression = {
  op: 'equals',
  fieldId: currentSpouseAddressCountryFieldId,
  value: 'US',
};

export const beneficiaryMaritalHistoryFields = [
  {
    id: beneficiaryMaritalStatusFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current marital status',
    helpText:
      'I-485 Pt5Line1 and I-130 Pt4Line18 both accept the same six options; I-485 labels the sixth value "Legally Separated" and I-130 labels it "Separated", but they map to the same semantic value.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          divorced: 'Pt6Line1_MaritalStatus[0]',
          single: 'Pt6Line1_MaritalStatus[1]',
          widowed: 'Pt6Line1_MaritalStatus[2]',
          married: 'Pt6Line1_MaritalStatus[3]',
          marriage_annulled: 'Pt6Line1_MaritalStatus[4]',
          legally_separated: 'Pt6Line1_MaritalStatus[5]',
        },
      },
      {
        ...i130Consumer,
        widgetValueProjection: {
          widowed: 'Pt4Line18_MaritalStatus[0]',
          marriage_annulled: 'Pt4Line18_MaritalStatus[1]',
          legally_separated: 'Pt4Line18_MaritalStatus[2]',
          single: 'Pt4Line18_MaritalStatus[3]',
          married: 'Pt4Line18_MaritalStatus[4]',
          divorced: 'Pt4Line18_MaritalStatus[5]',
        },
      },
    ],
  },
  {
    id: defineFieldId('beneficiary.marital.current_spouse_is_military'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse is a U.S. military or Coast Guard member',
    helpText:
      'I-485 Pt5Line2 is a three-value field (yes, no, not_applicable). Store the literal "not_applicable" when the applicant is not currently married.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('beneficiary.marital.times_married'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Number of times the applicant has been married',
    helpText:
      'I-485 Pt5Line3 and I-130 Pt4Line17 both ask for total marriages including annulled. Includes the current marriage if any.',
    valueType: 'number',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer, i130Consumer],
  },
] as const satisfies readonly FieldDefinition[];

export const beneficiaryCurrentSpouseFields = [
  {
    id: defineFieldId('beneficiary.current_spouse.name.legal.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse legal family name',
    helpText:
      'I-485 Pt5Line4a; I-130 Pt4 spouse-1 slot; I-131 Part2 Line17 (current spouse on travel-document filing). Only written when marital status is married or legally_separated.',
    valueType: 'text',
    required: beneficiaryIsCurrentlyMarriedRule,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130Consumer, required: false },
      { ...i131Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.current_spouse.name.legal.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse legal given name',
    helpText: 'I-485 Pt5Line4b; I-130 Pt4 spouse-1 slot; I-131 Part2 Line17.',
    valueType: 'text',
    required: beneficiaryIsCurrentlyMarriedRule,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130Consumer, required: false },
      { ...i131Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.current_spouse.name.legal.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse legal middle name',
    helpText: 'I-485 Pt5Line4c; I-130 Pt4 spouse-1 slot; I-131 Part2 Line17.',
    valueType: 'text',
    required: false,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
      { ...i131Consumer, required: false },
    ],
  },
  {
    id: defineFieldId(
      'beneficiary.current_spouse.alien_registration_number',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse A-Number',
    helpText:
      'I-485 Pt5Line5. Stored as text because A-Numbers have meaningful leading zeroes. I-130 does not collect the current spouse A-Number.',
    valueType: 'text',
    required: false,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    validation: [{ type: 'max_length', value: 9 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.current_spouse.birth.date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse date of birth',
    helpText:
      'I-485 Pt5Line6. I-130 does not collect the current spouse date of birth.',
    valueType: 'date',
    required: beneficiaryIsCurrentlyMarriedRule,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  {
    id: defineFieldId('beneficiary.current_spouse.marriage.date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Date of marriage to current spouse',
    helpText: 'I-485 Pt5Line7; I-130 Pt4Line19.',
    valueType: 'date',
    required: beneficiaryIsCurrentlyMarriedRule,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.current_spouse.birth.place.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse city or town of birth',
    helpText:
      'I-485 Pt5Line8a. I-130 does not collect the current spouse place of birth.',
    valueType: 'text',
    required: beneficiaryIsCurrentlyMarriedRule,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    validation: [{ type: 'required' }, { type: 'max_length', value: 20 }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  {
    id: defineFieldId('beneficiary.current_spouse.birth.place.region'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse state or province of birth',
    helpText:
      'I-485 Pt5Line8b. Widget is free-text on this form; USCIS does not restrict to a U.S. state list here.',
    valueType: 'text',
    required: false,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.current_spouse.birth.place.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse country of birth',
    valueType: 'select',
    required: beneficiaryIsCurrentlyMarriedRule,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  {
    id: defineFieldId('beneficiary.current_spouse.marriage.place.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Place of marriage to current spouse, city or town',
    helpText: 'I-485 Pt5Line9a; I-130 Pt4Line20a.',
    valueType: 'text',
    required: beneficiaryIsCurrentlyMarriedRule,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    validation: [{ type: 'required' }, { type: 'max_length', value: 20 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.current_spouse.marriage.place.region'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Place of marriage to current spouse, state or province',
    helpText:
      'I-485 Pt5Line9b. I-130 splits state (Pt4Line20b) and province (Pt4Line20c); the PDF engine writes whichever half matches the country.',
    valueType: 'text',
    required: false,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.current_spouse.marriage.place.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Place of marriage to current spouse, country',
    helpText: 'I-485 Pt5Line9c; I-130 Pt4Line20d.',
    valueType: 'select',
    required: beneficiaryIsCurrentlyMarriedRule,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.current_spouse.applying_concurrently'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse is applying for adjustment of status with you',
    helpText:
      'I-485 Pt5Line10. Do not confuse with the Part 2 derivative-applicant question; that belongs to the eligibility-category slice.',
    valueType: 'boolean',
    required: beneficiaryIsCurrentlyMarriedRule,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  // I-485 Part 6 Item 8 — Current Spouse's Current Physical Address.
  // Marriage-based filers' spouse IS the sponsor, so the address is
  // typically the same as sponsor.mailing_address; injectSystemAnswers
  // auto-copies when the applicant hasn't explicitly answered.
  // Optional + visibility-gated on married/separated so single
  // applicants don't see the section. State/ZIP gate on US country,
  // province/postal gate on non-US — otherwise both pairs render
  // simultaneously and the user sees "State" AND "State/Province".
  // (Bug surfaced 2026-05-26 by a customer.)
  {
    id: defineFieldId(
      'beneficiary.current_spouse.physical_address.street_number_and_name',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse street number and name',
    helpText: 'I-485 Pt6Line8 Item 8 — current spouse current physical address.',
    valueType: 'text',
    required: false,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'beneficiary.current_spouse.physical_address.unit_type',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse unit type',
    helpText: 'I-485 Pt6Line8 Item 8 — Apt / Ste / Flr.',
    valueType: 'select',
    required: false,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          apt: 'P6Line8_Unit[0]',
          ste: 'P6Line8_Unit[1]',
          flr: 'P6Line8_Unit[2]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'beneficiary.current_spouse.physical_address.unit_number',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse apt/suite/floor number',
    helpText: 'I-485 Pt6Line8 Item 8.',
    valueType: 'text',
    required: false,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    validation: [{ type: 'max_length', value: 12 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'beneficiary.current_spouse.physical_address.city',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse city',
    helpText: 'I-485 Pt6Line8 Item 8.',
    valueType: 'text',
    required: false,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    validation: [{ type: 'max_length', value: 40 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'beneficiary.current_spouse.physical_address.state',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse state',
    helpText: 'I-485 Pt6Line8 Item 8. USPS 2-letter code.',
    valueType: 'select',
    required: false,
    visibility: {
      op: 'and',
      rules: [
        beneficiaryIsCurrentlyMarriedRule,
        currentSpouseAddressCountryIsUs,
      ],
    },
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'beneficiary.current_spouse.physical_address.zip_code',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse ZIP code',
    helpText: 'I-485 Pt6Line8 Item 8.',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'and',
      rules: [
        beneficiaryIsCurrentlyMarriedRule,
        currentSpouseAddressCountryIsUs,
      ],
    },
    validation: [{ type: 'max_length', value: 10 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'beneficiary.current_spouse.physical_address.province',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse province',
    helpText: 'I-485 Pt6Line8 Item 8 (non-US addresses).',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'and',
      rules: [
        beneficiaryIsCurrentlyMarriedRule,
        { op: 'not', rule: currentSpouseAddressCountryIsUs },
      ],
    },
    validation: [{ type: 'max_length', value: 40 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'beneficiary.current_spouse.physical_address.postal_code',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse postal code',
    helpText: 'I-485 Pt6Line8 Item 8 (non-US addresses).',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'and',
      rules: [
        beneficiaryIsCurrentlyMarriedRule,
        { op: 'not', rule: currentSpouseAddressCountryIsUs },
      ],
    },
    validation: [{ type: 'max_length', value: 10 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: currentSpouseAddressCountryFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Current spouse country',
    helpText: 'I-485 Pt6Line8 Item 8.',
    valueType: 'select',
    required: false,
    visibility: beneficiaryIsCurrentlyMarriedRule,
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

export const beneficiaryPriorMarriagesFields = [
  {
    id: defineFieldId('beneficiary.prior_marriages.spouse.name.legal.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Prior spouse legal family name',
    helpText:
      'I-485 Pt511a (2024-10 edition widget name is missing "Line"); I-130 Pt4Line16a for spouse 1, Pt4Line18a for spouse 2. Instances beyond two overflow to I-485 Part 14 or I-130 Part 10.',
    valueType: 'text',
    required: true,
    repeatableGroupId: 'beneficiary.prior_marriages',
    repeatableBound: PRIOR_MARRIAGES_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130Consumer, required: true },
    ],
  },
  {
    id: defineFieldId(
      'beneficiary.prior_marriages.spouse.name.legal.first',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Prior spouse legal given name',
    helpText:
      'I-485 Pt5Line11b; I-130 Pt4Line16b / Pt4Line18b.',
    valueType: 'text',
    required: true,
    repeatableGroupId: 'beneficiary.prior_marriages',
    repeatableBound: PRIOR_MARRIAGES_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130Consumer, required: true },
    ],
  },
  {
    id: defineFieldId(
      'beneficiary.prior_marriages.spouse.name.legal.middle',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Prior spouse legal middle name',
    helpText:
      'I-485 Pt5Line11c; I-130 Pt4Line16c / Pt4Line18c.',
    valueType: 'text',
    required: false,
    repeatableGroupId: 'beneficiary.prior_marriages',
    repeatableBound: PRIOR_MARRIAGES_BOUND,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.prior_marriages.spouse.birth.date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Prior spouse date of birth',
    helpText:
      'I-485 Pt5Line12. I-130 does not collect prior-spouse date of birth.',
    valueType: 'date',
    required: true,
    repeatableGroupId: 'beneficiary.prior_marriages',
    repeatableBound: PRIOR_MARRIAGES_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  {
    id: defineFieldId('beneficiary.prior_marriages.date_started'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Date of prior marriage',
    helpText:
      'I-485 Pt6Line16_DateofBirth[0] — note the misleading widget name (USCIS labels the field "Date of Marriage to Prior Spouse"). I-130 does not collect prior-marriage start date.',
    valueType: 'date',
    required: true,
    repeatableGroupId: 'beneficiary.prior_marriages',
    repeatableBound: PRIOR_MARRIAGES_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  {
    id: defineFieldId('beneficiary.prior_marriages.place_started.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Place of prior marriage, city or town',
    helpText: 'I-485 Pt5Line14a. I-130 does not collect prior-marriage place.',
    valueType: 'text',
    required: true,
    repeatableGroupId: 'beneficiary.prior_marriages',
    repeatableBound: PRIOR_MARRIAGES_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 20 }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  {
    id: defineFieldId('beneficiary.prior_marriages.place_started.region'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Place of prior marriage, state or province',
    helpText: 'I-485 Pt5Line14b.',
    valueType: 'text',
    required: false,
    repeatableGroupId: 'beneficiary.prior_marriages',
    repeatableBound: PRIOR_MARRIAGES_BOUND,
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.prior_marriages.place_started.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Place of prior marriage, country',
    helpText: 'I-485 Pt5Line14c.',
    valueType: 'select',
    required: true,
    repeatableGroupId: 'beneficiary.prior_marriages',
    repeatableBound: PRIOR_MARRIAGES_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  {
    id: defineFieldId('beneficiary.prior_marriages.date_ended'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Date prior marriage legally ended',
    helpText:
      'I-485 Pt6Line16_DateofBirth[1] — note the misleading widget name (USCIS labels the field "Date Marriage with Prior Spouse Legally Ended"). I-130 Pt4Line17_DateMarriageEnded[0] for spouse 1 and Pt4Line17_DateMarriageEnded[1] for spouse 2.',
    valueType: 'date',
    required: true,
    repeatableGroupId: 'beneficiary.prior_marriages',
    repeatableBound: PRIOR_MARRIAGES_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.prior_marriages.place_ended.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Place where prior marriage ended, city or town',
    helpText:
      'I-485 Pt5Line16a. I-130 does not collect where prior marriages ended.',
    valueType: 'text',
    required: true,
    repeatableGroupId: 'beneficiary.prior_marriages',
    repeatableBound: PRIOR_MARRIAGES_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 20 }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  {
    id: defineFieldId('beneficiary.prior_marriages.place_ended.region'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Place where prior marriage ended, state or province',
    helpText: 'I-485 Pt5Line16b.',
    valueType: 'text',
    required: false,
    repeatableGroupId: 'beneficiary.prior_marriages',
    repeatableBound: PRIOR_MARRIAGES_BOUND,
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.prior_marriages.place_ended.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'marriage',
    partyRole: 'beneficiary',
    label: 'Place where prior marriage ended, country',
    helpText: 'I-485 Pt5Line16c.',
    valueType: 'select',
    required: true,
    repeatableGroupId: 'beneficiary.prior_marriages',
    repeatableBound: PRIOR_MARRIAGES_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
] as const satisfies readonly FieldDefinition[];

const sponsorMaritalStatusFieldId = defineFieldId('sponsor.marital.status');

const sponsorIsCurrentlyMarriedRule: RuleExpression = {
  op: 'one_of',
  fieldId: sponsorMaritalStatusFieldId,
  values: ['married', 'legally_separated'],
};

export const sponsorMaritalHistoryFields = [
  {
    id: sponsorMaritalStatusFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_marriage',
    partyRole: 'sponsor',
    label: 'Petitioner current marital status',
    helpText:
      'I-130 Pt2Line17. Six options match the beneficiary enum. I-130 labels the "Legally Separated" value as "Separated" on the printed form.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i130Consumer,
        widgetValueProjection: {
          widowed: 'Pt2Line17_Widowed[0]',
          marriage_annulled: 'Pt2Line17_Annulled[0]',
          legally_separated: 'Pt2Line17_Separated[0]',
          single: 'Pt2Line17_Single[0]',
          married: 'Pt2Line17_Married[0]',
          divorced: 'Pt2Line17_Divorced[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('sponsor.marital.times_married'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_marriage',
    partyRole: 'sponsor',
    label: 'Petitioner number of times married',
    helpText:
      'I-130 Pt2Line16. Includes the current marriage and any annulled marriages.',
    valueType: 'number',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i130Consumer],
  },
] as const satisfies readonly FieldDefinition[];

export const sponsorCurrentSpouseFields = [
  {
    id: defineFieldId('sponsor.current_spouse.name.legal.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_marriage',
    partyRole: 'sponsor',
    label: 'Petitioner current spouse legal family name',
    helpText:
      'I-130 Pt2 spouse-1 slot. Widget name in the 2024-04 edition is `PtLine20a_FamilyName` (missing the "2"). I-864 does not collect petitioner spouse identity.',
    valueType: 'text',
    required: sponsorIsCurrentlyMarriedRule,
    visibility: sponsorIsCurrentlyMarriedRule,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.current_spouse.name.legal.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_marriage',
    partyRole: 'sponsor',
    label: 'Petitioner current spouse legal given name',
    helpText: 'I-130 Pt2Line20b.',
    valueType: 'text',
    required: sponsorIsCurrentlyMarriedRule,
    visibility: sponsorIsCurrentlyMarriedRule,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.current_spouse.name.legal.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_marriage',
    partyRole: 'sponsor',
    label: 'Petitioner current spouse legal middle name',
    helpText: 'I-130 Pt2Line20c.',
    valueType: 'text',
    required: false,
    visibility: sponsorIsCurrentlyMarriedRule,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.current_spouse.marriage.date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_marriage',
    partyRole: 'sponsor',
    label: 'Petitioner date of current marriage',
    helpText: 'I-130 Pt2Line18.',
    valueType: 'date',
    required: sponsorIsCurrentlyMarriedRule,
    visibility: sponsorIsCurrentlyMarriedRule,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.current_spouse.marriage.place.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_marriage',
    partyRole: 'sponsor',
    label: 'Petitioner place of current marriage, city or town',
    helpText: 'I-130 Pt2Line19a.',
    valueType: 'text',
    required: sponsorIsCurrentlyMarriedRule,
    visibility: sponsorIsCurrentlyMarriedRule,
    validation: [{ type: 'required' }, { type: 'max_length', value: 20 }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.current_spouse.marriage.place.region'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_marriage',
    partyRole: 'sponsor',
    label: 'Petitioner place of current marriage, state or province',
    helpText:
      'I-130 Pt2Line19b (state) and Pt2Line19c (province). The PDF engine writes whichever half matches the country.',
    valueType: 'text',
    required: false,
    visibility: sponsorIsCurrentlyMarriedRule,
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.current_spouse.marriage.place.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_marriage',
    partyRole: 'sponsor',
    label: 'Petitioner place of current marriage, country',
    helpText: 'I-130 Pt2Line19d.',
    valueType: 'select',
    required: sponsorIsCurrentlyMarriedRule,
    visibility: sponsorIsCurrentlyMarriedRule,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
] as const satisfies readonly FieldDefinition[];

export const sponsorPriorMarriagesFields = [
  {
    id: defineFieldId('sponsor.prior_marriages.spouse.name.legal.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_marriage',
    partyRole: 'sponsor',
    label: 'Petitioner prior spouse legal family name',
    helpText:
      'I-130 PtLine20a (spouse-1 slot, widget typo missing the "2") when petitioner is unmarried, and Pt2Line22a (spouse-2 slot). Further entries overflow to Part 10 additional info.',
    valueType: 'text',
    required: true,
    repeatableGroupId: 'sponsor.prior_marriages',
    repeatableBound: PRIOR_MARRIAGES_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.prior_marriages.spouse.name.legal.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_marriage',
    partyRole: 'sponsor',
    label: 'Petitioner prior spouse legal given name',
    helpText: 'I-130 Pt2Line20b (spouse-1 slot) / Pt2Line22b (spouse-2 slot).',
    valueType: 'text',
    required: true,
    repeatableGroupId: 'sponsor.prior_marriages',
    repeatableBound: PRIOR_MARRIAGES_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.prior_marriages.spouse.name.legal.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_marriage',
    partyRole: 'sponsor',
    label: 'Petitioner prior spouse legal middle name',
    helpText: 'I-130 Pt2Line20c (spouse-1 slot) / Pt2Line22c (spouse-2 slot).',
    valueType: 'text',
    required: false,
    repeatableGroupId: 'sponsor.prior_marriages',
    repeatableBound: PRIOR_MARRIAGES_BOUND,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.prior_marriages.date_ended'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_marriage',
    partyRole: 'sponsor',
    label: 'Petitioner prior marriage date ended',
    helpText:
      'I-130 Pt2Line21_DateMarriageEnded[0] for spouse-1 slot and Pt2Line23_DateMarriageEnded[0] for spouse-2 slot.',
    valueType: 'date',
    required: true,
    repeatableGroupId: 'sponsor.prior_marriages',
    repeatableBound: PRIOR_MARRIAGES_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
] as const satisfies readonly FieldDefinition[];

export const beneficiaryChildrenCountFields = [
  {
    id: defineFieldId('beneficiary.children.count'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'children',
    partyRole: 'beneficiary',
    label: 'Total number of living children',
    helpText:
      'I-485 Pt6Line1. Includes biological, adopted, and stepchildren of any age regardless of marital status or residence. Must be entered even when the count is zero.',
    valueType: 'number',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
] as const satisfies readonly FieldDefinition[];

export const beneficiaryChildrenFields = [
  {
    id: defineFieldId('beneficiary.children.name.legal.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'children',
    partyRole: 'beneficiary',
    label: 'Child legal family name',
    helpText:
      'I-485 Pt6Line2a (slot 1) / Pt6Line7a (slot 2) / Pt6Line12a (slot 3). I-130 Pt4Line30a/34a/38a/42a/46a. Instances beyond three (I-485) or five (I-130) overflow to Part 14.',
    valueType: 'text',
    required: true,
    repeatableGroupId: 'beneficiary.children',
    repeatableBound: BENEFICIARY_CHILDREN_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.children.name.legal.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'children',
    partyRole: 'beneficiary',
    label: 'Child legal given name',
    helpText:
      'I-485 Pt6Line2b (slot 1) / Pt6Line7b (slot 2) / Pt6Line12b (slot 3). I-130 Pt4Line30b/34b/38b/42b/46b.',
    valueType: 'text',
    required: true,
    repeatableGroupId: 'beneficiary.children',
    repeatableBound: BENEFICIARY_CHILDREN_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.children.name.legal.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'children',
    partyRole: 'beneficiary',
    label: 'Child legal middle name',
    helpText:
      'I-485 Pt6Line2c (slot 1) / Pt6Line7c (slot 2) / Pt6Line12c (slot 3). I-130 Pt4Line30c/34c/38c/42c/46c.',
    valueType: 'text',
    required: false,
    repeatableGroupId: 'beneficiary.children',
    repeatableBound: BENEFICIARY_CHILDREN_BOUND,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.children.alien_registration_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'children',
    partyRole: 'beneficiary',
    label: 'Child A-Number',
    helpText:
      'I-485 Pt6Line3 (slot 1) / Pt6Line8 (slot 2) / Pt6Line13 (slot 3). Stored as text because A-Numbers have meaningful leading zeroes. Optional per the form instructions.',
    valueType: 'text',
    required: false,
    repeatableGroupId: 'beneficiary.children',
    repeatableBound: BENEFICIARY_CHILDREN_BOUND,
    validation: [{ type: 'max_length', value: 9 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.children.birth.date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'children',
    partyRole: 'beneficiary',
    label: 'Child date of birth',
    helpText:
      'I-485 Pt6Line4 (slot 1) / Pt6Line9 (slot 2) / Pt6Line14 (slot 3). I-130 Pt4Line32/36/40/44/48.',
    valueType: 'date',
    required: true,
    repeatableGroupId: 'beneficiary.children',
    repeatableBound: BENEFICIARY_CHILDREN_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.children.birth.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'children',
    partyRole: 'beneficiary',
    label: 'Child country of birth',
    helpText:
      'I-485 Pt6Line6_Country (slot 1, widget is off-by-one with the printed item number 5) / Pt6Line10_Country (slot 2) / Pt6Line15_Country (slot 3). I-130 child slot 1 has no CountryOfBirth widget on the form; deferred until a per-slot projector lands.',
    valueType: 'select',
    required: true,
    repeatableGroupId: 'beneficiary.children',
    repeatableBound: BENEFICIARY_CHILDREN_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  {
    id: defineFieldId('beneficiary.children.applying_concurrently'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'children',
    partyRole: 'beneficiary',
    label: 'Child is applying for adjustment of status with you',
    helpText:
      'I-485 Pt6Line6_YesNo (slot 1) / Pt6Line11_YesNo (slot 2) / Pt6Line16_YesNo (slot 3).',
    valueType: 'boolean',
    required: true,
    repeatableGroupId: 'beneficiary.children',
    repeatableBound: BENEFICIARY_CHILDREN_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
] as const satisfies readonly FieldDefinition[];

export const sponsorHouseholdChildrenFields = [
  {
    id: defineFieldId('sponsor.household.yourself_count'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_household',
    partyRole: 'sponsor',
    label: 'Sponsor household size — yourself',
    helpText:
      'I-864 P5_Line2_Yourself. Always 1 — the sponsor always counts themselves. USCIS instructions mandate entering "1" here regardless of any other circumstances. This value is auto-set to 1 and written to the PDF without requiring manual entry.',
    valueType: 'number',
    required: false,
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.household.spouse_as_immigrant_count'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_household',
    partyRole: 'sponsor',
    label: 'Sponsor household size — spouse as intending immigrant',
    helpText:
      'I-864 P5_Line3_Married. Enter 1 if the person being sponsored is your spouse and will reside in your household; enter 0 otherwise. For marriage-based AOS the value is always 1. Auto-set to 1 for this filing.',
    valueType: 'number',
    required: false,
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.household.dependent_children_count'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_household',
    partyRole: 'sponsor',
    label: 'Sponsor dependent children count',
    helpText:
      'I-864 P5_Line4_DependentChildren. Count of sponsor dependent children not already counted among the intending immigrants in Item 1. This is a household-size input; per-child biographical data is not collected by I-864.',
    valueType: 'number',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i864Consumer],
  },
  {
    id: defineFieldId('sponsor.household.other_dependents_count'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_household',
    partyRole: 'sponsor',
    label: 'Sponsor household size — other dependents',
    helpText:
      'I-864 P5_Line5_OtherDependents. Count of other dependents living in the sponsor\'s household who are not already counted in Lines 2–4 (e.g., elderly parents, disabled relatives). Enter 0 if none.',
    valueType: 'number',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i864Consumer],
  },
  {
    id: defineFieldId('sponsor.household.income_contributors_count'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_household',
    partyRole: 'sponsor',
    label: 'Sponsor household size — Part 6 income contributors (Item 7)',
    helpText:
      'I-864 P5_Line7_SameResidence (Item 7). Count of siblings/parents/adult children with same principal residence who combine their income via Form I-864A. Auto-derived from the household-income-contributors repeatable group.',
    valueType: 'number',
    required: false,
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.household.previously_sponsored_persons_count'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_household',
    partyRole: 'sponsor',
    label: 'Sponsor household — previously sponsored I-864 immigrants (Item 6)',
    helpText:
      'I-864 P5_Line6_Sponsors (Item 6). Count of other persons the sponsor has previously sponsored on Form I-864 / I-864EZ who are now lawful permanent residents and the sponsor is still obligated to support. Enter 0 if none.',
    valueType: 'number',
    required: false,
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.household.total_size'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_household',
    partyRole: 'sponsor',
    label: 'Sponsor household — total size (Item 8)',
    helpText:
      'I-864 P5_Line8 (Household Size). Total = sum of Items 1–7. No AcroForm widget exists in the 10/17/24 template (calculated by USCIS XFA at print time). Stored for auditing; not written to the PDF.',
    valueType: 'number',
    required: false,
    pdfConsumers: [],
  },
] as const satisfies readonly FieldDefinition[];

export const beneficiaryParentsFields = [
  {
    id: defineFieldId('beneficiary.parent_one.name.legal.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 1 legal last name',
    helpText:
      'I-485 Pt4Line1a / I-130A Pt1Line10_FamilyName. Parents are a fixed pair (parent_one, parent_two); there is no overflow slot on any AOS packet form.',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_one.name.legal.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 1 legal first name',
    helpText: 'I-485 Pt4Line1b / I-130A Pt1Line10_GivenName.',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_one.name.legal.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 1 legal middle name',
    helpText: 'I-485 Pt4Line1c / I-130A Pt1Line10_MiddleName.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_one.name.at_birth.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 1 family name at birth (if different from legal name)',
    helpText:
      'I-485 Pt4Line2a. Collected only by I-485; I-130A does not collect parent name-at-birth.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.parent_one.name.at_birth.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 1 given name at birth (if different from legal name)',
    helpText: 'I-485 Pt4Line2b.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.parent_one.name.at_birth.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 1 middle name at birth (if different from legal name)',
    helpText: 'I-485 Pt4Line2c.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.parent_one.birth.date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 1 date of birth',
    helpText: 'I-485 Pt4Line3_DateofBirth / I-130A Pt1Line11_DateofBirth.',
    valueType: 'date',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_one.demographics.sex'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 1 sex',
    helpText:
      'I-485 Pt4Line4_Gender[0..1] / I-130A Pt1Line12_Male / Pt1Line12_Female. Allowed values: male, female. Parent sex enum is narrower than beneficiary sex; the PDF widgets only offer these two options.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_one.birth.place.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 1 city or town of birth',
    helpText:
      'I-485 Pt4Line5_CityTown / I-130A Pt1Line12CityTownOfBirth (widget missing underscore in 2024-04 edition).',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_one.birth.place.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 1 country of birth',
    helpText:
      'I-485 Pt4Line6_Country / I-130A Pt1Line13_CountryofBirth (widget line off-by-one from visible item 14). Stored as ISO 3166-1 alpha-2.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_one.current_residence.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 1 current city or town of residence',
    helpText:
      'I-485 Pt4Line7_CityTown (if living) / I-130A Pt1Line14_CountryofBirth (widget label is a USCIS typo — this widget actually collects city of residence for Parent 1; there is no Pt1Line??_CityTownVillageofRes in the 2024-04 edition).',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_one.current_residence.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 1 current country of residence',
    helpText:
      'I-485 Pt4Line8_Country (if living) / I-130A Pt1Line15_CountryofResidence. Stored as ISO 3166-1 alpha-2.',
    valueType: 'select',
    required: false,
    validation: [],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_two.name.legal.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 2 legal last name',
    helpText: 'I-485 Pt4Line9a / I-130A Pt1Line16_FamilyName.',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_two.name.legal.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 2 legal first name',
    helpText: 'I-485 Pt4Line9b / I-130A Pt1Line16_GivenName.',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_two.name.legal.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 2 legal middle name',
    helpText: 'I-485 Pt4Line9c / I-130A Pt1Line16_MiddleName.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_two.name.at_birth.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 2 family name at birth (if different from legal name)',
    helpText:
      'I-485 Pt4Line10a. Collected only by I-485; I-130A does not collect parent name-at-birth.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.parent_two.name.at_birth.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 2 given name at birth (if different from legal name)',
    helpText: 'I-485 Pt4Line10b.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.parent_two.name.at_birth.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 2 middle name at birth (if different from legal name)',
    helpText: 'I-485 Pt4Line10c.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.parent_two.birth.date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 2 date of birth',
    helpText: 'I-485 Pt4Line11_DateofBirth / I-130A Pt1Line17_DateofBirth.',
    valueType: 'date',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_two.demographics.sex'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 2 sex',
    helpText:
      'I-485 Pt4Line12_Gender[0..1] / I-130A Pt1Line19_Male / Pt1Line19_Female. Allowed values: male, female.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_two.birth.place.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 2 city or town of birth',
    helpText: 'I-485 Pt4Line13_CityTown / I-130A Pt1Line18_CityTownOfBirth.',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_two.birth.place.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 2 country of birth',
    helpText:
      'I-485 Pt4Line14_Country / I-130A Pt1Line19_CountryofBirth. Stored as ISO 3166-1 alpha-2.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_two.current_residence.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 2 current city or town of residence',
    helpText:
      'I-485 Pt4Line15_CityTown (if living) / I-130A Pt1Line20_CityTownVillageofRes.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.parent_two.current_residence.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'parents',
    partyRole: 'beneficiary',
    label: 'Parent 2 current country of residence',
    helpText:
      'I-485 Pt4Line16_Country (if living) / I-130A Pt1Line21_CountryofResidence. Stored as ISO 3166-1 alpha-2.',
    valueType: 'select',
    required: false,
    validation: [],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
    ],
  },
] as const satisfies readonly FieldDefinition[];

// ─── sponsor.sponsored_immigrants ────────────────────────────────
// I-864 Part 4 derivative immigrants. The principal beneficiary is
// already listed in Part 3 (bound to beneficiary.name.*); this group
// lists the additional family members the sponsor is supporting.
// For marriage-based AOS with only the spouse beneficiary, this is
// empty. For cases with derivative children, populate via the intake
// repeatable; the auto-populate logic in injectSystemAnswers seeds
// rows from beneficiary.children that are applying_concurrently.
const SPONSOR_SPONSORED_IMMIGRANTS_BOUND = {
  min: 0,
  max: 4,
  overflowStrategy: 'additional_info',
} as const;

const sponsorIsSponsoringDerivativesFieldId = defineFieldId(
  'sponsor.derivative_immigrants.is_sponsoring',
);

const requiredWhenSponsoringDerivatives: RuleExpression = {
  op: 'equals',
  fieldId: sponsorIsSponsoringDerivativesFieldId,
  value: true,
};

export const sponsorSponsoredImmigrantsFields = [
  {
    id: sponsorIsSponsoringDerivativesFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_household',
    partyRole: 'sponsor',
    label:
      'Are you sponsoring family members in addition to the principal immigrant?',
    helpText:
      'For most marriage-based filings the answer is No — the sponsor supports only the spouse beneficiary. Pick Yes only when there are derivative immigrants (e.g., the beneficiary\'s minor children) being sponsored on the same packet.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i864Consumer,
        widgetValueProjection: {
          true: 'P3_Line2_SponsoringFamily[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'sponsor.sponsored_immigrants.name.legal.last',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_household',
    partyRole: 'sponsor',
    label: 'Derivative immigrant — family name',
    helpText:
      'I-864 P3_Line3a/8a/13a/18a (Family Members 1-4). Each row is the legal family name of a derivative immigrant the sponsor is supporting.',
    valueType: 'text',
    required: requiredWhenSponsoringDerivatives,
    visibility: requiredWhenSponsoringDerivatives,
    repeatableGroupId: 'sponsor.sponsored_immigrants',
    repeatableBound: SPONSOR_SPONSORED_IMMIGRANTS_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      {
        ...i864Consumer,
        required: false,
        widgetNames: [
          'P3_Line3a_FamilyName[0]',
          'P3_Line8a_FamilyName[0]',
          'P3_Line13a_FamilyName[0]',
          'P3_Line18a_FamilyName[0]',
        ],
      },
    ],
  },
  {
    id: defineFieldId(
      'sponsor.sponsored_immigrants.name.legal.first',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_household',
    partyRole: 'sponsor',
    label: 'Derivative immigrant — given name',
    helpText:
      'I-864 P3_Line3b/8b/13b/18b (Family Members 1-4).',
    valueType: 'text',
    required: requiredWhenSponsoringDerivatives,
    visibility: requiredWhenSponsoringDerivatives,
    repeatableGroupId: 'sponsor.sponsored_immigrants',
    repeatableBound: SPONSOR_SPONSORED_IMMIGRANTS_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      {
        ...i864Consumer,
        required: false,
        widgetNames: [
          'P3_Line3b_GivenName[0]',
          'P3_Line8b_GivenName[0]',
          'P3_Line13b_GivenName[0]',
          'P3_Line18b_GivenName[0]',
        ],
      },
    ],
  },
  {
    id: defineFieldId(
      'sponsor.sponsored_immigrants.name.legal.middle',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_household',
    partyRole: 'sponsor',
    label: 'Derivative immigrant — middle name',
    helpText:
      'I-864 P3_Line3c/8c/13c/18c (Family Members 1-4). Optional.',
    valueType: 'text',
    required: false,
    visibility: requiredWhenSponsoringDerivatives,
    repeatableGroupId: 'sponsor.sponsored_immigrants',
    repeatableBound: SPONSOR_SPONSORED_IMMIGRANTS_BOUND,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [
      {
        ...i864Consumer,
        required: false,
        widgetNames: [
          'P3_Line3c_MiddleName[0]',
          'P3_Line8c_MiddleName[0]',
          'P3_Line13c_MiddleName[0]',
          'P3_Line18c_MiddleName[0]',
        ],
      },
    ],
  },
  {
    id: defineFieldId(
      'sponsor.sponsored_immigrants.relationship_to_principal',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_household',
    partyRole: 'sponsor',
    label: 'Derivative immigrant — relationship to principal',
    helpText:
      'I-864 P3_Line4/9/14/19. Common values: Son, Daughter, Step-son, Step-daughter, Spouse.',
    valueType: 'text',
    required: requiredWhenSponsoringDerivatives,
    visibility: requiredWhenSponsoringDerivatives,
    repeatableGroupId: 'sponsor.sponsored_immigrants',
    repeatableBound: SPONSOR_SPONSORED_IMMIGRANTS_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 30 }],
    pdfConsumers: [
      {
        ...i864Consumer,
        required: false,
        widgetNames: [
          'P3_Line4_Relationship[0]',
          'P3_Line9_Relationship[0]',
          'P3_Line14_Relationship[0]',
          'P3_Line19_Relationship[0]',
        ],
      },
    ],
  },
  {
    id: defineFieldId(
      'sponsor.sponsored_immigrants.date_of_birth',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_household',
    partyRole: 'sponsor',
    label: 'Derivative immigrant — date of birth',
    helpText:
      'I-864 P3_Line_DateOfBirth (slot 1, no number suffix), P3_Line10/15/20.',
    valueType: 'date',
    required: requiredWhenSponsoringDerivatives,
    visibility: requiredWhenSponsoringDerivatives,
    repeatableGroupId: 'sponsor.sponsored_immigrants',
    repeatableBound: SPONSOR_SPONSORED_IMMIGRANTS_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i864Consumer,
        required: false,
        widgetNames: [
          'P3_Line_DateOfBirth[0]',
          'P3_Line10_DateOfBirth[0]',
          'P3_Line15_DateOfBirth[0]',
          'P3_Line20_DateOfBirth[0]',
        ],
      },
    ],
  },
  {
    id: defineFieldId(
      'sponsor.sponsored_immigrants.uscis_online_account',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_household',
    partyRole: 'sponsor',
    label: 'Derivative immigrant — USCIS online account number',
    helpText:
      'I-864 P3_Line7/12/17/22 (slots 1-4). Optional. The catalog skips P3_Line11/21 A-Number widgets — slot 1/3 have no A-Number widget on the 2024-10 edition, so binding would misalign rows.',
    valueType: 'text',
    required: false,
    visibility: requiredWhenSponsoringDerivatives,
    repeatableGroupId: 'sponsor.sponsored_immigrants',
    repeatableBound: SPONSOR_SPONSORED_IMMIGRANTS_BOUND,
    validation: [{ type: 'max_length', value: 12 }],
    pdfConsumers: [
      {
        ...i864Consumer,
        required: false,
        widgetNames: [
          'P3_Line7_AcctIdentifier[0]',
          'P3_Line12_AcctIdentifier[0]',
          'P3_Line17_AcctIdentifier[0]',
          'P3_Line22_AcctIdentifier[0]',
        ],
      },
    ],
  },
] as const satisfies readonly FieldDefinition[];

export const sponsorParentsFields = [
  {
    id: defineFieldId('sponsor.parent_one.name.legal.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_parents',
    partyRole: 'sponsor',
    label: 'Sponsor parent 1 legal last name',
    helpText:
      'I-130 Pt2Line24_FamilyName. Sponsor parent fields are driven only by I-130 Part 2 items 24-35; I-864 does not collect sponsor parents.',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.parent_one.name.legal.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_parents',
    partyRole: 'sponsor',
    label: 'Sponsor parent 1 legal first name',
    helpText: 'I-130 Pt2Line24_GivenName.',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.parent_one.name.legal.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_parents',
    partyRole: 'sponsor',
    label: 'Sponsor parent 1 legal middle name',
    helpText: 'I-130 Pt2Line24_MiddleName.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.parent_one.birth.date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_parents',
    partyRole: 'sponsor',
    label: 'Sponsor parent 1 date of birth',
    helpText: 'I-130 Pt2Line25_DateofBirth.',
    valueType: 'date',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.parent_one.demographics.sex'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_parents',
    partyRole: 'sponsor',
    label: 'Sponsor parent 1 sex',
    helpText:
      'I-130 Pt2Line26_Male / Pt2Line26_Female. Allowed values: male, female.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.parent_one.birth.place.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_parents',
    partyRole: 'sponsor',
    label: 'Sponsor parent 1 country of birth',
    helpText:
      'I-130 Pt2Line27_CountryofBirth. I-130 does not collect sponsor parent city of birth. Stored as ISO 3166-1 alpha-2.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.parent_one.current_residence.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_parents',
    partyRole: 'sponsor',
    label: 'Sponsor parent 1 current city or town of residence',
    helpText: 'I-130 Pt2Line28_CityTownOrVillageOfResidence.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.parent_one.current_residence.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_parents',
    partyRole: 'sponsor',
    label: 'Sponsor parent 1 current country of residence',
    helpText:
      'I-130 Pt2Line29_CountryOfResidence. Stored as ISO 3166-1 alpha-2.',
    valueType: 'select',
    required: false,
    validation: [],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.parent_two.name.legal.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_parents',
    partyRole: 'sponsor',
    label: 'Sponsor parent 2 legal last name',
    helpText: 'I-130 Pt2Line30a_FamilyName.',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.parent_two.name.legal.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_parents',
    partyRole: 'sponsor',
    label: 'Sponsor parent 2 legal first name',
    helpText: 'I-130 Pt2Line30b_GivenName.',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.parent_two.name.legal.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_parents',
    partyRole: 'sponsor',
    label: 'Sponsor parent 2 legal middle name',
    helpText: 'I-130 Pt2Line30c_MiddleName.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.parent_two.birth.date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_parents',
    partyRole: 'sponsor',
    label: 'Sponsor parent 2 date of birth',
    helpText: 'I-130 Pt2Line31_DateofBirth.',
    valueType: 'date',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.parent_two.demographics.sex'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_parents',
    partyRole: 'sponsor',
    label: 'Sponsor parent 2 sex',
    helpText:
      'I-130 Pt2Line32_Male / Pt2Line32_Female. Allowed values: male, female.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.parent_two.birth.place.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_parents',
    partyRole: 'sponsor',
    label: 'Sponsor parent 2 country of birth',
    helpText:
      'I-130 Pt2Line33_CountryofBirth. Stored as ISO 3166-1 alpha-2.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.parent_two.current_residence.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_parents',
    partyRole: 'sponsor',
    label: 'Sponsor parent 2 current city or town of residence',
    helpText: 'I-130 Pt2Line34_CityTownOrVillageOfResidence.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.parent_two.current_residence.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_parents',
    partyRole: 'sponsor',
    label: 'Sponsor parent 2 current country of residence',
    helpText:
      'I-130 Pt2Line35_CountryOfResidence. Stored as ISO 3166-1 alpha-2.',
    valueType: 'select',
    required: false,
    validation: [],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

const beneficiarySsnHasBeenIssuedFieldId = defineFieldId(
  'beneficiary.ssn.has_been_issued',
);

const requiredWhenBeneficiaryHasSsn: RuleExpression = {
  op: 'equals',
  fieldId: beneficiarySsnHasBeenIssuedFieldId,
  value: true,
};

const beneficiarySsnWantsCardFieldId = defineFieldId(
  'beneficiary.ssn.wants_card',
);

const requiredWhenBeneficiaryWantsSsnCard: RuleExpression = {
  op: 'equals',
  fieldId: beneficiarySsnWantsCardFieldId,
  value: true,
};

export const beneficiarySsnFields = [
  {
    id: beneficiarySsnHasBeenIssuedFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Has the SSA ever issued you a Social Security card?',
    helpText:
      'I-485 Pt1Line14_YN and I-765 Page2 Line12a_Checkbox (item 13.A) ask the same question.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i765Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.ssn.number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Social Security Number',
    helpText:
      'I-485 Pt1Line15_SSN, I-765 Page2 Line12b_SSN (item 13.B), and I-131 Part2_Line10_SSN (Part 2 item 10). Stored as 9 digits without separators.',
    valueType: 'text',
    required: requiredWhenBeneficiaryHasSsn,
    visibility: requiredWhenBeneficiaryHasSsn,
    validation: [
      { type: 'pattern', pattern: '^\\d{9}$' },
      { type: 'max_length', value: 9 },
    ],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i765Consumer, required: false },
      { ...i131Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: beneficiarySsnWantsCardFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Do you want the SSA to issue you a Social Security card?',
    helpText:
      'I-485 Pt1Line16_YN and I-765 Page2 Line13_Checkbox.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i765Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.ssn.consent_disclosure'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label:
      'I authorize disclosure of information from this application to the SSA',
    helpText:
      'I-485 Pt1Line17_YN and I-765 Page2 Line14_Checkbox_Yes/No. The SSA only issues a card when both wants_card and consent_disclosure are true.',
    valueType: 'boolean',
    required: requiredWhenBeneficiaryWantsSsnCard,
    visibility: requiredWhenBeneficiaryWantsSsnCard,
    validation: [],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i765Consumer, required: false },
    ],
  },
] as const satisfies readonly FieldDefinition[];

const beneficiaryPassportNumberFieldId = defineFieldId(
  'beneficiary.passport.number',
);

const beneficiaryTravelDocumentNumberFieldId = defineFieldId(
  'beneficiary.travel_document.number',
);

const requiredWhenAnyTravelDocumentEntered: RuleExpression = {
  op: 'or',
  rules: [
    { op: 'exists', fieldId: beneficiaryPassportNumberFieldId },
    { op: 'exists', fieldId: beneficiaryTravelDocumentNumberFieldId },
  ],
};

const beneficiaryEntryTypeFieldId = defineFieldId(
  'beneficiary.last_arrival.entry_type',
);

const requiredWhenInspectedAdmitted: RuleExpression = {
  op: 'equals',
  fieldId: beneficiaryEntryTypeFieldId,
  value: 'inspected_admitted',
};

const requiredWhenInspectedParoled: RuleExpression = {
  op: 'equals',
  fieldId: beneficiaryEntryTypeFieldId,
  value: 'inspected_paroled',
};

const requiredWhenEntryTypeOther: RuleExpression = {
  op: 'equals',
  fieldId: beneficiaryEntryTypeFieldId,
  value: 'other',
};

const beneficiaryI94NumberFieldId = defineFieldId('beneficiary.i94.number');

const requiredWhenI94Issued: RuleExpression = {
  op: 'exists',
  fieldId: beneficiaryI94NumberFieldId,
};

export const beneficiaryImmigrationHistoryFields = [
  {
    id: beneficiaryPassportNumberFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Passport number used at last arrival',
    helpText:
      'I-485 Pt1Line10_PassportNum (01/20/25 edition) and I-765 Page3 Line20b_Passport (item 21.B) and I-130 Pt4Line22_PassportNumber. Optional — applicants who entered without a passport leave this blank.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i765Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: beneficiaryTravelDocumentNumberFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Travel document number used at last arrival',
    helpText:
      'I-485 Pt2Line19_TravelDoc (item 19; widget mis-prefixed Pt2), I-765 Page3 Line20c_TravelDoc (item 21.C), I-130 Pt4Line23_TravelDocNumber. Optional alternative to passport for refugee/parole travel documents.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i765Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.travel_document.expiration_date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Expiration date of passport or travel document',
    helpText:
      'I-485 Pt1Line20_ExpDate, I-765 Page3 Line20e_ExpDate (item 21.E), I-130 Pt4Line25_ExpDate. Required when a passport or travel document is supplied.',
    valueType: 'date',
    required: requiredWhenAnyTravelDocumentEntered,
    visibility: requiredWhenAnyTravelDocumentEntered,
    validation: [],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i765Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.travel_document.issuing_country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Country that issued the passport or travel document',
    helpText:
      'I-485 Pt1Line21_Passport, I-765 Page3 Line20d_CountryOfIssuance (item 21.D), I-130 Pt4Line24_CountryOfIssuance. Stored as ISO 3166-1 alpha-2.',
    valueType: 'select',
    required: requiredWhenAnyTravelDocumentEntered,
    visibility: requiredWhenAnyTravelDocumentEntered,
    validation: [],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i765Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.visa.number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Nonimmigrant visa number from this passport',
    helpText:
      'I-485 Pt1Line22_VisaNum only. I-765 does not collect a visa number. Optional — applicants who entered visa-free leave this blank.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.last_arrival.place.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Place of last arrival into the United States — city or town',
    helpText:
      'I-485 Pt1Line23a_CityTown and I-765 Page3 place_entry (item 23; widget unprefixed/lowercase). On I-765 the engine concatenates "<City>, <State>" when state is supplied.',
    valueType: 'text',
    required: true,
    validation: [{ type: 'required' }, { type: 'max_length', value: 50 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i765Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.last_arrival.place.state'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Place of last arrival into the United States — U.S. state',
    helpText:
      'I-485 Pt1Line23b_State only. I-765 has no state-only widget for place of last arrival. Optional because some entries (e.g. via territory or sea) lack a U.S. state.',
    valueType: 'select',
    required: false,
    validation: [],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.last_arrival.date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Date of last arrival into the United States',
    helpText:
      'I-485 Pt1Line24_Date, I-765 Page3 Line21_DateOfLastEntry, I-130 Pt4Line21c_DateOfArrival.',
    valueType: 'date',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i765Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: beneficiaryEntryTypeFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'How did you enter the United States the last time?',
    helpText:
      'I-485 Pt1Line25{a,b,c,d}_CB (item 25; mutually exclusive checkboxes). Allowed values: inspected_admitted, inspected_paroled, without_admission_or_parole, other. I-765 does not collect this radio.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        required: true,
        widgetValueProjection: {
          inspected_admitted: 'Pt2Line11_CB[0]',
          inspected_paroled: 'Pt2Line11_CB[1]',
          without_admission_or_parole: 'Pt2Line11_CB[2]',
          other: 'Pt2Line11_CB[3]',
        },
      },
    ],
  },
  {
    id: defineFieldId('beneficiary.last_arrival.admitted_as'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Admitted as (e.g., F-1 student, B-2 visitor)',
    helpText:
      'I-485 Pt1Line11_Admitted (01/20/25), I-130 Pt4Line21a_ClassOfAdmission. Required when entry_type is inspected_admitted.',
    valueType: 'text',
    required: requiredWhenInspectedAdmitted,
    visibility: requiredWhenInspectedAdmitted,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.last_arrival.paroled_as'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Paroled as (e.g., humanitarian parole, Cuban parole)',
    helpText:
      'I-485 Pt1Line25b_ParoleEntrance (item 25.b free text). Required when entry_type is inspected_paroled.',
    valueType: 'text',
    required: requiredWhenInspectedParoled,
    visibility: requiredWhenInspectedParoled,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.last_arrival.other_explanation'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Other entry-type explanation',
    helpText:
      'I-485 Pt2Line25d_other (item 25.d free text; widget mis-prefixed Pt2). Required when entry_type is other.',
    valueType: 'text',
    required: requiredWhenEntryTypeOther,
    visibility: requiredWhenEntryTypeOther,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  // Phase A1 — i-485 01/20/25 Item 16: alien crewman visa.
  // Yacht/cruise/freighter crew members may have been issued a D
  // (crewman) visa. Most AOS marriage applicants answer "No"; Brandan
  // (yacht-industry background) answered "Yes" in the customer case.
  {
    id: defineFieldId('beneficiary.last_arrival.alien_crewman_visa'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Have you ever been issued an "alien crewman" visa?',
    helpText:
      'I-485 01/20/25 Pt1Line16_YN. Crewman visa = D visa for vessel/aircraft crew. Default No for typical AOS marriage cases.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  // Phase A2 — i-485 01/20/25 Item 17: arrived to join vessel as seaman.
  // Even crewman-visa holders may answer "No" here if their LAST entry
  // was on a different visa (e.g., B-2 tourist).
  {
    id: defineFieldId('beneficiary.last_arrival.vessel_seaman'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Did you last arrive in the United States to join a vessel as a seaman or crewman, or while serving in any capacity aboard a vessel or aircraft?',
    helpText:
      'I-485 01/20/25 Pt1Line17_YN. Distinct from alien_crewman_visa — applicants who hold a crewman visa but entered as a tourist on the most recent trip answer "No" here.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  // Phase A3 — i-485 01/20/25 Item 13: was last arrival the first time
  // you were physically present in the US?
  {
    id: defineFieldId('beneficiary.last_arrival.was_first_arrival'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Was your last arrival the first time you were physically present in the United States?',
    helpText:
      'I-485 01/20/25 Pt1Line13_YN. True if this is the applicant\'s first ever entry to the US; false if they had been here before.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: true }],
  },
  {
    id: beneficiaryI94NumberFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Form I-94 Arrival/Departure Record Number',
    helpText:
      'I-485 P2Line26a_I94 (item 26.a; widget mis-prefixed P2 missing t), I-765 Page3 Line20a_I94Number (item 21.A), I-131 Part2_Line13_I94RecordNo (Part 2 item 13), I-130 Pt4Line21b_ArrivalDeparture. Optional — applicants without an I-94 leave this blank.',
    valueType: 'text',
    required: false,
    validation: [
      { type: 'pattern', pattern: '^[A-Z0-9]{9,11}$' },
      { type: 'max_length', value: 11 },
    ],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i765Consumer, required: false },
      { ...i131Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.i94.expiration_date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Expiration date of authorized stay shown on Form I-94',
    helpText:
      'I-485 Pt1Line26b_Date, I-131 Part2_Line14_I94ExpDate (Part 2 item 14), I-130 Pt4Line21d_DateExpired. I-765 does not collect I-94 expiration. Conditional on i94.number being present.',
    valueType: 'date',
    required: requiredWhenI94Issued,
    visibility: requiredWhenI94Issued,
    validation: [],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i131Consumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.i94.status_at_arrival'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Status on Form I-94 (class of admission, or paroled)',
    helpText:
      'I-485 Pt1Line26c_Status, I-765 Page3 Line23_StatusLastEntry, and I-131 Part2_Line12_ClassofAdmission (Part 2 item 12). I-765 phrases the same domain question as "Immigration Status at Your Last Arrival"; I-131 stores it under "Class of Admission (COA)". Slice 39 (2026-05-09) — converted from free text to a select whose option keys ARE the USCIS-friendly class-of-admission strings the I-485 Pt1Line11_admitted_as widget expects verbatim (per ADR 0017). Mirrors the legacy app\'s `I94_STATUS_DESCRIPTIONS` map (visa code → human description); see docs/rebuild/legacy-comparison-2026-05-09.md §B3.',
    valueType: 'select',
    required: false,
    // Empty rule list — the select widget enforces option-set
    // membership at render time. The empty array preserves the
    // tuple shape so `as const`-narrowed `find()` calls in the
    // test file can still access `?.validation?.` on union
    // members (regression caught by the `i94Number?.validation`
    // type-check during the text→select migration).
    validation: [],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i765Consumer, required: false },
      { ...i131Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.immigration_status.current'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Current immigration status (if changed since arrival)',
    helpText:
      'I-485 Pt1Line27_Status and I-765 Page3 Line24_CurrentStatus. Slice 39 (2026-05-09) — converted from free text to a 7-value select that mirrors the screener-onboarding question ("How did you most recently enter or stay in the U.S.?"). The select option KEYS are the USCIS-friendly strings the I-485/I-765 PDF widgets expect verbatim, so no enum→text projection is needed at packet-fill time. The applicant-web `optionsResolver.ts` adds the longer applicant-facing labels.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i765Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.i94.name.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Name on Form I-94 — family name (last)',
    helpText:
      'I-485 Pt1Line28a_FamilyName only. May differ from legal last name due to USCIS transliteration. Conditional on i94.number being present.',
    valueType: 'text',
    required: requiredWhenI94Issued,
    visibility: requiredWhenI94Issued,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.i94.name.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Name on Form I-94 — given name (first)',
    helpText:
      'I-485 Pt1Line28b_GivenName only. Conditional on i94.number being present.',
    valueType: 'text',
    required: requiredWhenI94Issued,
    visibility: requiredWhenI94Issued,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.i94.name.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Name on Form I-94 — middle name',
    helpText:
      'I-485 Pt1Line28c_MiddleName only. Optional even when i94.number is present.',
    valueType: 'text',
    required: false,
    visibility: requiredWhenI94Issued,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

const beneficiaryPriorImmigrantVisaAppliedFieldId = defineFieldId(
  'beneficiary.prior_immigrant_visa.applied',
);

const requiredWhenPriorImmigrantVisaApplied: RuleExpression = {
  op: 'equals',
  fieldId: beneficiaryPriorImmigrantVisaAppliedFieldId,
  value: true,
};

export const beneficiaryPriorImmigrantVisaFields = [
  // I-130 Pt4Line10: has anyone else ever filed a petition for the beneficiary?
  // (Yes/No/Unknown — 3-state select, not boolean)
  {
    id: defineFieldId('beneficiary.prior_i130.has_anyone_filed'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_history',
    partyRole: 'beneficiary',
    label:
      'Has anyone else ever filed an immigrant petition (Form I-130) for the beneficiary?',
    helpText:
      'I-130 Pt4Line10 — Yes/No/Unknown 3-state. Disclosure of prior I-130s strengthens or weakens the bona-fide-marriage analysis.',
    valueType: 'select',
    required: false,
    pdfConsumers: [
      {
        ...i130Consumer,
        required: false,
        widgetValueProjection: {
          yes: 'Pt4Line10_Yes[0]',
          no: 'Pt4Line10_No[0]',
          unknown: 'Pt4Line10_Unknown[0]',
        },
      },
    ],
  },
  {
    id: beneficiaryPriorImmigrantVisaAppliedFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_history',
    partyRole: 'beneficiary',
    label:
      'Has the beneficiary ever applied for an immigrant visa at a U.S. Embassy or U.S. Consulate abroad?',
    helpText:
      'I-485 01/20/25 Pt4Line1_YN (Part 4 item 1). Drives visibility of Items 2–4. No other in-scope AOS form asks this question.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          // Pt4Line1_YN[1] (x=498 LEFT) = Yes; [0] (x=540 RIGHT) = No.
          true: 'Pt4Line1_YN[1]',
          false: 'Pt4Line1_YN[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('beneficiary.prior_filings.applied_for_lpr_in_us'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_history',
    partyRole: 'beneficiary',
    label:
      'Have you previously applied for permanent residence while in the United States?',
    helpText:
      'I-485 01/20/25 Pt4Line5_YN (Part 4 item 5). Yes/No — separate from the at-consulate question (Item 1) which asks about applications abroad.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt4Line5_YN[1]',
          false: 'Pt4Line5_YN[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('beneficiary.prior_filings.lpr_rescinded_under_ina_246'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_history',
    partyRole: 'beneficiary',
    label:
      'Have you EVER held lawful permanent resident status which was later rescinded under INA section 246?',
    helpText:
      'I-485 01/20/25 Pt4Line6_YN (Part 4 item 6). Yes/No — applies only to applicants who previously held LPR status that was rescinded.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt4Line6_YN[1]',
          false: 'Pt4Line6_YN[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('beneficiary.prior_immigrant_visa.location.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_history',
    partyRole: 'beneficiary',
    label: 'Prior immigrant visa application — U.S. Embassy or Consulate city',
    helpText:
      'I-485 Pt3Line2a_City (item 2.A). Free-text city name where the U.S. Embassy or Consulate is located.',
    valueType: 'text',
    required: requiredWhenPriorImmigrantVisaApplied,
    visibility: requiredWhenPriorImmigrantVisaApplied,
    validation: [{ type: 'required' }, { type: 'max_length', value: 40 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.prior_immigrant_visa.location.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_history',
    partyRole: 'beneficiary',
    label:
      'Prior immigrant visa application — U.S. Embassy or Consulate country',
    helpText:
      'I-485 Pt3Line2b_Country (item 2.B). ISO 3166-1 alpha-2 code of the country hosting the consulate or embassy.',
    valueType: 'select',
    required: requiredWhenPriorImmigrantVisaApplied,
    visibility: requiredWhenPriorImmigrantVisaApplied,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.prior_immigrant_visa.decision'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_history',
    partyRole: 'beneficiary',
    label: 'Prior immigrant visa application — decision',
    helpText:
      'I-485 Pt3Line3_Decision. Free text; USCIS instructions give "approved, refused, denied, withdrawn" as examples but do not constrain the field to those values.',
    valueType: 'text',
    required: requiredWhenPriorImmigrantVisaApplied,
    visibility: requiredWhenPriorImmigrantVisaApplied,
    validation: [{ type: 'required' }, { type: 'max_length', value: 40 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.prior_immigrant_visa.decision_date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_history',
    partyRole: 'beneficiary',
    label: 'Prior immigrant visa application — date of decision',
    helpText: 'I-485 Pt3Line4_Date. Entered as mm/dd/yyyy.',
    valueType: 'date',
    required: requiredWhenPriorImmigrantVisaApplied,
    visibility: requiredWhenPriorImmigrantVisaApplied,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

const beneficiaryAddressHistoryCountryFieldId = defineFieldId(
  'beneficiary.address_history.country',
);

const beneficiaryAddressHistoryUnitTypeFieldId = defineFieldId(
  'beneficiary.address_history.unit_type',
);

const requiredWhenBeneficiaryAddressHistoryCountryIsUs: RuleExpression = {
  op: 'equals',
  fieldId: beneficiaryAddressHistoryCountryFieldId,
  value: 'US',
};

const requiredWhenBeneficiaryAddressHistoryUnitTypeSelected: RuleExpression = {
  op: 'exists',
  fieldId: beneficiaryAddressHistoryUnitTypeFieldId,
};

export const beneficiaryAddressHistoryFields = [
  {
    id: defineFieldId('beneficiary.address_history.street_number_and_name'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Beneficiary prior address street number and name',
    helpText:
      'I-485 Pt3Line7_StreetNumberName (Physical Address Two) and Pt3Line9_StreetNumberName (most recent international address > 1 year). Additional prior-address instances overflow to Part 14 (Additional Information). I-130A Pt1Line4/6/8 mirror the beneficiary address history.',
    valueType: 'text',
    required: true,
    repeatableGroupId: 'beneficiary.address_history',
    repeatableBound: BENEFICIARY_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 25 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: false },
    ],
  },
  {
    id: beneficiaryAddressHistoryUnitTypeFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Beneficiary prior address unit type',
    helpText:
      'Supported PDF values are Apt, Ste, and Flr. `pdftk` reports the three button widgets in floor/apartment/suite order; the PDF engine keys on the widget state string (Apt/Ste/Flr), not array index.',
    valueType: 'select',
    required: false,
    repeatableGroupId: 'beneficiary.address_history',
    repeatableBound: BENEFICIARY_ADDRESS_HISTORY_BOUND,
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.address_history.unit_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Beneficiary prior address apartment, suite, or floor number',
    valueType: 'text',
    required: requiredWhenBeneficiaryAddressHistoryUnitTypeSelected,
    visibility: requiredWhenBeneficiaryAddressHistoryUnitTypeSelected,
    repeatableGroupId: 'beneficiary.address_history',
    repeatableBound: BENEFICIARY_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 6 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.address_history.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Beneficiary prior address city or town',
    valueType: 'text',
    required: true,
    repeatableGroupId: 'beneficiary.address_history',
    repeatableBound: BENEFICIARY_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 20 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.address_history.state'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Beneficiary prior address state (U.S.)',
    helpText:
      'I-485 Pt3Line9 carries a `State[0]` widget even though it is labeled "most recent international address"; the PDF engine leaves it blank for non-U.S. group instances.',
    valueType: 'select',
    required: requiredWhenBeneficiaryAddressHistoryCountryIsUs,
    visibility: requiredWhenBeneficiaryAddressHistoryCountryIsUs,
    repeatableGroupId: 'beneficiary.address_history',
    repeatableBound: BENEFICIARY_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.address_history.zip_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Beneficiary prior address ZIP code (U.S.)',
    valueType: 'text',
    required: requiredWhenBeneficiaryAddressHistoryCountryIsUs,
    visibility: requiredWhenBeneficiaryAddressHistoryCountryIsUs,
    repeatableGroupId: 'beneficiary.address_history',
    repeatableBound: BENEFICIARY_ADDRESS_HISTORY_BOUND,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 5 },
      { type: 'pattern', pattern: '^\\d{5}$' },
    ],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.address_history.province'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Beneficiary prior address province (non-U.S.)',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'not',
      rule: requiredWhenBeneficiaryAddressHistoryCountryIsUs,
    },
    repeatableGroupId: 'beneficiary.address_history',
    repeatableBound: BENEFICIARY_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.address_history.postal_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Beneficiary prior address postal code (non-U.S.)',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'not',
      rule: requiredWhenBeneficiaryAddressHistoryCountryIsUs,
    },
    repeatableGroupId: 'beneficiary.address_history',
    repeatableBound: BENEFICIARY_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'max_length', value: 10 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
    ],
  },
  {
    id: beneficiaryAddressHistoryCountryFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Beneficiary prior address country',
    helpText:
      'ISO 3166-1 alpha-2 code. Drives state/ZIP vs province/postal visibility within the repeatable group instance.',
    valueType: 'select',
    required: true,
    repeatableGroupId: 'beneficiary.address_history',
    repeatableBound: BENEFICIARY_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.address_history.date_from'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Beneficiary prior address date of residence, from',
    helpText:
      'I-485 Pt3Line8a_DateFrom (Physical Address Two) and Pt3Line10a_DateFrom (most recent international). I-130A Pt1Line5a/7a/9a_DateFrom mirror these rows.',
    valueType: 'date',
    required: true,
    repeatableGroupId: 'beneficiary.address_history',
    repeatableBound: BENEFICIARY_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.address_history.date_to'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'physical_address',
    partyRole: 'beneficiary',
    label: 'Beneficiary prior address date of residence, to',
    helpText:
      'I-485 Pt3Line8b_DateTo (Physical Address Two) and Pt3Line10a_DateTo (most recent international; widget shares the `10a` stem with the "from" date — USCIS inconsistency preserved verbatim). I-130A Pt1Line5b/7b/9b_DateTo mirror these rows.',
    valueType: 'date',
    required: true,
    repeatableGroupId: 'beneficiary.address_history',
    repeatableBound: BENEFICIARY_ADDRESS_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: false },
    ],
  },
] as const satisfies readonly FieldDefinition[];

const beneficiaryEmploymentHistoryStatusFieldId = defineFieldId(
  'beneficiary.employment_history.status',
);

const beneficiaryEmploymentHistoryCountryFieldId = defineFieldId(
  'beneficiary.employment_history.address.country',
);

const beneficiaryEmploymentHistoryUnitTypeFieldId = defineFieldId(
  'beneficiary.employment_history.address.unit_type',
);

const requiredWhenBeneficiaryEmploymentCountryIsUs: RuleExpression = {
  op: 'equals',
  fieldId: beneficiaryEmploymentHistoryCountryFieldId,
  value: 'US',
};

const requiredWhenBeneficiaryEmploymentUnitTypeSelected: RuleExpression = {
  op: 'exists',
  fieldId: beneficiaryEmploymentHistoryUnitTypeFieldId,
};

// Employment-history status discriminator. Each row of the repeatable
// group describes a single period — and the period type drives which
// sibling fields apply. The five allowed values mirror USCIS I-485
// Part 4 Item 7's "Employer or School / unemployment / retirement"
// universe.
const beneficiaryEmploymentStatusEmployerLike: RuleExpression = {
  op: 'one_of',
  fieldId: beneficiaryEmploymentHistoryStatusFieldId,
  values: ['employed', 'self_employed', 'in_school'],
};

const beneficiaryEmploymentStatusNeedsFinancialSupport: RuleExpression = {
  op: 'one_of',
  fieldId: beneficiaryEmploymentHistoryStatusFieldId,
  values: ['unemployed', 'retired'],
};

export const beneficiaryEmploymentHistoryFields = [
  {
    id: beneficiaryEmploymentHistoryStatusFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'employment',
    partyRole: 'beneficiary',
    label: 'What were you doing during this period?',
    valueType: 'select',
    required: true,
    repeatableGroupId: 'beneficiary.employment_history',
    repeatableBound: BENEFICIARY_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    // No direct PDF widget — drives the projection of employer_name /
    // occupation / address values into existing I-485 widgets. When
    // status is unemployed/retired the user fills source_of_financial
    // _support, which the PDF engine routes into the I-485 Part 14
    // Additional Information overflow path.
    pdfConsumers: [],
  },
  {
    id: defineFieldId('beneficiary.employment_history.employer_name'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'employment',
    partyRole: 'beneficiary',
    label: 'Beneficiary employer name',
    helpText:
      'I-485 Pt3Line11_EmployerName (Employer One, item 11), Pt3Line4a_EmployerName (Employer Two name, USCIS bug — should be Pt3Line15a_*), Pt3Line19_EmployerName (most recent international employer if not already listed, item 19). I-130A Pt2Line1_EmployerOrCompName (Employer 1) / Pt2Line5_EmployerOrCompName (Employer 2). I-130 Pt4Line26_NameOfCompany (current employer only). Overflow beyond three slots emits to I-485 Part 14.',
    valueType: 'text',
    required: beneficiaryEmploymentStatusEmployerLike,
    visibility: beneficiaryEmploymentStatusEmployerLike,
    repeatableGroupId: 'beneficiary.employment_history',
    repeatableBound: BENEFICIARY_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
      { ...i130Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.employment_history.occupation'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'employment',
    partyRole: 'beneficiary',
    label: 'Beneficiary occupation',
    helpText:
      'I-485 widgets reuse the `_EmployerName` suffix for the occupation slots: Pt3Line13_EmployerName (item 13 occupation), Pt3Line17_EmployerName (item 17 occupation), Pt3Line20_EmployerName (item 21 occupation). Despite the widget name, FieldNameAlt explicitly says "Enter Your Occupation". I-130A uses the cleanly named Pt2Line3_Occupation / Pt2Line7_Occupation.',
    valueType: 'text',
    required: beneficiaryEmploymentStatusEmployerLike,
    visibility: beneficiaryEmploymentStatusEmployerLike,
    repeatableGroupId: 'beneficiary.employment_history',
    repeatableBound: BENEFICIARY_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
    ],
  },
  {
    id: defineFieldId(
      'beneficiary.employment_history.address.street_number_and_name',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'employment',
    partyRole: 'beneficiary',
    label: 'Beneficiary employer address street number and name',
    helpText:
      'I-485 Pt3Line12_StreetNumberName (Employer One), Pt3Line16_StreetNumberName (Employer Two), Pt3Line20_StreetNumberName (most recent international employer). I-130A Pt2Line2a_StreetNumberName (Employer 1) / Pt2Line6_StreetNumberName (Employer 2). I-130 Pt4Line26_StreetNumberName (current employer only).',
    valueType: 'text',
    required: beneficiaryEmploymentStatusEmployerLike,
    visibility: beneficiaryEmploymentStatusEmployerLike,
    repeatableGroupId: 'beneficiary.employment_history',
    repeatableBound: BENEFICIARY_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 25 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
      { ...i130Consumer, required: true },
    ],
  },
  {
    id: beneficiaryEmploymentHistoryUnitTypeFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'employment',
    partyRole: 'beneficiary',
    label: 'Beneficiary employer address unit type',
    helpText:
      'Supported PDF values are Apt, Ste, and Flr. I-485 Pt3Line12_Unit / Pt3Line16_Unit / Pt3Line20_Unit; I-130A Pt2Line2b_Unit / Pt2Line6_Unit; I-130 Pt4Line26_Unit.',
    valueType: 'select',
    required: false,
    visibility: beneficiaryEmploymentStatusEmployerLike,
    repeatableGroupId: 'beneficiary.employment_history',
    repeatableBound: BENEFICIARY_EMPLOYMENT_HISTORY_BOUND,
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.employment_history.address.unit_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'employment',
    partyRole: 'beneficiary',
    label: 'Beneficiary employer address apartment, suite, or floor number',
    valueType: 'text',
    required: {
      op: 'and',
      rules: [
        beneficiaryEmploymentStatusEmployerLike,
        requiredWhenBeneficiaryEmploymentUnitTypeSelected,
      ],
    },
    visibility: {
      op: 'and',
      rules: [
        beneficiaryEmploymentStatusEmployerLike,
        requiredWhenBeneficiaryEmploymentUnitTypeSelected,
      ],
    },
    repeatableGroupId: 'beneficiary.employment_history',
    repeatableBound: BENEFICIARY_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 6 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.employment_history.address.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'employment',
    partyRole: 'beneficiary',
    label: 'Beneficiary employer address city or town',
    valueType: 'text',
    required: beneficiaryEmploymentStatusEmployerLike,
    visibility: beneficiaryEmploymentStatusEmployerLike,
    repeatableGroupId: 'beneficiary.employment_history',
    repeatableBound: BENEFICIARY_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 20 }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
      { ...i130Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.employment_history.address.state'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'employment',
    partyRole: 'beneficiary',
    label: 'Beneficiary employer address state (U.S.)',
    valueType: 'select',
    required: {
      op: 'and',
      rules: [
        beneficiaryEmploymentStatusEmployerLike,
        requiredWhenBeneficiaryEmploymentCountryIsUs,
      ],
    },
    visibility: {
      op: 'and',
      rules: [
        beneficiaryEmploymentStatusEmployerLike,
        requiredWhenBeneficiaryEmploymentCountryIsUs,
      ],
    },
    repeatableGroupId: 'beneficiary.employment_history',
    repeatableBound: BENEFICIARY_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.employment_history.address.zip_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'employment',
    partyRole: 'beneficiary',
    label: 'Beneficiary employer address ZIP code (U.S.)',
    valueType: 'text',
    required: {
      op: 'and',
      rules: [
        beneficiaryEmploymentStatusEmployerLike,
        requiredWhenBeneficiaryEmploymentCountryIsUs,
      ],
    },
    visibility: {
      op: 'and',
      rules: [
        beneficiaryEmploymentStatusEmployerLike,
        requiredWhenBeneficiaryEmploymentCountryIsUs,
      ],
    },
    repeatableGroupId: 'beneficiary.employment_history',
    repeatableBound: BENEFICIARY_EMPLOYMENT_HISTORY_BOUND,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 5 },
      { type: 'pattern', pattern: '^\\d{5}$' },
    ],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.employment_history.address.province'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'employment',
    partyRole: 'beneficiary',
    label: 'Beneficiary employer address province (non-U.S.)',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'and',
      rules: [
        beneficiaryEmploymentStatusEmployerLike,
        {
          op: 'not',
          rule: requiredWhenBeneficiaryEmploymentCountryIsUs,
        },
      ],
    },
    repeatableGroupId: 'beneficiary.employment_history',
    repeatableBound: BENEFICIARY_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: defineFieldId('beneficiary.employment_history.address.postal_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'employment',
    partyRole: 'beneficiary',
    label: 'Beneficiary employer address postal code (non-U.S.)',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'and',
      rules: [
        beneficiaryEmploymentStatusEmployerLike,
        {
          op: 'not',
          rule: requiredWhenBeneficiaryEmploymentCountryIsUs,
        },
      ],
    },
    repeatableGroupId: 'beneficiary.employment_history',
    repeatableBound: BENEFICIARY_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'max_length', value: 10 }],
    pdfConsumers: [
      { ...i485Consumer, required: false },
      { ...i130aConsumer, required: false },
      { ...i130Consumer, required: false },
    ],
  },
  {
    id: beneficiaryEmploymentHistoryCountryFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'employment',
    partyRole: 'beneficiary',
    label: 'Beneficiary employer address country',
    helpText:
      'ISO 3166-1 alpha-2 code. Drives state/ZIP vs province/postal visibility within the repeatable group instance.',
    valueType: 'select',
    required: beneficiaryEmploymentStatusEmployerLike,
    visibility: beneficiaryEmploymentStatusEmployerLike,
    repeatableGroupId: 'beneficiary.employment_history',
    repeatableBound: BENEFICIARY_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
      { ...i130Consumer, required: true },
    ],
  },
  {
    id: defineFieldId(
      'beneficiary.employment_history.source_of_financial_support',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'employment',
    partyRole: 'beneficiary',
    label: 'How did you support yourself during this period?',
    valueType: 'text',
    required: beneficiaryEmploymentStatusNeedsFinancialSupport,
    visibility: beneficiaryEmploymentStatusNeedsFinancialSupport,
    repeatableGroupId: 'beneficiary.employment_history',
    repeatableBound: BENEFICIARY_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    // I-485 Part 4 prints this in the Additional Information overflow
    // line for unemployed / retired rows ("If unemployed or retired,
    // source of financial support"). No dedicated Part 3 widget; the
    // PDF engine routes it via the overflow_strategy on the group.
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.employment_history.date_from'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'employment',
    partyRole: 'beneficiary',
    label: 'Beneficiary employment date from',
    helpText:
      'I-485 Pt3Line14a_DateFrom (Employer One), Pt3Line18a_DateFrom (Employer Two; widget shares 18a stem with date_to), Pt3Line22a_DateFrom (most recent international; widget shares 22a stem). I-130A Pt2Line4a_DateFrom / Pt2Line8a_DateFrom. I-130 Pt4Line27_DateEmploymentBegan (current employer only).',
    valueType: 'date',
    required: true,
    repeatableGroupId: 'beneficiary.employment_history',
    repeatableBound: BENEFICIARY_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
      { ...i130Consumer, required: true },
    ],
  },
  {
    id: defineFieldId('beneficiary.employment_history.date_to'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'employment',
    partyRole: 'beneficiary',
    label: 'Beneficiary employment date to',
    helpText:
      'I-485 Pt3Line14b_DateTo (Employer One), Pt3Line18a_DateTo (Employer Two; widget shares 18a stem — USCIS bug, no 18b), Pt3Line22a_DateTo (most recent international; widget shares 22a stem — USCIS bug, no 22b). I-130A Pt2Line4b_DateTo / Pt2Line8b_DateTo. PDF engine writes "Present" when the applicant is currently employed at the slot.',
    valueType: 'date',
    required: true,
    repeatableGroupId: 'beneficiary.employment_history',
    repeatableBound: BENEFICIARY_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      { ...i485Consumer, required: true },
      { ...i130aConsumer, required: true },
    ],
  },
] as const satisfies readonly FieldDefinition[];

const sponsorEmploymentHistoryStatusFieldId = defineFieldId(
  'sponsor.employment_history.status',
);

const sponsorEmploymentHistoryCountryFieldId = defineFieldId(
  'sponsor.employment_history.address.country',
);

const sponsorEmploymentHistoryUnitTypeFieldId = defineFieldId(
  'sponsor.employment_history.address.unit_type',
);

const requiredWhenSponsorEmploymentCountryIsUs: RuleExpression = {
  op: 'equals',
  fieldId: sponsorEmploymentHistoryCountryFieldId,
  value: 'US',
};

const requiredWhenSponsorEmploymentUnitTypeSelected: RuleExpression = {
  op: 'exists',
  fieldId: sponsorEmploymentHistoryUnitTypeFieldId,
};

// Sponsor employment-history status discriminator — mirrors the
// beneficiary side. Each row of the I-130 Part 2 employment history
// describes one period (employed / self-employed / in-school /
// unemployed / retired) and the period type drives which siblings
// apply.
const sponsorEmploymentStatusEmployerLike: RuleExpression = {
  op: 'one_of',
  fieldId: sponsorEmploymentHistoryStatusFieldId,
  values: ['employed', 'self_employed', 'in_school'],
};

const sponsorEmploymentStatusNeedsFinancialSupport: RuleExpression = {
  op: 'one_of',
  fieldId: sponsorEmploymentHistoryStatusFieldId,
  values: ['unemployed', 'retired'],
};

export const sponsorEmploymentHistoryFields = [
  {
    id: sponsorEmploymentHistoryStatusFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'What was the sponsor doing during this period?',
    valueType: 'select',
    required: true,
    repeatableGroupId: 'sponsor.employment_history',
    repeatableBound: SPONSOR_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
  },
  {
    id: defineFieldId('sponsor.employment_history.employer_name'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Sponsor employer name',
    helpText:
      'I-130 Pt2Line40_EmployerOrCompName (Employer 1, visible item 42) and Pt2Line44_EmployerOrOrgName (Employer 2, visible item 46). USCIS authoring bug: Employer 1 widget uses `EmployerOrCompName` while Employer 2 uses `EmployerOrOrgName`. Overflow beyond two slots emits to Part 10.',
    valueType: 'text',
    required: sponsorEmploymentStatusEmployerLike,
    visibility: sponsorEmploymentStatusEmployerLike,
    repeatableGroupId: 'sponsor.employment_history',
    repeatableBound: SPONSOR_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.employment_history.occupation'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Sponsor occupation',
    helpText:
      'I-130 Pt2Line42_Occupation (Employer 1, visible item 44) / Pt2Line46_Occupation (Employer 2, visible item 48). Visible-item-vs-widget-line numbering is offset by two on this block.',
    valueType: 'text',
    required: sponsorEmploymentStatusEmployerLike,
    visibility: sponsorEmploymentStatusEmployerLike,
    repeatableGroupId: 'sponsor.employment_history',
    repeatableBound: SPONSOR_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId(
      'sponsor.employment_history.address.street_number_and_name',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Sponsor employer address street number and name',
    helpText:
      'I-130 Pt2Line41_StreetNumberName (Employer 1, visible item 43.A) / Pt2Line45_StreetNumberName (Employer 2, visible item 47.A).',
    valueType: 'text',
    required: sponsorEmploymentStatusEmployerLike,
    visibility: sponsorEmploymentStatusEmployerLike,
    repeatableGroupId: 'sponsor.employment_history',
    repeatableBound: SPONSOR_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 25 }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: sponsorEmploymentHistoryUnitTypeFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Sponsor employer address unit type',
    helpText:
      'Supported PDF values are Apt, Ste, and Flr. I-130 Pt2Line41_Unit / Pt2Line45_Unit.',
    valueType: 'select',
    required: false,
    visibility: sponsorEmploymentStatusEmployerLike,
    repeatableGroupId: 'sponsor.employment_history',
    repeatableBound: SPONSOR_EMPLOYMENT_HISTORY_BOUND,
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.employment_history.address.unit_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Sponsor employer address apartment, suite, or floor number',
    valueType: 'text',
    required: {
      op: 'and',
      rules: [
        sponsorEmploymentStatusEmployerLike,
        requiredWhenSponsorEmploymentUnitTypeSelected,
      ],
    },
    visibility: {
      op: 'and',
      rules: [
        sponsorEmploymentStatusEmployerLike,
        requiredWhenSponsorEmploymentUnitTypeSelected,
      ],
    },
    repeatableGroupId: 'sponsor.employment_history',
    repeatableBound: SPONSOR_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 6 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.employment_history.address.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Sponsor employer address city or town',
    valueType: 'text',
    required: sponsorEmploymentStatusEmployerLike,
    visibility: sponsorEmploymentStatusEmployerLike,
    repeatableGroupId: 'sponsor.employment_history',
    repeatableBound: SPONSOR_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 20 }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.employment_history.address.state'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Sponsor employer address state (U.S.)',
    valueType: 'select',
    required: {
      op: 'and',
      rules: [
        sponsorEmploymentStatusEmployerLike,
        requiredWhenSponsorEmploymentCountryIsUs,
      ],
    },
    visibility: {
      op: 'and',
      rules: [
        sponsorEmploymentStatusEmployerLike,
        requiredWhenSponsorEmploymentCountryIsUs,
      ],
    },
    repeatableGroupId: 'sponsor.employment_history',
    repeatableBound: SPONSOR_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.employment_history.address.zip_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Sponsor employer address ZIP code (U.S.)',
    valueType: 'text',
    required: {
      op: 'and',
      rules: [
        sponsorEmploymentStatusEmployerLike,
        requiredWhenSponsorEmploymentCountryIsUs,
      ],
    },
    visibility: {
      op: 'and',
      rules: [
        sponsorEmploymentStatusEmployerLike,
        requiredWhenSponsorEmploymentCountryIsUs,
      ],
    },
    repeatableGroupId: 'sponsor.employment_history',
    repeatableBound: SPONSOR_EMPLOYMENT_HISTORY_BOUND,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 5 },
      { type: 'pattern', pattern: '^\\d{5}$' },
    ],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.employment_history.address.province'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Sponsor employer address province (non-U.S.)',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'and',
      rules: [
        sponsorEmploymentStatusEmployerLike,
        {
          op: 'not',
          rule: requiredWhenSponsorEmploymentCountryIsUs,
        },
      ],
    },
    repeatableGroupId: 'sponsor.employment_history',
    repeatableBound: SPONSOR_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'max_length', value: 20 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.employment_history.address.postal_code'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Sponsor employer address postal code (non-U.S.)',
    valueType: 'text',
    required: false,
    visibility: {
      op: 'and',
      rules: [
        sponsorEmploymentStatusEmployerLike,
        {
          op: 'not',
          rule: requiredWhenSponsorEmploymentCountryIsUs,
        },
      ],
    },
    repeatableGroupId: 'sponsor.employment_history',
    repeatableBound: SPONSOR_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'max_length', value: 10 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: sponsorEmploymentHistoryCountryFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Sponsor employer address country',
    helpText:
      'ISO 3166-1 alpha-2 code. Drives state/ZIP vs province/postal visibility within the repeatable group instance.',
    valueType: 'select',
    required: sponsorEmploymentStatusEmployerLike,
    visibility: sponsorEmploymentStatusEmployerLike,
    repeatableGroupId: 'sponsor.employment_history',
    repeatableBound: SPONSOR_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId(
      'sponsor.employment_history.source_of_financial_support',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'How did the sponsor support themselves during this period?',
    valueType: 'text',
    required: sponsorEmploymentStatusNeedsFinancialSupport,
    visibility: sponsorEmploymentStatusNeedsFinancialSupport,
    repeatableGroupId: 'sponsor.employment_history',
    repeatableBound: SPONSOR_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.employment_history.date_from'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Sponsor employment date from',
    helpText:
      'I-130 Pt2Line43a_DateFrom (Employer 1, visible item 45.A) / Pt2Line47a_DateFrom (Employer 2, visible item 49.A).',
    valueType: 'date',
    required: true,
    repeatableGroupId: 'sponsor.employment_history',
    repeatableBound: SPONSOR_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.employment_history.date_to'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Sponsor employment date to',
    helpText:
      'I-130 Pt2Line43b_DateTo (Employer 1, visible item 45.B) / Pt2Line47b_DateTo (Employer 2, visible item 49.B). PDF engine writes "Present" when the petitioner is currently employed at the slot (Pt2Line43b is auto-populated to Present per FieldNameAlt).',
    valueType: 'date',
    required: true,
    repeatableGroupId: 'sponsor.employment_history',
    repeatableBound: SPONSOR_EMPLOYMENT_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
] as const satisfies readonly FieldDefinition[];

const sponsorCurrentEmploymentStatusFieldId = defineFieldId(
  'sponsor.current_employment.status',
);

const requiredWhenSponsorIsEmployed: RuleExpression = {
  op: 'equals',
  fieldId: sponsorCurrentEmploymentStatusFieldId,
  value: 'employed',
};

const requiredWhenSponsorIsSelfEmployed: RuleExpression = {
  op: 'equals',
  fieldId: sponsorCurrentEmploymentStatusFieldId,
  value: 'self_employed',
};

const requiredWhenSponsorIsRetired: RuleExpression = {
  op: 'equals',
  fieldId: sponsorCurrentEmploymentStatusFieldId,
  value: 'retired',
};

const requiredWhenSponsorIsUnemployed: RuleExpression = {
  op: 'equals',
  fieldId: sponsorCurrentEmploymentStatusFieldId,
  value: 'unemployed',
};

export const sponsorCurrentEmploymentFields = [
  {
    id: sponsorCurrentEmploymentStatusFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Your current employment status',
    helpText:
      'I-864 Part 6 items 1, 4, 5, 6 are four mutually exclusive checkboxes (P6_Line1_Checkbox = employed, P6_Line4_Checkbox = self_employed, P6_Line5_Checkbox = retired, P6_Line6_Checkbox = unemployed). Stored as a single semantic select; the PDF engine sets the matching widget. Distinct from `sponsor.employment_history` (I-130) which captures historical employer slots.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i864Consumer,
        required: true,
        widgetValueProjection: {
          employed: 'P6_Line1_Checkbox[0]',
          self_employed: 'P6_Line4_Checkbox[0]',
          retired: 'P6_Line5_Checkbox[0]',
          unemployed: 'P6_Line6_Checkbox[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('sponsor.current_employment.employed_as'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Your occupation',
    helpText:
      'I-864 P6_Line1a_NameofEmployer. Despite the widget name, FieldNameAlt reads "Enter what you are employed as" — this captures the occupation the sponsor holds while employed, not the employer name. Conditional on status = employed.',
    valueType: 'text',
    required: requiredWhenSponsorIsEmployed,
    visibility: requiredWhenSponsorIsEmployed,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.current_employment.employer_one_name'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: "Your employer's name",
    helpText:
      'I-864 P6_Line1a1_NameofEmployer (FieldNameAlt: "Enter Name of Employer 1"). Conditional on status = employed.',
    valueType: 'text',
    required: requiredWhenSponsorIsEmployed,
    visibility: requiredWhenSponsorIsEmployed,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.current_employment.employer_two_name'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Second employer (if you have one)',
    helpText:
      'I-864 P6_Line1a2_NameofEmployer (FieldNameAlt: "Enter Name of Employer 2 (if applicable)"). Optional even when status = employed.',
    valueType: 'text',
    required: false,
    visibility: requiredWhenSponsorIsEmployed,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.current_employment.self_employed_as'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Your business or self-employment',
    helpText:
      'I-864 P6_Line4a_SelfEmployedAs. Conditional on status = self_employed.',
    valueType: 'text',
    required: requiredWhenSponsorIsSelfEmployed,
    visibility: requiredWhenSponsorIsSelfEmployed,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.current_employment.retired_since_date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Retired since',
    helpText:
      'I-864 P6_Line5a_DateRetired. Conditional on status = retired.',
    valueType: 'date',
    required: requiredWhenSponsorIsRetired,
    visibility: requiredWhenSponsorIsRetired,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.current_employment.unemployed_since_date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Unemployed since',
    helpText:
      'I-864 P6_Line6a_DateofUnemployment. Conditional on status = unemployed.',
    valueType: 'date',
    required: requiredWhenSponsorIsUnemployed,
    visibility: requiredWhenSponsorIsUnemployed,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

const sponsorIntendingImmigrantNoDependentsListedFieldId = defineFieldId(
  'sponsor.income.intending_immigrant_without_dependents_listed',
);

const requiredWhenIntendingImmigrantListedWithoutDependents: RuleExpression = {
  op: 'equals',
  fieldId: sponsorIntendingImmigrantNoDependentsListedFieldId,
  value: true,
};

// UI-only gate: "Is anyone else in your household filing an I-864A to
// help meet the income requirement?" Most sponsors answer No, which
// collapses ~8 confusing I-864A coordination fields behind the gate.
// No PDF consumer — this is a routing question, not a form widget.
const sponsorHasHouseholdCoFilerFieldId = defineFieldId(
  'sponsor.income.has_household_co_filer',
);

const whenSponsorHasHouseholdCoFiler: RuleExpression = {
  op: 'equals',
  fieldId: sponsorHasHouseholdCoFilerFieldId,
  value: true,
};

export const sponsorIncomeFields = [
  {
    id: defineFieldId('sponsor.income.individual_annual'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Your annual income',
    helpText:
      'I-864 P6_Line2_TotalIncome (visible item 7). Widget-line numbering is decoupled from visible-item numbering across Part 6 items 7-17; mappings key on the exact widget name. Dollar amount; the PDF engine owns currency formatting.',
    valueType: 'number',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i864Consumer],
  },
  {
    id: sponsorHasHouseholdCoFilerFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label:
      'Is anyone else in your household helping you meet the income threshold (filing a Form I-864A)?',
    helpText:
      "UI-only gate. When No (the typical answer for a marriage-based AOS) the household-members income block, the I-864A coordination booleans, the intending-immigrant-name override, and the household-members asset roll-up all stay hidden so the page collapses to the sponsor's own income + assets. When Yes the gated fields appear and the sponsor can add household income contributors. No PDF widget — this is pure routing.",
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
  },
  {
    id: defineFieldId('sponsor.household_members_income.name'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Household member name',
    helpText:
      'I-864 P6_Line3_Name / P6_Line6_Name / P6_Line9_Name / P6_Line12_Name (visible items 8-11). Four slots are bounded on the PDF; overflow emits to Part 11 (Additional Information). Repeatable group `sponsor.household_members_income`.',
    valueType: 'text',
    required: true,
    visibility: whenSponsorHasHouseholdCoFiler,
    repeatableGroupId: 'sponsor.household_members_income',
    repeatableBound: SPONSOR_HOUSEHOLD_INCOME_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 38 }],
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.household_members_income.relationship'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Relationship to you',
    helpText:
      'I-864 P6_Line4_Relationship / P6_Line7_Relationship / P6_Line10_Relationship / P6_Line13_Relationship (visible items 8-11). Repeatable group `sponsor.household_members_income`.',
    valueType: 'text',
    required: true,
    visibility: whenSponsorHasHouseholdCoFiler,
    repeatableGroupId: 'sponsor.household_members_income',
    repeatableBound: SPONSOR_HOUSEHOLD_INCOME_BOUND,
    validation: [{ type: 'required' }, { type: 'max_length', value: 38 }],
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.household_members_income.current_income'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Their annual income',
    helpText:
      'I-864 P6_Line5_CurrentIncome / P6_Line8_CurrentIncome / P6_Line11_CurrentIncome / P6_Line14_CurrentIncome (visible items 8-11). Dollar amount stored as number; PDF engine formats.',
    valueType: 'number',
    required: true,
    visibility: whenSponsorHasHouseholdCoFiler,
    repeatableGroupId: 'sponsor.household_members_income',
    repeatableBound: SPONSOR_HOUSEHOLD_INCOME_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.income.household_total_annual'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: "Your household's combined annual income",
    helpText:
      'I-864 P6_Line15_TotalHouseholdIncome (visible item 12). Derived: sum of `sponsor.income.individual_annual` (visible item 7) and each `sponsor.household_members_income.current_income` instance (visible items 8-11). Stored so the PDF engine can emit it without recomputation.',
    valueType: 'number',
    required: true,
    validation: [{ type: 'required' }],
    computed: {
      op: 'sum',
      addends: [
        defineFieldId('sponsor.income.individual_annual'),
        {
          repeatableField: defineFieldId(
            'sponsor.household_members_income.current_income',
          ),
          groupId: 'sponsor.household_members_income',
        },
      ],
    },
    pdfConsumers: [i864Consumer],
  },
  {
    id: defineFieldId('sponsor.income.i864a_completed_by_all_listed'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label:
      'Each household member listed will file their own Form I-864A',
    helpText:
      'I-864 P6_Line16_CompletedForm (visible item 13). "The people listed in Item Numbers 8. - 11. have completed Form I-864A. I am filing along with this affidavit all necessary Form I-864A\'s completed by these people."',
    valueType: 'boolean',
    required: false,
    visibility: whenSponsorHasHouseholdCoFiler,
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: sponsorIntendingImmigrantNoDependentsListedFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label:
      'One of the listed contributors is the beneficiary (no accompanying dependents)',
    helpText:
      'I-864 P6_Line17_NotNeedComplete (visible item 14). "One or more of the people listed in Item Numbers 8. - 11. do not need to complete Form I-864A because he or she is the intending immigrant and has no accompanying dependents." Note: this widget shares its `Line17` prefix with two unrelated widgets (`P6_Line17_Name` for visible item 14 name fill-in and `P6_Line17_IWasNotReq` for visible item 17 IRS exemption); mappings key on the exact widget name.',
    valueType: 'boolean',
    required: false,
    visibility: whenSponsorHasHouseholdCoFiler,
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.income.intending_immigrant_name'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: "Beneficiary's name",
    helpText:
      'I-864 P6_Line17_Name (visible item 14 name fill-in). USCIS authoring gap: this widget has an empty FieldNameAlt ("Part 6. Sponsor\'s Employment and Income."). Conditional on both the co-filer gate AND the beneficiary-listed-without-dependents flag.',
    valueType: 'text',
    required: {
      op: 'and',
      rules: [
        whenSponsorHasHouseholdCoFiler,
        requiredWhenIntendingImmigrantListedWithoutDependents,
      ],
    },
    visibility: {
      op: 'and',
      rules: [
        whenSponsorHasHouseholdCoFiler,
        requiredWhenIntendingImmigrantListedWithoutDependents,
      ],
    },
    validation: [{ type: 'required' }, { type: 'max_length', value: 38 }],
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

const sponsorFiledTaxReturnsThreeYearsFieldId = defineFieldId(
  'sponsor.income.filed_tax_returns_three_years',
);

const requiredWhenSponsorFiledTaxReturns: RuleExpression = {
  op: 'equals',
  fieldId: sponsorFiledTaxReturnsThreeYearsFieldId,
  value: true,
};

export const sponsorTaxHistoryFields = [
  {
    id: sponsorFiledTaxReturnsThreeYearsFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label:
      'Did you file federal income taxes for each of the last 3 years?',
    helpText:
      'I-864 P6_Line18a_Checkbox[0] (Yes) / P6_Line18a_Checkbox[1] (No) (visible item 15). Mutually exclusive Yes/No collapsed to a single semantic boolean. Drives the tax-history rows below.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i864Consumer],
  },
  {
    id: defineFieldId('sponsor.tax_history.tax_year'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Tax year',
    helpText:
      'I-864 P6_Line19a_TaxYear (instance 0, most recent, visible 16.a) / P6_Line19b_TaxYear (instance 1, 2nd most recent, visible 16.b) / P6_Line19c_TaxYear (instance 2, 3rd most recent, visible 16.c). Repeatable group `sponsor.tax_history` bounded `min: 1, max: 3` — USCIS requires only the most recent year (16.a); years 2 and 3 are optional supplementary evidence per the Item 16 instructions. Stored as 4-char YYYY text; year arithmetic is never needed.',
    valueType: 'text',
    required: requiredWhenSponsorFiledTaxReturns,
    visibility: requiredWhenSponsorFiledTaxReturns,
    repeatableGroupId: 'sponsor.tax_history',
    repeatableBound: SPONSOR_TAX_HISTORY_BOUND,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 4 },
      { type: 'pattern', pattern: '^\\d{4}$' },
    ],
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.tax_history.total_income'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Total income (from your 1040)',
    helpText:
      'I-864 P6_Line19a_TotalIncome (instance 0) / P6_Line19b_TotalIncome (instance 1) / P6_Line19c_TotalIncome (instance 2). Adjusted gross income per IRS Form 1040 for the matching tax year. Dollar amount stored as number; PDF engine formats.',
    valueType: 'number',
    required: requiredWhenSponsorFiledTaxReturns,
    visibility: requiredWhenSponsorFiledTaxReturns,
    repeatableGroupId: 'sponsor.tax_history',
    repeatableBound: SPONSOR_TAX_HISTORY_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.income.not_required_to_file_tax_return'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label:
      "You weren't required to file a tax return (income below IRS threshold)",
    helpText:
      'I-864 P6_Line17_IWasNotReq (visible item 17). "I was not required to file a Federal income tax return as my income was below the IRS required level and I have attached evidence to support this." USCIS authoring quirk: widget shares `Line17` prefix with the unrelated `P6_Line17_NotNeedComplete` (visible item 14) and `P6_Line17_Name` (visible item 14 name fill-in); FieldNameAlt additionally miscites items "8., 11., 14., and 17." as naming people. Mappings key on the exact widget name.',
    valueType: 'boolean',
    required: false,
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

export const sponsorAssetsFields = [
  {
    id: defineFieldId('sponsor.assets.cash_and_accounts_balance'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Cash, savings & checking balance',
    helpText:
      'I-864 P7_Line1_BalanceofAccounts (visible item 1). Part 7 is conditionally skippable when sponsor income clears the Federal Poverty Guidelines; every asset field is `required: false` at the semantic layer. Widget-line and visible-item numbering agree on Part 7.',
    valueType: 'number',
    required: false,
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.assets.real_estate_net_value'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Real-estate equity (market value minus mortgage)',
    helpText:
      'I-864 P7_Line2_RealEstate (visible item 2). Net value = assessed value minus mortgage debt.',
    valueType: 'number',
    required: false,
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.assets.stocks_bonds_value'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Stocks, bonds, CDs, and other investments',
    helpText:
      'I-864 P7_Line3_StocksBonds (visible item 3). Covers any assets not already in items 1 or 2.',
    valueType: 'number',
    required: false,
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.assets.total'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Sponsor assets — total',
    helpText:
      'I-864 P7_Line4_Total (visible item 4). Derived: sum of `sponsor.assets.cash_and_accounts_balance`, `sponsor.assets.real_estate_net_value`, and `sponsor.assets.stocks_bonds_value`. Stored so the PDF engine can emit without recomputation.',
    valueType: 'number',
    required: false,
    computed: {
      op: 'sum',
      addends: [
        defineFieldId('sponsor.assets.cash_and_accounts_balance'),
        defineFieldId('sponsor.assets.real_estate_net_value'),
        defineFieldId('sponsor.assets.stocks_bonds_value'),
      ],
    },
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.assets.household_members_total'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label:
      "Household members' total assets (from their I-864As)",
    helpText:
      'I-864 P7_Line5_TotalAssetsHouseholdMembers (visible item 5). Roll-up of all Form I-864A Part 4 Item 6 filings. The per-member constituents live on each I-864A and are not modeled as I-864 fields.',
    valueType: 'number',
    required: false,
    visibility: whenSponsorHasHouseholdCoFiler,
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'sponsor.assets.intending_immigrant_cash_and_accounts_balance',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: "Beneficiary's cash, savings & checking",
    helpText:
      "I-864 P7_Line6_BalanceofAccounts (visible item 6). Reported by the sponsor on the sponsor's I-864; stored under `sponsor.assets.intending_immigrant_*` so it sits alongside the sponsor's own asset totals.",
    valueType: 'number',
    required: false,
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'sponsor.assets.intending_immigrant_real_estate_net_value',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: "Beneficiary's real-estate equity",
    helpText:
      'I-864 P7_Line7_RealEstate (visible item 7). Net value = investment value minus mortgage debt (FieldNameAlt wording differs slightly from item 2).',
    valueType: 'number',
    required: false,
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'sponsor.assets.intending_immigrant_stocks_bonds_value',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: "Beneficiary's stocks, bonds, and other investments",
    helpText:
      'I-864 P7_Line8_StocksBonds (visible item 8). Covers any immigrant assets not already in items 6 or 7.',
    valueType: 'number',
    required: false,
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.assets.intending_immigrant_total'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Beneficiary assets — total',
    helpText:
      'I-864 P7_Line9_Total (visible item 9). Derived: sum of the three intending-immigrant asset fields. Stored so the PDF engine can emit without recomputation.',
    valueType: 'number',
    required: false,
    computed: {
      op: 'sum',
      addends: [
        defineFieldId(
          'sponsor.assets.intending_immigrant_cash_and_accounts_balance',
        ),
        defineFieldId(
          'sponsor.assets.intending_immigrant_real_estate_net_value',
        ),
        defineFieldId(
          'sponsor.assets.intending_immigrant_stocks_bonds_value',
        ),
      ],
    },
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.assets.grand_total'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'financials',
    partyRole: 'sponsor',
    label: 'Combined assets — grand total',
    helpText:
      "I-864 P7_Line10_TotalValueAssets (visible item 10). Derived: sum of `sponsor.assets.total`, `sponsor.assets.household_members_total`, and `sponsor.assets.intending_immigrant_total`. Must equal 5× (or 3× for spouses / children of USCs, or 1× for orphans being formally adopted in the U.S.) the difference between the poverty guidelines and the sponsor's household income — per the Part 7 marginal instruction.",
    valueType: 'number',
    required: false,
    computed: {
      op: 'sum',
      addends: [
        defineFieldId('sponsor.assets.total'),
        defineFieldId('sponsor.assets.household_members_total'),
        defineFieldId('sponsor.assets.intending_immigrant_total'),
      ],
    },
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

// ---------------------------------------------------------------------------
// Eligibility & Inadmissibility (I-485 Part 8)
//
// Single-source-of-truth fields under the `eligibility.*` namespace. Drives
// both the I-485 Part 8 yes/no battery during intake and the standalone
// pre-qualification screener that runs before intake. The screener-vs-intake
// orchestration split is a Phase 2 form-engine concern, not a Phase 0
// cataloging concern; do not duplicate these widgets under a `screener.*`
// namespace.
// ---------------------------------------------------------------------------

const eligibilityHasOrganizationMembershipFieldId = defineFieldId(
  'eligibility.organization_memberships.has_any',
);

const requiredWhenHasOrganizationMembership = {
  op: 'equals',
  fieldId: eligibilityHasOrganizationMembershipFieldId,
  value: true,
} as const;

const eligibilityWasJExchangeVisitorFieldId = defineFieldId(
  'eligibility.j_exchange_visitor.was_subject_to_two_year_rule',
);

const requiredWhenWasJExchangeVisitor = {
  op: 'equals',
  fieldId: eligibilityWasJExchangeVisitorFieldId,
  value: true,
} as const;

const eligibilityFailedRemovalProceedingFieldId = defineFieldId(
  'eligibility.immigration_violations.failed_to_attend_removal_proceeding',
);

const requiredWhenFailedRemovalProceeding = {
  op: 'equals',
  fieldId: eligibilityFailedRemovalProceedingFieldId,
  value: true,
} as const;

const eligibilitySubjectToPublicChargeFieldId = defineFieldId(
  'eligibility.public_charge.is_subject_to_inadmissibility_ground',
);

const requiredWhenSubjectToPublicCharge = {
  op: 'equals',
  fieldId: eligibilitySubjectToPublicChargeFieldId,
  value: true,
} as const;

const eligibilityReceivedListedBenefitsFieldId = defineFieldId(
  'eligibility.public_charge.received_listed_cash_benefits',
);

const requiredWhenReceivedListedBenefits = {
  op: 'equals',
  fieldId: eligibilityReceivedListedBenefitsFieldId,
  value: true,
} as const;

const eligibilityReceivedLongTermInstitutionalizationFieldId = defineFieldId(
  'eligibility.public_charge.received_long_term_institutionalization',
);

const requiredWhenReceivedLongTermInstitutionalization = {
  op: 'equals',
  fieldId: eligibilityReceivedLongTermInstitutionalizationFieldId,
  value: true,
} as const;

// Synthetic blanket-question field. Drives derivation of:
//   - eligibility.public_charge.is_subject_to_inadmissibility_ground
//     (Item 61) — auto-true for marriage-based, regardless of summary
//   - eligibility.public_charge.received_listed_cash_benefits (Item 63)
//   - eligibility.public_charge.received_long_term_institutionalization
//     (Item 64)
// Hides the raw boolean intake questions for 63/64 behind a single
// 4-way select so the typical "none" applicant doesn't get jumpscared
// by USCIS's medical/welfare disclosure tree.
const eligibilityPublicChargeBenefitsHistorySummaryFieldId = defineFieldId(
  'eligibility.public_charge.benefits_history_summary',
);

// Always evaluates to false because no valid summary answer equals
// this sentinel. Used to hide the raw boolean fields for Items 63/64
// from intake — they're now derived from the summary in
// injectSystemAnswers, but the catalog fields stay live so the engine
// can still write the PDF.
const hiddenWhenDerivedFromSummary = {
  op: 'equals',
  fieldId: eligibilityPublicChargeBenefitsHistorySummaryFieldId,
  value: '__derived_only__',
} as const;

const eligibilityAvoidedArmedForcesFieldId = defineFieldId(
  'eligibility.miscellaneous.left_us_to_avoid_armed_forces_service',
);

const requiredWhenAvoidedArmedForces = {
  op: 'equals',
  fieldId: eligibilityAvoidedArmedForcesFieldId,
  value: true,
} as const;

export const eligibilityGeneralFields = [
  {
    id: eligibilityHasOrganizationMembershipFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER been a member, involved in, or associated with any organization',
    helpText:
      'I-485 Pt8Line1_YesNo. Headline yes/no for the organization-membership block. Membership covers any organization, association, fund, foundation, party, club, society, or similar group in the United States or anywhere else, including any military service. If yes, applicant lists up to three organizations in items 2–13; overflow goes to Part 14.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.organization_memberships.organization_name'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Organization name',
    helpText:
      'I-485 Pt8Line2_OrgName (slot 1) / Pt8Line6_OrgName (slot 2) / Pt8Line10_OrgName (slot 3). Repeatable per organization slot; instances beyond three overflow to Part 14.',
    valueType: 'text',
    required: requiredWhenHasOrganizationMembership,
    visibility: requiredWhenHasOrganizationMembership,
    repeatableGroupId: 'eligibility.organization_memberships',
    repeatableBound: ELIGIBILITY_ORG_MEMBERSHIPS_BOUND,
    validation: [{ type: 'max_length', value: 200 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('eligibility.organization_memberships.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Organization city or town',
    helpText:
      'I-485 Pt8Line3a_CityTown (slot 1) / Pt8Line8a_CityTown (slot 2 — note USCIS quirk: widget is named `Line8a` even though visible item is 7.a) / Pt8Line11a_CityTown (slot 3).',
    valueType: 'text',
    required: false,
    visibility: requiredWhenHasOrganizationMembership,
    repeatableGroupId: 'eligibility.organization_memberships',
    repeatableBound: ELIGIBILITY_ORG_MEMBERSHIPS_BOUND,
    validation: [{ type: 'max_length', value: 100 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('eligibility.organization_memberships.state_or_province'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Organization state or province',
    helpText:
      'I-485 Pt8Line3b_State (slot 1) / Pt8Line7b_State (slot 2) / Pt8Line11b_State (slot 3).',
    valueType: 'text',
    required: false,
    visibility: requiredWhenHasOrganizationMembership,
    repeatableGroupId: 'eligibility.organization_memberships',
    repeatableBound: ELIGIBILITY_ORG_MEMBERSHIPS_BOUND,
    validation: [{ type: 'max_length', value: 100 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('eligibility.organization_memberships.country'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Organization country',
    helpText:
      'I-485 Pt8Line3c_Country (slot 1) / Pt8Line7c_Country (slot 2) / Pt8Line11c_Country (slot 3). ISO 3166-1 alpha-2 code.',
    valueType: 'select',
    required: false,
    visibility: requiredWhenHasOrganizationMembership,
    repeatableGroupId: 'eligibility.organization_memberships',
    repeatableBound: ELIGIBILITY_ORG_MEMBERSHIPS_BOUND,
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('eligibility.organization_memberships.nature_of_group'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Nature of group',
    helpText:
      'I-485 Pt8Line4_Group (slot 1) / Pt8Line8_Group (slot 2) / Pt8Line12_Group (slot 3). Free-text description of what the organization does.',
    valueType: 'text',
    required: false,
    visibility: requiredWhenHasOrganizationMembership,
    repeatableGroupId: 'eligibility.organization_memberships',
    repeatableBound: ELIGIBILITY_ORG_MEMBERSHIPS_BOUND,
    validation: [{ type: 'max_length', value: 500 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('eligibility.organization_memberships.date_from'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Membership date from',
    helpText:
      'I-485 Pt8Line5a_DateFrom (slot 1) / Pt8Line9a_DateFrom (slot 2) / Pt8Line13a_DateFrom (slot 3).',
    valueType: 'date',
    required: false,
    visibility: requiredWhenHasOrganizationMembership,
    repeatableGroupId: 'eligibility.organization_memberships',
    repeatableBound: ELIGIBILITY_ORG_MEMBERSHIPS_BOUND,
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('eligibility.organization_memberships.date_to'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Membership date to',
    helpText:
      'I-485 Pt8Line5b_DateTo (slot 1) / Pt8Line9b_DateTo (slot 2) / Pt8Line13b_DateTo (slot 3).',
    valueType: 'date',
    required: false,
    visibility: requiredWhenHasOrganizationMembership,
    repeatableGroupId: 'eligibility.organization_memberships',
    repeatableBound: ELIGIBILITY_ORG_MEMBERSHIPS_BOUND,
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('eligibility.prior_admission.was_denied_admission'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Has EVER been denied admission to the United States',
    helpText: 'I-485 Pt8Line14_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.prior_admission.was_denied_visa'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Has EVER been denied a visa to the United States',
    helpText: 'I-485 Pt8Line15_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.prior_admission.worked_without_authorization',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER worked in the United States without authorization',
    helpText: 'I-485 Pt8Line16_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.prior_admission.violated_nonimmigrant_status',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Has EVER violated terms or conditions of nonimmigrant status',
    helpText: 'I-485 Pt8Line17_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.removal.in_or_was_in_proceedings'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Is presently or has EVER been in removal, exclusion, rescission, or deportation proceedings',
    helpText: 'I-485 Pt8Line18_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.removal.has_final_order'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER been issued a final order of exclusion, deportation, or removal',
    helpText: 'I-485 Pt8Line19_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.removal.had_prior_order_reinstated'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER had a prior final order of exclusion, deportation, or removal reinstated',
    helpText: 'I-485 Pt8Line20_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.removal.lpr_status_rescinded'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER held lawful permanent resident status which was later rescinded',
    helpText: 'I-485 Pt8Line21_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.removal.granted_voluntary_departure_then_failed_to_depart',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER been granted voluntary departure but failed to depart within the allotted time',
    helpText: 'I-485 Pt8Line22_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.removal.applied_for_relief_or_protection',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER applied for any kind of relief or protection from removal, exclusion, or deportation',
    helpText: 'I-485 Pt8Line23_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: eligibilityWasJExchangeVisitorFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER been a J nonimmigrant exchange visitor subject to the two-year foreign residence requirement',
    helpText: 'I-485 Pt8Line24a_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.j_exchange_visitor.complied_with_two_year_rule',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has complied with the two-year foreign residence requirement',
    helpText: 'I-485 Pt8Line24b_YesNo. Conditional on item 24.a yes.',
    valueType: 'boolean',
    required: requiredWhenWasJExchangeVisitor,
    visibility: requiredWhenWasJExchangeVisitor,
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'eligibility.j_exchange_visitor.granted_waiver_or_recommendation',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has been granted a waiver or favorable Department of State waiver recommendation',
    helpText:
      'I-485 Pt8Line24c_YesNo. Conditional on item 24.a yes; required when 24.b is no.',
    valueType: 'boolean',
    required: requiredWhenWasJExchangeVisitor,
    visibility: requiredWhenWasJExchangeVisitor,
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

export const eligibilityCriminalFields = [
  {
    id: defineFieldId('eligibility.criminal.has_been_arrested_or_detained'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER been arrested, cited, charged, or detained by any law enforcement official',
    helpText:
      'I-485 Pt8Line25_YesNo. Includes U.S. immigration officials and U.S. armed forces or U.S. Coast Guard.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.criminal.has_committed_crime'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER committed a crime of any kind, even if not arrested, cited, charged, or tried',
    helpText: 'I-485 Pt8Line26_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.criminal.has_pled_guilty_or_convicted'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER pled guilty to or been convicted of a crime (even if expunged, sealed, pardoned, or rehabilitated)',
    helpText: 'I-485 Pt8Line27_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.criminal.has_been_sentenced_or_restrained'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER been ordered punished or had liberty restrained by a judge — prison, suspended sentence, house arrest, parole, alternative sentencing, drug or alcohol treatment, rehab, probation, community service',
    helpText: 'I-485 Pt8Line28_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.criminal.has_been_defendant_in_proceeding',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER been a defendant or accused in a criminal proceeding, including pre-trial diversion, deferred prosecution, or withheld adjudication',
    helpText: 'I-485 Pt8Line29_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.criminal.violated_controlled_substance_law'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER violated, attempted, or conspired to violate a controlled-substance law of any state, the U.S., or a foreign country',
    helpText: 'I-485 Pt8Line30_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.criminal.convicted_two_offenses_combined_five_years',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER been convicted of two or more offenses with combined sentences of five years or more',
    helpText:
      'I-485 Pt8Line31_YesNo. Excludes purely political offenses.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.criminal.trafficked_controlled_substance'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER illicitly trafficked or benefited from trafficking of any controlled substance',
    helpText: 'I-485 Pt8Line32_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.criminal.aided_controlled_substance_trafficking',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER knowingly aided, abetted, assisted, conspired, or colluded in illicit narcotics or controlled-substance trafficking',
    helpText: 'I-485 Pt8Line33_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.criminal.benefited_from_family_member_trafficking',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Is the spouse, son, or daughter of a foreign national who trafficked controlled substances and applicant benefited financially within the last five years',
    helpText: 'I-485 Pt8Line34_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.criminal.engaged_in_prostitution'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER engaged in prostitution or is coming to the U.S. to engage in prostitution',
    helpText: 'I-485 Pt8Line35_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line30_YesNo[1]',
          false: 'Pt8Line30_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.criminal.procured_prostitutes'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER directly or indirectly procured or imported prostitutes for the purpose of prostitution',
    helpText: 'I-485 Pt8Line36_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.criminal.received_proceeds_from_prostitution'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER received any proceeds or money from prostitution',
    helpText: 'I-485 Pt8Line37_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.criminal.intends_illegal_gambling_or_commercialized_vice',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Intends to engage in illegal gambling, prostitution, bootlegging, or sale of child pornography in the U.S.',
    helpText: 'I-485 Pt8Line38_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.criminal.exercised_immunity_to_avoid_prosecution'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER exercised diplomatic or other immunity to avoid criminal prosecution in the U.S.',
    helpText: 'I-485 Pt8Line39_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line34_YesNo[1]',
          false: 'Pt8Line34_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.criminal.violated_religious_freedoms_as_official',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER, while serving as a foreign government official, been responsible for or carried out violations of religious freedoms',
    helpText: 'I-485 Pt8Line40_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.criminal.induced_commercial_sex_acts_by_force',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER induced, by force, fraud, or coercion (or otherwise been involved in) trafficking persons for commercial sex acts',
    helpText: 'I-485 Pt8Line41_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.criminal.trafficked_persons_for_servitude',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER trafficked persons for involuntary servitude, peonage, debt bondage, or slavery',
    helpText: 'I-485 Pt8Line42_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line37_YesNo[1]',
          false: 'Pt8Line37_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.criminal.aided_human_trafficking',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER knowingly aided, abetted, assisted, conspired, or colluded with others in trafficking persons',
    helpText:
      'I-485 Pt8Line38_YesNo. Item 38 — aided trafficking. (Earlier inline projection at Pt8Line27 was wrong — Item 27 is the controlled-substance trafficking question; this field rebinds to its semantic match via JSON.)',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.criminal.benefited_from_family_member_human_trafficking',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Is the spouse, son, or daughter of a foreign national who engaged in trafficking persons and benefited financially within the last five years',
    helpText: 'I-485 Pt8Line44_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.criminal.engaged_in_money_laundering'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER engaged or conspired in money laundering, or seeks to enter the U.S. for that purpose',
    helpText: 'I-485 Pt8Line45_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
] as const satisfies readonly FieldDefinition[];

export const eligibilitySecurityFields = [
  {
    id: defineFieldId('eligibility.security.intends_espionage_or_sabotage'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Intends to engage in espionage (including spying) or sabotage in the U.S. (item 42.A)',
    helpText: 'I-485 01/20/25 Pt8Line42\\.a_YesNo (note the escaped dot in the widget basename).',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line42\\.a_YesNo[0]',
          false: 'Pt8Line42\\.a_YesNo[1]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.security.intends_export_violation'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Intends to engage in activity that violates U.S. export laws for goods, technology, or sensitive information (item 42.B)',
    helpText: 'I-485 01/20/25 Pt8Line42\\.b_YesNo (note the escaped dot in the widget basename). Yes index is [1]/No is [0] for this widget.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line42\\.b_YesNo[1]',
          false: 'Pt8Line42\\.b_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.security.intends_to_overthrow_government',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Intends to engage in activity to oppose, control, or overthrow the U.S. Government by force, violence, or other unlawful means',
    helpText: 'I-485 Pt8Line46c_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line42c_YesNo[1]',
          false: 'Pt8Line42c_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.security.intends_endanger_us_safety'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Intends to engage in activity that could endanger the welfare, safety, or security of the U.S.',
    helpText:
      'I-485 Pt8Line45_YesNo. Item 45. (Catalog helpText previously said Pt8Line46d which is not a real widget on the 01/20/25 edition; the inline widgetValueProjection below has always pointed at the correct Pt8Line45 widget.)',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line45_YesNo[1]',
          false: 'Pt8Line45_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.security.intends_other_unlawful_activity'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Intends to engage in any other unlawful activity',
    helpText: 'I-485 Pt8Line46e_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line42d_YesNo[0]',
          false: 'Pt8Line42d_YesNo[1]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.security.adverse_foreign_policy_consequences',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Engaged in or intends activity that could have potentially serious adverse foreign-policy consequences for the U.S.',
    helpText: 'I-485 Pt8Line47_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line42d_YesNo[0]',
          false: 'Pt8Line42d_YesNo[1]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.security.committed_terrorist_activity'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER committed, threatened, attempted, conspired, incited, endorsed, advocated, planned, or prepared hijacking, sabotage, kidnapping, political assassination, or use of a weapon or explosive against persons or property',
    helpText: 'I-485 Pt8Line48a_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line43b_YesNo[0]',
          false: 'Pt8Line43b_YesNo[1]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.security.member_of_terrorist_group'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER been a member of a group or organization that did any activity in item 48.a',
    helpText: 'I-485 Pt8Line48b_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line43fYesNo[0]',
          false: 'Pt8Line43fYesNo[1]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.security.recruited_for_terrorist_group',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER recruited members or asked for money or things of value for a group that did any activity in item 48.a',
    helpText: 'I-485 Pt8Line48c_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line45_YesNo[1]',
          false: 'Pt8Line45_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.security.provided_material_support_to_terrorist_group',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER provided money, value, services, or labor to an individual, group, or organization that did any activity in item 48.a',
    helpText: 'I-485 Pt8Line48d_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line43b_YesNo[0]',
          false: 'Pt8Line43b_YesNo[1]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.security.provided_material_support_to_terrorist_group_alt',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Provided money, value, services, or labor to an individual, group, or organization that did any activity in item 48.a — duplicate widget',
    helpText:
      'I-485 Pt8Line48e_YesNo. USCIS authoring quirk: 48.e widget exists in the AcroForm but its wording duplicates 48.d. Catalogued separately so the PDF engine fills both consistently when the underlying answer is yes.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line43fYesNo[0]',
          false: 'Pt8Line43fYesNo[1]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.security.received_weapons_training'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER received any type of military, paramilitary, or weapons training',
    helpText: 'I-485 Pt8Line49_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line43g_YesNo[0]',
          false: 'Pt8Line43g_YesNo[1]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.security.intends_to_engage_in_48a_to_49'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Intends to engage in any of the activities listed in items 48.a–49',
    helpText: 'I-485 Pt8Line50_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line43h_YesNo[1]',
          false: 'Pt8Line43h_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.security.associated_with_violent_individual_committed',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Committed, threatened, attempted, conspired, incited, endorsed, advocated, planned, or prepared violent activity in association with an individual you knew about',
    helpText: 'I-485 Pt8Line51a_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line43i_YesNo[1]',
          false: 'Pt8Line43i_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.security.associated_with_violent_individual_member',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Participated in or been a member or representative of a group that did any activity in item 51.a',
    helpText: 'I-485 Pt8Line51b_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line46_YesNo[0]',
          false: 'Pt8Line46_YesNo[1]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.security.associated_with_violent_individual_recruited',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Recruited members or asked for money or things of value for a group that did any activity in item 51.a',
    helpText: 'I-485 Pt8Line51c_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.security.associated_with_violent_individual_supported_activity',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Provided money, value, services, or labor for any of the activities described in item 51.a',
    helpText: 'I-485 Pt8Line51d_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.security.associated_with_violent_individual_supported_actor',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Provided money, value, services, or labor to an individual, group, or organization that did any activity in item 51.a',
    helpText: 'I-485 Pt8Line51e_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.security.associated_with_violent_individual_received_training',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Received military, paramilitary, or weapons training from a group that did any activity in item 51.a',
    helpText: 'I-485 Pt8Line51f_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.security.transported_weapons'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER assisted or participated in selling, providing, or transporting weapons to any person who used them against another person',
    helpText: 'I-485 Pt8Line52_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line47_YesNo[1]',
          false: 'Pt8Line47_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.security.served_in_detention_facility'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER worked, volunteered, or served in any prison, jail, prison camp, detention facility, labor camp, or other situation involving detained persons',
    helpText: 'I-485 Pt8Line53_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line48_YesNo[1]',
          false: 'Pt8Line48_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.security.member_of_armed_group_with_weapons',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER been a member of, assisted, or participated in a group that used or threatened weapons against persons',
    helpText: 'I-485 Pt8Line54_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line47_YesNo[1]',
          false: 'Pt8Line47_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.security.served_in_irregular_armed_unit',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER served in or been a member of a military, paramilitary, police, vigilante, rebel, guerilla, militia, insurgent, or other armed group',
    helpText: 'I-485 Pt8Line55_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line48_YesNo[1]',
          false: 'Pt8Line48_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.security.member_of_communist_or_totalitarian_party',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER been a member of, or affiliated with, the Communist Party or any other totalitarian party in the U.S. or abroad',
    helpText: 'I-485 Pt8Line56_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line52_YesNo[1]',
          false: 'Pt8Line52_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.security.nazi_persecution_1933_to_1945'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'During March 23 1933 – May 8 1945 ordered, incited, assisted, or participated in persecution because of race, religion, national origin, or political opinion in association with the Nazi government of Germany or its allies',
    helpText: 'I-485 Pt8Line57_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.security.committed_torture_or_genocide'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Acts involving torture or genocide',
    helpText: 'I-485 Pt8Line58a_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.security.killed_any_person'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Killing any person',
    helpText: 'I-485 Pt8Line58b_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line53c_YesNo[0]',
          false: 'Pt8Line53c_YesNo[1]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.security.severely_injured_any_person'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Intentionally and severely injuring any person',
    helpText: 'I-485 Pt8Line58c_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line53d_YesNo[1]',
          false: 'Pt8Line53d_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.security.engaged_in_nonconsensual_sexual_contact'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Engaging in any kind of sexual contact with a person who did not consent or was unable to consent',
    helpText: 'I-485 Pt8Line58d_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line53c_YesNo[0]',
          false: 'Pt8Line53c_YesNo[1]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.security.limited_religious_belief'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      "Limiting or denying any person's ability to exercise religious beliefs",
    helpText: 'I-485 Pt8Line58e_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line53d_YesNo[1]',
          false: 'Pt8Line53d_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.security.recruited_child_soldier'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER recruited, enlisted, conscripted, or used any person under 15 years of age in an armed force or group',
    helpText: 'I-485 Pt8Line59_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line54_YesNo[1]',
          false: 'Pt8Line54_YesNo[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.security.used_child_in_hostilities'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER used any person under 15 years of age to take part in hostilities or to support combatants',
    helpText: 'I-485 Pt8Line60_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line55_YesNo[0]',
          false: 'Pt8Line55_YesNo[1]',
        },
      },
    ],
  },
  // 2026-05-27 — new i-485 Pt8 items missing from the prior catalog.
  // Items 35.a, 51 are sworn Yes/No questions that print blank on
  // every generated packet today because no catalog field covers them.
  {
    id: defineFieldId('eligibility.security.served_as_foreign_govt_official'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Has EVER served as a foreign government official',
    helpText:
      'I-485 Pt8Line35a_YesNo. Item 35.a — sworn answer. If yes, Item 35.b asks whether the role involved violations of religious freedoms; the form-engine surfaces 35.b only when 35.a = Yes.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.security.served_in_armed_group'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER served in, been a member of, assisted, or participated in any armed group (paramilitary unit, self-defense unit, vigilante unit, rebel group, or guerrilla group)',
    helpText:
      'I-485 Pt8Line51_YesNo. Item 51 — sworn answer. If yes, the form instructions require the applicant to detail the country, unit, rank/position, and dates in Part 14 Additional Information.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  // 2026-05-27 — new i-485 Pt8 security / inadmissibility items added
  // to round out Part 9. Widget basenames verified against the
  // 01/20/25 fixture's TU (tooltip) metadata.
  {
    id: defineFieldId('eligibility.security.intends_overthrow_government'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Intends to engage in any activity whose purpose includes opposing, controlling, or overthrowing the U.S. Government by force, violence, or other unlawful means',
    helpText: 'I-485 Pt8Line42c_YesNo. Item 42.C.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.security.committed_kidnapping_or_hijacking'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER committed kidnapping, assassination, or hijacking or sabotage of a conveyance (aircraft, vessel, vehicle, or train)',
    helpText: 'I-485 Pt8Line43b_YesNo. Item 43.B.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.security.used_weapon_or_explosive'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER used a weapon or explosive or any dangerous device with the intent to endanger the safety of another person or people or cause damage to property',
    helpText: 'I-485 Pt8Line43c_YesNo. Item 43.C.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.security.threatened_43b_43c_acts'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER threatened, attempted, conspired, prepared, or planned to do any of the activities described in Item Numbers 43.B. – 43.C.',
    helpText: 'I-485 Pt8Line43d_YesNo. Item 43.D.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.security.incited_43b_43c_acts'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER incited, under circumstances indicating an intention to cause death or serious bodily harm or injury, any of the activities described in Item Numbers 43.B. – 43.C.',
    helpText: 'I-485 Pt8Line43e_YesNo. Item 43.E.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.security.recruited_for_43b_43e_group'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER recruited members or asked for money or things of value for a group or organization that did any of the activities in Items 43.B. – 43.E.',
    helpText: 'I-485 Pt8Line43g_YesNo. Item 43.G.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.security.provided_material_support_to_group'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER provided money, services, labor, or any other assistance for any of the activities described in Items 43.B. – 43.E. by a group or organization',
    helpText: 'I-485 Pt8Line43h_YesNo. Item 43.H.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.security.provided_material_support_to_individual'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER provided money, services, labor, or any other assistance for an individual, group, or organization that did any of the activities described in Items 43.B. – 43.E.',
    helpText: 'I-485 Pt8Line43i_YesNo. Item 43.I.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.security.intends_43b_43i_activity'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Intends to engage in any of the activities listed in Items 43.B. – 43.I.',
    helpText: 'I-485 Pt8Line44_YesNo. Item 44.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  // Item 45 (endanger welfare/safety/security) is already covered by
  // `eligibility.security.intends_endanger_us_safety` above — that
  // field's inline widgetValueProjection correctly points at
  // Pt8Line45. No new field needed; the catalog's helpText is being
  // corrected in a separate edit.
  {
    id: defineFieldId('eligibility.security.spouse_of_42_44_perpetrator'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Is the spouse or child of an individual who EVER engaged in any of the activities described in Items 42. – 44.',
    helpText: 'I-485 Pt8Line46_YesNo. Item 46.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.security.member_of_violent_group'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER been a member of, assisted, or participated in any group, unit, or organization that used a weapon against any person, or threatened to do so',
    helpText: 'I-485 Pt8Line49_YesNo. Item 49.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.security.served_in_military_paramilitary'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER served in, been a member of, assisted, or participated in any military unit, paramilitary unit, police unit, civil patrol, militia, rebel group, guerrilla group, or insurgent organization',
    helpText: 'I-485 Pt8Line50_YesNo. Item 50.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.security.communist_party_member'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER been a member of, or in any way affiliated with, the Communist Party or any totalitarian party in the U.S. or abroad',
    helpText: 'I-485 Pt8Line52_YesNo. Item 52.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
] as const satisfies readonly FieldDefinition[];

export const eligibilityPublicChargeFields = [
  {
    id: eligibilitySubjectToPublicChargeFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Subject to the public-charge ground of inadmissibility under INA 212(a)(4)',
    helpText:
      'I-485 Pt8Line61_YesNo. Headline yes/no for the public-charge block. Marriage-based filers are always subject (the whole point of Form I-864 is to defeat the public-charge ground), so this is hidden from intake and auto-defaulted to true via injectSystemAnswers. Visible only on a legacy fallback path (`benefits_history_summary` unanswered).',
    valueType: 'boolean',
    required: hiddenWhenDerivedFromSummary,
    visibility: hiddenWhenDerivedFromSummary,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line54_YesNo[1]',
          false: 'Pt8Line54_YesNo[0]',
        },
      },
    ],
  },
  {
    id: eligibilityPublicChargeBenefitsHistorySummaryFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Have you ever received government cash assistance or long-term institutional care?',
    helpText:
      'One blanket question that drives I-485 Part 9 Items 63 + 64. "Cash assistance" = SSI, TANF, or state/local "General Assistance." "Long-term institutional care" = hospital, nursing home, or similar facility paid by a government program. Most marriage-based filers select "No, never" — we only ask the per-row details if the answer is Yes. Synthetic — no direct PDF widget; drives derivation in injectSystemAnswers.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
  },
  {
    id: defineFieldId('eligibility.public_charge.exemption_basis'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Are you exempt from the public-charge ground of inadmissibility?',
    helpText:
      'I-485 01/20/25 Pt9Line56_CB[0..24]. Most marriage-based filers pick `not_exempt_complete_57_67` (CB[23]). Auto-defaulted via injectSystemAnswers; the applicant can override in intake if they qualify for an exemption. Distinct from `affidavit_of_support.exemption_basis` (Pt3Line1_CB) which gates Form I-864.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          afghan_iraqi_i360_ds157: 'Pt9Line56_CB[0]',
          lautenberg_parolee_general: 'Pt9Line56_CB[1]',
          u_nonimmigrant_245m: 'Pt9Line56_CB[2]',
          asylee_i589_i730: 'Pt9Line56_CB[3]',
          u_status_other_245m: 'Pt9Line56_CB[4]',
          vawa_self_petitioner: 'Pt9Line56_CB[5]',
          special_immigrant_juvenile: 'Pt9Line56_CB[6]',
          cuban_adjustment_act: 'Pt9Line56_CB[7]',
          hrifa_dependent_battered: 'Pt9Line56_CB[8]',
          t_nonimmigrant_245l: 'Pt9Line56_CB[9]',
          cuban_adjustment_battered: 'Pt9Line56_CB[10]',
          hrifa_dependent: 'Pt9Line56_CB[11]',
          t_status_other_245l: 'Pt9Line56_CB[12]',
          registry_pre_1972: 'Pt9Line56_CB[13]',
          nacara: 'Pt9Line56_CB[14]',
          amerasian_homecoming: 'Pt9Line56_CB[15]',
          polish_hungarian_parolee: 'Pt9Line56_CB[16]',
          cuban_haitian_irca: 'Pt9Line56_CB[17]',
          lautenberg_parolee: 'Pt9Line56_CB[18]',
          foreign_operations_seah: 'Pt9Line56_CB[19]',
          american_indian_canada: 'Pt9Line56_CB[20]',
          liberian_refugee_immigration_fairness: 'Pt9Line56_CB[21]',
          spouse_child_parent_us_citizen: 'Pt9Line56_CB[22]',
          not_exempt_complete_57_67: 'Pt9Line56_CB[23]',
          syrian_national_pl_106_378: 'Pt9Line56_CB[24]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.public_charge.household_size'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Household size',
    helpText:
      'I-485 01/20/25 Pt9Line57_HouseholdSize. Free-text count of household members for the Public Charge analysis.',
    valueType: 'number',
    required: requiredWhenSubjectToPublicCharge,
    visibility: requiredWhenSubjectToPublicCharge,
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('eligibility.public_charge.annual_household_income_bracket'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Annual household income bracket',
    helpText:
      'I-485 01/20/25 Pt9Line53_CB[0..4] (printed Item 58 — the widget basename is offset from the visible item number). One of: $0–$27,000 / $27,001–$52,000 / $52,001–$85,000 / $85,001–$141,000 / Over $141,000.',
    valueType: 'select',
    required: requiredWhenSubjectToPublicCharge,
    visibility: requiredWhenSubjectToPublicCharge,
    pdfConsumers: [
      {
        ...i485Consumer,
        required: false,
        widgetValueProjection: {
          '0_to_27000': 'Pt9Line53_CB[0]',
          '27001_to_52000': 'Pt9Line53_CB[1]',
          '52001_to_85000': 'Pt9Line53_CB[2]',
          '85001_to_141000': 'Pt9Line53_CB[3]',
          'over_141000': 'Pt9Line53_CB[4]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.public_charge.household_assets_bracket'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Total value of household assets bracket',
    helpText:
      'I-485 01/20/25 Pt9Line59_CB[0..4] (printed Item 59). One of: $0–$18,400 / $18,401–$136,000 / $136,001–$321,400 / $321,401–$707,100 / Over $707,100.',
    valueType: 'select',
    required: requiredWhenSubjectToPublicCharge,
    visibility: requiredWhenSubjectToPublicCharge,
    pdfConsumers: [
      {
        ...i485Consumer,
        required: false,
        widgetValueProjection: {
          '0_to_18400': 'Pt9Line59_CB[0]',
          '18401_to_136000': 'Pt9Line59_CB[1]',
          '136001_to_321400': 'Pt9Line59_CB[2]',
          '321401_to_707100': 'Pt9Line59_CB[3]',
          'over_707100': 'Pt9Line59_CB[4]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.public_charge.household_liabilities_bracket'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Total value of household liabilities bracket',
    helpText:
      'I-485 01/20/25 Pt9Line60_CB[0..4] (printed Item 60). One of: $0 / $1–$10,100 / $10,101–$57,700 / $57,701–$186,800 / Over $186,800.',
    valueType: 'select',
    required: requiredWhenSubjectToPublicCharge,
    visibility: requiredWhenSubjectToPublicCharge,
    pdfConsumers: [
      {
        ...i485Consumer,
        required: false,
        widgetValueProjection: {
          'zero': 'Pt9Line60_CB[0]',
          '1_to_10100': 'Pt9Line60_CB[1]',
          '10101_to_57700': 'Pt9Line60_CB[2]',
          '57701_to_186800': 'Pt9Line60_CB[3]',
          'over_186800': 'Pt9Line60_CB[4]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.public_charge.highest_education_degree'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Highest education degree completed',
    helpText:
      'I-485 Pt9Line61_CB[0..8]. One of: Less than high school diploma / High school diploma or GED / 1+ years college no degree / Associate / Bachelor / Master / Professional (JD/MD/DMD) / Doctorate / (unused). This is a public-charge bracket selector and does NOT establish a `beneficiary.education.*` namespace; education in general is not collected by the AOS v1 form set.',
    valueType: 'select',
    required: requiredWhenSubjectToPublicCharge,
    visibility: requiredWhenSubjectToPublicCharge,
    pdfConsumers: [
      {
        ...i485Consumer,
        required: false,
        widgetValueProjection: {
          'less_than_high_school_diploma': 'Pt9Line61_CB[0]',
          'high_school_diploma_or_ged': 'Pt9Line61_CB[1]',
          'one_or_more_years_college_no_degree': 'Pt9Line61_CB[2]',
          'associates_degree': 'Pt9Line61_CB[3]',
          'bachelors_degree': 'Pt9Line61_CB[4]',
          'masters_degree': 'Pt9Line61_CB[5]',
          'professional_degree': 'Pt9Line61_CB[6]',
          'doctorate_degree': 'Pt9Line61_CB[7]',
        },
      },
    ],
  },
  {
    id: eligibilityReceivedListedBenefitsFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has ever received SSI, TANF, or State/Tribal/territorial/local cash benefits for income maintenance',
    helpText:
      'I-485 01/20/25 Pt9Line63_YesNo. "General Assistance" in the State context. Hidden from intake — derived from `benefits_history_summary` via injectSystemAnswers and written to the PDF.',
    valueType: 'boolean',
    required: hiddenWhenDerivedFromSummary,
    visibility: hiddenWhenDerivedFromSummary,
    pdfConsumers: [
      {
        ...i485Consumer,
        required: false,
        widgetValueProjection: {
          true: 'Pt9Line63_YesNo[0]',
          false: 'Pt9Line63_YesNo[1]',
        },
      },
    ],
  },
  {
    id: eligibilityReceivedLongTermInstitutionalizationFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has ever received long-term institutionalization at government expense',
    helpText:
      'I-485 01/20/25 Pt9Line64_YesNo. Hidden from intake — derived from `benefits_history_summary` via injectSystemAnswers and written to the PDF.',
    valueType: 'boolean',
    required: hiddenWhenDerivedFromSummary,
    visibility: hiddenWhenDerivedFromSummary,
    pdfConsumers: [
      {
        ...i485Consumer,
        required: false,
        widgetValueProjection: {
          true: 'Pt9Line64_YesNo[0]',
          false: 'Pt9Line64_YesNo[1]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.public_benefits_received.benefit_type',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Public benefit received — benefit type',
    helpText:
      'I-485 01/20/25 Pt8Line68c_Column1Row1..Row4 (printed Item 65). Repeatable group with up to four rows on the printed PDF; overflow goes to Part 14. (Earlier catalog had this swapped with the institutionalization group on Pt8Line68d.)',
    valueType: 'text',
    required: false,
    visibility: requiredWhenReceivedListedBenefits,
    repeatableGroupId: 'eligibility.public_benefits_received',
    repeatableBound: ELIGIBILITY_PUBLIC_BENEFITS_BOUND,
    validation: [{ type: 'max_length', value: 200 }],
    pdfConsumers: [
      {
        ...i485Consumer,
        required: false,
        widgetNames: [
          'Pt8Line68c_Column1Row1[0]',
          'Pt8Line68c_Column1Row2[0]',
          'Pt8Line68c_Column1Row3[0]',
          'Pt8Line68c_Column1Row4[0]',
        ],
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.public_benefits_received.start_date',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Public benefit received — start date',
    helpText: 'I-485 01/20/25 Pt8Line68c_Column2Row1..Row4 (printed Item 65 start date).',
    valueType: 'date',
    required: false,
    visibility: requiredWhenReceivedListedBenefits,
    repeatableGroupId: 'eligibility.public_benefits_received',
    repeatableBound: ELIGIBILITY_PUBLIC_BENEFITS_BOUND,
    pdfConsumers: [
      {
        ...i485Consumer,
        required: false,
        widgetNames: [
          'Pt8Line68c_Column2Row1[0]',
          'Pt8Line68c_Column2Row2[0]',
          'Pt8Line68c_Column2Row3[0]',
          'Pt8Line68c_Column2Row4[0]',
        ],
      },
    ],
  },
  {
    id: defineFieldId('eligibility.public_benefits_received.end_date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Public benefit received — end date',
    helpText: 'I-485 01/20/25 Pt8Line68c_Column3Row1..Row4.',
    valueType: 'date',
    required: false,
    visibility: requiredWhenReceivedListedBenefits,
    repeatableGroupId: 'eligibility.public_benefits_received',
    repeatableBound: ELIGIBILITY_PUBLIC_BENEFITS_BOUND,
    pdfConsumers: [
      {
        ...i485Consumer,
        required: false,
        widgetNames: [
          'Pt8Line68c_Column3Row1[0]',
          'Pt8Line68c_Column3Row2[0]',
          'Pt8Line68c_Column3Row3[0]',
          'Pt8Line68c_Column3Row4[0]',
        ],
      },
    ],
  },
  {
    id: defineFieldId('eligibility.public_benefits_received.dollar_amount'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Public benefit received — dollar amount',
    helpText: 'I-485 01/20/25 Pt8Line68c_Column4Row1..Row4.',
    valueType: 'number',
    required: false,
    visibility: requiredWhenReceivedListedBenefits,
    repeatableGroupId: 'eligibility.public_benefits_received',
    repeatableBound: ELIGIBILITY_PUBLIC_BENEFITS_BOUND,
    pdfConsumers: [
      {
        ...i485Consumer,
        required: false,
        widgetNames: [
          'Pt8Line68c_Column4Row1[0]',
          'Pt8Line68c_Column4Row2[0]',
          'Pt8Line68c_Column4Row3[0]',
          'Pt8Line68c_Column4Row4[0]',
        ],
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.public_benefits_received.received_means_tested',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Public benefit — received a Federal means-tested benefit?',
    helpText:
      'I-485 01/20/25 Pt9Line65_Row{1..4}_YesNo. Per-row yes/no column added in the 01/20/25 edition. Each row is a Yes[0]/No[1] pair; engine recognizes the pair convention and writes the answer to the appropriate cell per repeatable instance.',
    valueType: 'boolean',
    required: false,
    visibility: requiredWhenReceivedListedBenefits,
    repeatableGroupId: 'eligibility.public_benefits_received',
    repeatableBound: ELIGIBILITY_PUBLIC_BENEFITS_BOUND,
    pdfConsumers: [
      {
        ...i485Consumer,
        required: false,
        widgetNames: [
          'Pt9Line65_Row1_YesNo[0]',
          'Pt9Line65_Row1_YesNo[1]',
          'Pt9Line65_Row2_YesNo[0]',
          'Pt9Line65_Row2_YesNo[1]',
          'Pt9Line65_Row3_YesNo[0]',
          'Pt9Line65_Row3_YesNo[1]',
          'Pt9Line65_Row4_YesNo[0]',
          'Pt9Line65_Row4_YesNo[1]',
        ],
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.long_term_institutionalization.institution_name_and_location',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Long-term institutionalization — institution name, city, state',
    helpText:
      'I-485 01/20/25 Pt8Line68d_Column1Row1..Row4 (printed Item 66). Repeatable group with up to four rows on the printed PDF. (Earlier catalog had this swapped with the benefits group on Pt8Line68c.)',
    valueType: 'text',
    required: false,
    visibility: requiredWhenReceivedLongTermInstitutionalization,
    repeatableGroupId: 'eligibility.long_term_institutionalization',
    repeatableBound: ELIGIBILITY_LONG_TERM_INSTITUTIONALIZATION_BOUND,
    validation: [{ type: 'max_length', value: 300 }],
    pdfConsumers: [
      {
        ...i485Consumer,
        required: false,
        widgetNames: [
          'Pt8Line68d_Column1Row1[0]',
          'Pt8Line68d_Column1Row2[0]',
          'Pt8Line68d_Column1Row3[0]',
          'Pt8Line68d_Column1Row4[0]',
        ],
      },
    ],
  },
  {
    id: defineFieldId('eligibility.long_term_institutionalization.date_from'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Long-term institutionalization — date from',
    helpText: 'I-485 01/20/25 Pt8Line68d_Column2Row1..Row4.',
    valueType: 'date',
    required: false,
    visibility: requiredWhenReceivedLongTermInstitutionalization,
    repeatableGroupId: 'eligibility.long_term_institutionalization',
    repeatableBound: ELIGIBILITY_LONG_TERM_INSTITUTIONALIZATION_BOUND,
    pdfConsumers: [
      {
        ...i485Consumer,
        required: false,
        widgetNames: [
          'Pt8Line68d_Column2Row1[0]',
          'Pt8Line68d_Column2Row2[0]',
          'Pt8Line68d_Column2Row3[0]',
          'Pt8Line68d_Column2Row4[0]',
        ],
      },
    ],
  },
  {
    id: defineFieldId('eligibility.long_term_institutionalization.date_to'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Long-term institutionalization — date to',
    helpText: 'I-485 01/20/25 Pt8Line68d_Column3Row1..Row4.',
    valueType: 'date',
    required: false,
    visibility: requiredWhenReceivedLongTermInstitutionalization,
    repeatableGroupId: 'eligibility.long_term_institutionalization',
    repeatableBound: ELIGIBILITY_LONG_TERM_INSTITUTIONALIZATION_BOUND,
    pdfConsumers: [
      {
        ...i485Consumer,
        required: false,
        widgetNames: [
          'Pt8Line68d_Column3Row1[0]',
          'Pt8Line68d_Column3Row2[0]',
          'Pt8Line68d_Column3Row3[0]',
          'Pt8Line68d_Column3Row4[0]',
        ],
      },
    ],
  },
  {
    id: defineFieldId('eligibility.long_term_institutionalization.reason'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Long-term institutionalization — reason',
    helpText: 'I-485 01/20/25 Pt8Line68d_Column4Row1..Row4.',
    valueType: 'text',
    required: false,
    visibility: requiredWhenReceivedLongTermInstitutionalization,
    repeatableGroupId: 'eligibility.long_term_institutionalization',
    repeatableBound: ELIGIBILITY_LONG_TERM_INSTITUTIONALIZATION_BOUND,
    validation: [{ type: 'max_length', value: 300 }],
    pdfConsumers: [
      {
        ...i485Consumer,
        required: false,
        widgetNames: [
          'Pt8Line68d_Column4Row1[0]',
          'Pt8Line68d_Column4Row2[0]',
          'Pt8Line68d_Column4Row3[0]',
          'Pt8Line68d_Column4Row4[0]',
        ],
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.long_term_institutionalization.received_means_tested',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Institutionalization — received Federal means-tested benefit?',
    helpText:
      'I-485 01/20/25 Pt9Line66_Row{1..4}_YesNo. Per-row yes/no column added in the 01/20/25 edition. Each row is a Yes[0]/No[1] pair; engine writes the answer to the cell owned by the matching repeatable instance.',
    valueType: 'boolean',
    required: false,
    visibility: requiredWhenReceivedLongTermInstitutionalization,
    repeatableGroupId: 'eligibility.long_term_institutionalization',
    repeatableBound: ELIGIBILITY_LONG_TERM_INSTITUTIONALIZATION_BOUND,
    pdfConsumers: [
      {
        ...i485Consumer,
        required: false,
        widgetNames: [
          'Pt9Line66_Row1_YesNo[0]',
          'Pt9Line66_Row1_YesNo[1]',
          'Pt9Line66_Row2_YesNo[0]',
          'Pt9Line66_Row2_YesNo[1]',
          'Pt9Line66_Row3_YesNo[0]',
          'Pt9Line66_Row3_YesNo[1]',
          'Pt9Line66_Row4_YesNo[0]',
          'Pt9Line66_Row4_YesNo[1]',
        ],
      },
    ],
  },
] as const satisfies readonly FieldDefinition[];

export const eligibilityImmigrationViolationFields = [
  {
    id: eligibilityFailedRemovalProceedingFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER failed or refused to attend a removal proceeding filed against them on or after April 1, 1997',
    helpText: 'I-485 Pt8Line69a_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt8Line55_YesNo[0]',
          false: 'Pt8Line55_YesNo[1]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.immigration_violations.failed_removal_had_reasonable_cause',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'If item 69.a is yes, applicant believes they had reasonable cause',
    helpText: 'I-485 Pt8Line69b_YesNo.',
    valueType: 'boolean',
    required: requiredWhenFailedRemovalProceeding,
    visibility: requiredWhenFailedRemovalProceeding,
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'eligibility.immigration_violations.failed_removal_reasonable_cause_explanation',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Explanation of reasonable cause',
    helpText:
      'I-485 Part 14 Additional Information. The PDF instructs applicants to attach a written statement; no AcroForm widget exists for this on Part 8 itself.',
    valueType: 'text',
    required: false,
    visibility: requiredWhenFailedRemovalProceeding,
    validation: [{ type: 'max_length', value: 5000 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'eligibility.immigration_violations.submitted_fraudulent_documentation',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER submitted fraudulent or counterfeit documentation to a U.S. Government official to obtain an immigration benefit',
    helpText: 'I-485 Pt8Line70_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.immigration_violations.lied_or_misrepresented_on_immigration_application',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER lied about, concealed, or misrepresented information on a visa, entry, admission, or other immigration application',
    helpText: 'I-485 Pt8Line71_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.immigration_violations.falsely_claimed_us_citizenship',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER falsely claimed to be a U.S. citizen, in writing or any other way',
    helpText: 'I-485 Pt8Line72_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.immigration_violations.was_stowaway',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER been a stowaway on a vessel or aircraft arriving in the U.S.',
    helpText: 'I-485 Pt8Line73_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.immigration_violations.smuggled_alien_into_us',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER knowingly encouraged, induced, assisted, abetted, or aided a foreign national to enter the U.S. illegally (alien smuggling)',
    helpText: 'I-485 Pt8Line74_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.immigration_violations.under_274c_civil_penalty_order',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Is under a final order of civil penalty for violating section 274C for fraudulent documents',
    helpText: 'I-485 Pt8Line75_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.removal.has_been_excluded_or_deported',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER been excluded, deported, or removed from the U.S., or departed after being ordered excluded, deported, or removed',
    helpText: 'I-485 Pt8Line76_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.immigration_violations.entered_without_inspection',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER entered the U.S. without being inspected and admitted or paroled',
    helpText: 'I-485 Pt8Line77_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.unlawful_presence.over_180_days_under_one_year_then_departed',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Since April 1, 1997, was unlawfully present for more than 180 days but less than a year, then departed',
    helpText: 'I-485 Pt8Line78a_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.unlawful_presence.over_one_year_then_departed',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Since April 1, 1997, was unlawfully present for one year or more and then departed',
    helpText: 'I-485 Pt8Line78b_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.unlawful_presence.reentered_after_one_year_aggregate',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Since April 1, 1997, reentered or attempted to reenter after being unlawfully present for more than one year in the aggregate',
    helpText: 'I-485 Pt8Line79a_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.unlawful_presence.reentered_after_removal',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Since April 1, 1997, reentered or attempted to reenter after being deported, excluded, or removed',
    helpText: 'I-485 Pt9Line78b_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  // 2026-05-27 — new fields for i-485 01/20/25 Pt9 items missing from
  // the catalog. Each is a sworn Yes/No that has its own AcroForm
  // widget but no prior catalog binding, so packets shipped blank.
  //
  // (Item 13 — violated nonimmigrant status — is already covered by
  // the pre-existing `eligibility.prior_admission.violated_nonimmigrant_status`
  // field in eligibilityGeneralFields; the binding has been added in
  // aosV1WidgetMappings.json this slice.)
  {
    id: defineFieldId(
      'eligibility.unlawful_presence.has_been_unlawfully_present',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Since April 1, 1997, has been unlawfully present in the United States (present after the period of stay expired, or present without admission/parole)',
    helpText: 'I-485 Pt9Line76_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.unlawful_presence.was_due_to_trafficking',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'If unlawfully present, was a severe form of trafficking in persons at least one central reason',
    helpText:
      'I-485 Pt9Line77_YesNo. Conditional on Pt9Line76 = Yes — the form only requires this when the applicant answered Yes to Item 76.',
    valueType: 'boolean',
    required: false,
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

export const eligibilityMiscellaneousFields = [
  {
    id: defineFieldId('eligibility.miscellaneous.intends_polygamy'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Plans to practice polygamy in the U.S.',
    helpText: 'I-485 Pt8Line80_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.miscellaneous.accompanying_helpless_inadmissible_person',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Is accompanying another foreign national who requires guardianship and is inadmissible due to physical/mental disability or infancy (INA 232(c))',
    helpText: 'I-485 Pt8Line81_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.miscellaneous.assisted_in_child_abduction'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER assisted in detaining, retaining, or withholding custody of a U.S. citizen child outside the U.S.',
    helpText: 'I-485 Pt8Line82_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId('eligibility.miscellaneous.voted_unlawfully'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER voted in violation of any Federal, state, or local law in the U.S.',
    helpText: 'I-485 Pt8Line83_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.miscellaneous.renounced_us_citizenship_to_avoid_taxation',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER renounced U.S. citizenship to avoid being taxed by the U.S.',
    helpText: 'I-485 Pt8Line84_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.miscellaneous.applied_for_armed_forces_exemption_as_foreign_national',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER applied for exemption or discharge from training or service in the U.S. Armed Forces or U.S. National Security Training Corps as a foreign national',
    helpText: 'I-485 Pt8Line85a_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.miscellaneous.relieved_or_discharged_armed_forces_as_foreign_national',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER been relieved or discharged from such training or service on the ground of being a foreign national',
    helpText: 'I-485 Pt8Line85b_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.miscellaneous.convicted_of_armed_forces_desertion',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label: 'Has EVER been convicted of desertion from U.S. armed forces',
    helpText: 'I-485 Pt8Line85c_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: eligibilityAvoidedArmedForcesFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Has EVER left or remained outside the U.S. to avoid or evade training or service in U.S. armed forces during war or a national emergency',
    helpText: 'I-485 Pt8Line86a_YesNo.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: defineFieldId(
      'eligibility.miscellaneous.nationality_or_status_when_left_us',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility',
    partyRole: 'beneficiary',
    label:
      'Nationality or immigration status immediately before leaving the U.S.',
    helpText:
      'I-485 Pt8Line86b_Nationality. Free-text — for example, U.S. citizen or national, lawful permanent resident, nonimmigrant, parolee, or present without admission or parole.',
    valueType: 'text',
    required: requiredWhenAvoidedArmedForces,
    visibility: requiredWhenAvoidedArmedForces,
    validation: [{ type: 'max_length', value: 200 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

// ===========================================================================
// Document Requirements
//
// Supporting documents that get attached to the AOS packet but are NOT
// AcroForm widgets. Modeled as FieldDefinitions whose `valueType` is
// `document_upload`, with `pdfConsumers: []` and a `documentSpec` sidecar.
// Visibility/requirement rules reuse Slice 1–11 IDs where applicable.
// ===========================================================================

const beneficiaryIsCurrentlyMarriedForDocsRule: RuleExpression = {
  op: 'one_of',
  fieldId: defineFieldId('beneficiary.marital.status'),
  values: ['married', 'legally_separated'],
};

const beneficiaryHasPriorMarriageRule: RuleExpression = {
  op: 'exists',
  fieldId: defineFieldId('beneficiary.prior_marriages.date_ended'),
};

const sponsorHasPriorMarriageRule: RuleExpression = {
  op: 'exists',
  fieldId: defineFieldId('sponsor.prior_marriages.date_ended'),
};

const beneficiaryHasChildrenRule: RuleExpression = {
  op: 'exists',
  fieldId: defineFieldId('beneficiary.children.name.legal.last'),
};

const sponsorIsUsCitizenRule: RuleExpression = {
  op: 'equals',
  fieldId: defineFieldId('sponsor.citizenship.status'),
  value: 'us_citizen',
};

const sponsorIsLprRule: RuleExpression = {
  op: 'equals',
  fieldId: defineFieldId('sponsor.citizenship.status'),
  value: 'lawful_permanent_resident',
};

const sponsorIsSelfEmployedForDocsRule: RuleExpression = {
  op: 'equals',
  fieldId: defineFieldId('sponsor.current_employment.status'),
  value: 'self_employed',
};

const sponsorIsEmployedForDocsRule: RuleExpression = {
  op: 'equals',
  fieldId: defineFieldId('sponsor.current_employment.status'),
  value: 'employed',
};

const beneficiaryWasArrestedRule: RuleExpression = {
  op: 'equals',
  fieldId: defineFieldId('eligibility.criminal.has_been_arrested_or_detained'),
  value: true,
};

const beneficiaryWasConvictedRule: RuleExpression = {
  op: 'equals',
  fieldId: defineFieldId('eligibility.criminal.has_pled_guilty_or_convicted'),
  value: true,
};

export const beneficiaryIdentityDocumentFields = [
  {
    id: defineFieldId('document.beneficiary_identity.birth_certificate'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Beneficiary birth certificate',
    helpText:
      'Issued by the civil authority of the beneficiary\'s country of birth. Submit a clear copy of the long-form (full) birth certificate.',
    valueType: 'document_upload',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Birth certificate',
      description:
        'Long-form birth certificate showing both parents\' names. Required to establish beneficiary identity and (for marriage-based AOS) the family relationship to qualifying parents when relevant.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference: 'I-485 instructions, Required Initial Evidence — Identity',
      expectedCount: 1,
    },
  },
  {
    id: defineFieldId(
      'document.beneficiary_identity.birth_certificate_translation',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Certified English translation of birth certificate',
    helpText:
      'Required when the birth certificate is not in English. Translator must include a certificate of competency.',
    valueType: 'document_upload',
    required: false,
    validation: [],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Birth certificate — English translation',
      description:
        'Full English translation of the beneficiary\'s birth certificate, with a signed translator certification of competency. Required when the original document is not in English.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference: '8 CFR 103.2(b)(3) — translations',
      expectedCount: 1,
    },
  },
  {
    id: defineFieldId('document.beneficiary_identity.passport_bio_page'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Beneficiary passport biographic page',
    helpText:
      'Photocopy of the page of the beneficiary\'s passport showing name, date of birth, photo, passport number, and expiration date.',
    valueType: 'document_upload',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Passport biographic page',
      description:
        'Photocopy of the passport page showing the beneficiary\'s photo, name, date of birth, passport number, and expiration date.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-485 instructions, Required Initial Evidence — Photos and Documentation of Immigrant Category',
      expectedCount: 1,
    },
  },
  {
    id: defineFieldId('document.beneficiary_identity.passport_visa_pages'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Passport pages with U.S. visa and admission stamps',
    helpText:
      'Photocopies of every page bearing a U.S. visa, admission stamp, or extension annotation.',
    valueType: 'document_upload',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Passport pages with U.S. visa and admission stamps',
      description:
        'Copies of all passport pages showing U.S. nonimmigrant visas, admission stamps, parole stamps, and any annotations evidencing lawful entry and current status.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference: 'I-485 instructions, Required Initial Evidence',
      expectedCount: { min: 1 },
    },
  },
  {
    id: defineFieldId('document.beneficiary_identity.passport_photos'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Two passport-style photos',
    helpText:
      'Two identical color passport-style photos taken within 30 days of filing, on white background, 2x2 inches.',
    valueType: 'document_upload',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Passport-style photos (2)',
      description:
        'Two identical color passport-style photographs of the beneficiary taken within 30 days of filing, 2x2 inches, white background, full face, no head covering except for religious reasons.',
      acceptedFormats: ['image'],
      uscisReference:
        'I-485 instructions, Required Initial Evidence — Two passport-style photographs',
      expectedCount: 2,
    },
  },
  {
    id: defineFieldId('document.beneficiary_identity.government_photo_id'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Beneficiary government-issued photo identification',
    helpText:
      'Driver license, national ID card, or other government-issued photo ID. Used to corroborate identity at the AOS interview.',
    valueType: 'document_upload',
    required: false,
    validation: [],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Government photo ID',
      description:
        'Photocopy of a government-issued photo identification document (driver license, state ID, or national ID).',
      acceptedFormats: ['pdf', 'image'],
      expectedCount: 1,
    },
  },
] as const satisfies readonly FieldDefinition[];

export const beneficiaryStatusDocumentFields = [
  {
    id: defineFieldId('document.beneficiary_status.i94_record'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'I-94 arrival/departure record',
    helpText:
      'Printout from the CBP I-94 website (i94.cbp.dhs.gov) for the most recent admission, or a paper I-94 when issued.',
    valueType: 'document_upload',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'I-94 arrival/departure record',
      description:
        'Most recent Form I-94 (Arrival/Departure Record). Print from the CBP I-94 website if it was issued electronically; submit the paper card if it was issued at a port of entry before automation.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-485 instructions, Required Initial Evidence — Inspection and Admission or Inspection and Parole',
      expectedCount: 1,
    },
  },
  {
    id: defineFieldId('document.beneficiary_status.current_ead_or_ap'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Current EAD or Advance Parole document (if any)',
    helpText:
      'Optional. Provide a copy of any unexpired Employment Authorization Document or Advance Parole travel document the beneficiary currently holds.',
    valueType: 'document_upload',
    required: false,
    validation: [],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Current EAD or Advance Parole',
      description:
        'Photocopy of any currently held Employment Authorization Document (Form I-766) or Advance Parole document, front and back.',
      acceptedFormats: ['pdf', 'image'],
      expectedCount: { min: 1 },
    },
  },
  {
    id: defineFieldId('document.beneficiary_status.medical_exam_i693'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Sealed I-693 medical examination',
    helpText:
      'Form I-693 completed and sealed by a USCIS-designated civil surgeon. Phase 0 only tracks the upload requirement; the I-693 contents themselves are out of scope for the v1 catalog.',
    valueType: 'document_upload',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Sealed Form I-693 medical examination',
      description:
        'Form I-693, Report of Immigration Medical Examination and Vaccination Record, completed and signed by a USCIS-designated civil surgeon and submitted in the surgeon\'s sealed envelope.',
      acceptedFormats: ['pdf'],
      uscisReference:
        'I-485 instructions, Required Initial Evidence — Report of Medical Examination',
      expectedCount: 1,
    },
  },
] as const satisfies readonly FieldDefinition[];

export const marriageRelationshipDocumentFields = [
  {
    id: defineFieldId('document.marriage.certificate'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Marriage certificate',
    helpText:
      'Civil marriage certificate for the current marriage between the beneficiary and the U.S. citizen or LPR petitioner.',
    valueType: 'document_upload',
    required: beneficiaryIsCurrentlyMarriedForDocsRule,
    visibility: beneficiaryIsCurrentlyMarriedForDocsRule,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Marriage certificate',
      description:
        'Civil-authority-issued marriage certificate for the qualifying marriage between the petitioner and beneficiary. The religious ceremony certificate alone is not sufficient.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-130 instructions, Required Evidence — Spouse petitions',
      expectedCount: 1,
    },
  },
  {
    id: defineFieldId('document.marriage.beneficiary_prior_marriage_termination'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Beneficiary prior-marriage termination evidence',
    helpText:
      'Final divorce decree, annulment, or death certificate proving every prior marriage of the beneficiary was lawfully terminated before the current marriage.',
    valueType: 'document_upload',
    required: beneficiaryHasPriorMarriageRule,
    visibility: beneficiaryHasPriorMarriageRule,
    repeatableGroupId: 'document.marriage.beneficiary_prior_terminations',
    repeatableBound: DOCUMENT_GROUP_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Beneficiary prior-marriage termination',
      description:
        'Divorce decree, annulment order, or death certificate ending each prior marriage of the beneficiary. One instance per prior marriage.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-130 instructions — proof every prior marriage was terminated',
      expectedCount: { min: 1 },
    },
  },
  {
    id: defineFieldId('document.marriage.sponsor_prior_marriage_termination'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'sponsor',
    label: 'Petitioner prior-marriage termination evidence',
    helpText:
      'Final divorce decree, annulment, or death certificate proving every prior marriage of the petitioner was lawfully terminated.',
    valueType: 'document_upload',
    required: sponsorHasPriorMarriageRule,
    visibility: sponsorHasPriorMarriageRule,
    repeatableGroupId: 'document.marriage.sponsor_prior_terminations',
    repeatableBound: DOCUMENT_GROUP_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Petitioner prior-marriage termination',
      description:
        'Divorce decree, annulment order, or death certificate ending each prior marriage of the petitioner. One instance per prior marriage.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-130 instructions — proof every prior marriage was terminated',
      expectedCount: { min: 1 },
    },
  },
  {
    id: defineFieldId('document.marriage.bona_fide_joint_finances'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Joint financial evidence (bona fide marriage)',
    helpText:
      'Joint bank statements, joint lease or mortgage, utilities in both names, joint tax returns, joint insurance — anything that demonstrates a shared financial life.',
    valueType: 'document_upload',
    required: beneficiaryIsCurrentlyMarriedForDocsRule,
    visibility: beneficiaryIsCurrentlyMarriedForDocsRule,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Joint financial evidence',
      description:
        'Documents showing shared financial life: joint bank statements, joint lease or mortgage, joint utility bills, joint tax filings, beneficiary listed on petitioner\'s insurance, etc.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference: 'I-130 instructions — bona fide marriage evidence',
      expectedCount: { min: 2 },
    },
  },
  {
    id: defineFieldId('document.marriage.bona_fide_photos_together'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Photos of the couple together',
    helpText:
      'A representative selection of photographs of the petitioner and beneficiary together over the course of the relationship.',
    valueType: 'document_upload',
    required: beneficiaryIsCurrentlyMarriedForDocsRule,
    visibility: beneficiaryIsCurrentlyMarriedForDocsRule,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Photos of the couple together',
      description:
        'Photographs spanning the relationship — wedding, family events, travel together, day-to-day life. Helps establish the bona fide nature of the marriage.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference: 'I-130 instructions — bona fide marriage evidence',
      expectedCount: { min: 5 },
    },
  },
  {
    id: defineFieldId('document.marriage.bona_fide_third_party_affidavits'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Third-party affidavits (optional)',
    helpText:
      'Sworn statements from friends or family attesting to the bona fide marriage. Optional but commonly submitted, especially when joint financial evidence is thin.',
    valueType: 'document_upload',
    required: false,
    visibility: beneficiaryIsCurrentlyMarriedForDocsRule,
    validation: [],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Third-party affidavits',
      description:
        'Notarized written statements from friends, family, or community members attesting to personal knowledge of the bona fide marriage.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference: 'I-130 instructions — bona fide marriage evidence',
      expectedCount: { min: 1 },
    },
  },
] as const satisfies readonly FieldDefinition[];

export const sponsorIdentityDocumentFields = [
  {
    id: defineFieldId(
      'document.sponsor_identity.us_citizen_evidence_birth_certificate',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'sponsor',
    label: 'Petitioner U.S. birth certificate (citizen evidence)',
    helpText:
      'One acceptable form of U.S. citizenship evidence. Use a U.S. birth certificate when the petitioner was born in the United States. Pair with the alternative slots below — the applicant supplies whichever applies.',
    valueType: 'document_upload',
    required: false,
    visibility: sponsorIsUsCitizenRule,
    oneOfGroupId: 'sponsor.us_citizen_evidence',
    validation: [],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Petitioner U.S. birth certificate',
      description:
        'U.S. state-issued birth certificate. Submit one of: this, a Naturalization Certificate, a Certificate of Citizenship, a U.S. passport, or Form FS-240 (Consular Report of Birth Abroad).',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-130 instructions — Evidence of petitioner U.S. citizenship',
      expectedCount: 1,
    },
  },
  {
    id: defineFieldId(
      'document.sponsor_identity.us_citizen_evidence_naturalization',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'sponsor',
    label: 'Petitioner Certificate of Naturalization',
    helpText:
      'Alternative U.S. citizenship evidence. Submit when the petitioner naturalized (Form N-550 or N-570). For derived citizenship via parent, use the Certificate of Citizenship slot below instead.',
    valueType: 'document_upload',
    required: false,
    visibility: sponsorIsUsCitizenRule,
    oneOfGroupId: 'sponsor.us_citizen_evidence',
    validation: [],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Certificate of Naturalization (N-550 / N-570)',
      description: 'Form N-550 or N-570 Certificate of Naturalization.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-130 instructions — Evidence of petitioner U.S. citizenship',
      expectedCount: 1,
    },
  },
  {
    id: defineFieldId(
      'document.sponsor_identity.us_citizen_evidence_citizenship_certificate',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'sponsor',
    label: 'Petitioner Certificate of Citizenship',
    helpText:
      'Alternative U.S. citizenship evidence. Submit when the petitioner derived citizenship via a U.S.-citizen parent (Form N-560 or N-561).',
    valueType: 'document_upload',
    required: false,
    visibility: sponsorIsUsCitizenRule,
    oneOfGroupId: 'sponsor.us_citizen_evidence',
    validation: [],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Certificate of Citizenship (N-560 / N-561)',
      description:
        'Form N-560 or N-561 Certificate of Citizenship — issued to children of U.S. citizens who derived citizenship.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-130 instructions — Evidence of petitioner U.S. citizenship',
      expectedCount: 1,
    },
  },
  {
    id: defineFieldId(
      'document.sponsor_identity.us_citizen_evidence_passport',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'sponsor',
    label: 'Petitioner U.S. passport',
    helpText:
      'Alternative U.S. citizenship evidence. Photocopy of the petitioner\'s unexpired U.S. passport biographic page.',
    valueType: 'document_upload',
    required: false,
    visibility: sponsorIsUsCitizenRule,
    oneOfGroupId: 'sponsor.us_citizen_evidence',
    validation: [],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Petitioner U.S. passport',
      description:
        'Photocopy of the petitioner\'s currently valid U.S. passport biographic page.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-130 instructions — Evidence of petitioner U.S. citizenship',
      expectedCount: 1,
    },
  },
  {
    id: defineFieldId(
      'document.sponsor_identity.us_citizen_evidence_fs240',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'sponsor',
    label: 'Petitioner Consular Report of Birth Abroad (FS-240)',
    helpText:
      'Alternative U.S. citizenship evidence for petitioners born abroad to a U.S.-citizen parent. Form FS-240 is issued by U.S. consulates and is fully equivalent to a U.S. birth certificate.',
    valueType: 'document_upload',
    required: false,
    visibility: sponsorIsUsCitizenRule,
    oneOfGroupId: 'sponsor.us_citizen_evidence',
    validation: [],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Consular Report of Birth Abroad (FS-240)',
      description:
        'Form FS-240 issued by a U.S. embassy or consulate, documenting the U.S. citizenship of a person born abroad to a U.S.-citizen parent.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-130 instructions — Evidence of petitioner U.S. citizenship',
      expectedCount: 1,
    },
  },
  {
    id: defineFieldId('document.sponsor_identity.lpr_green_card'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'sponsor',
    label: 'Petitioner Permanent Resident Card (green card)',
    helpText:
      'Required when the petitioner is a Lawful Permanent Resident. Photocopy of the front and back of the unexpired green card (Form I-551).',
    valueType: 'document_upload',
    required: sponsorIsLprRule,
    visibility: sponsorIsLprRule,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Permanent Resident Card (front and back)',
      description:
        'Front and back photocopy of the petitioner\'s currently valid Form I-551 (green card).',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-130 instructions — Evidence of petitioner permanent residence',
      expectedCount: 1,
    },
  },
  {
    id: defineFieldId('document.sponsor_identity.government_photo_id'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'sponsor',
    label: 'Petitioner government-issued photo identification',
    helpText:
      'Driver license, state ID, or other government-issued photo ID for the petitioner.',
    valueType: 'document_upload',
    required: false,
    validation: [],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Petitioner government photo ID',
      description:
        'Photocopy of a government-issued photo identification document for the petitioner.',
      acceptedFormats: ['pdf', 'image'],
      expectedCount: 1,
    },
  },
] as const satisfies readonly FieldDefinition[];

export const sponsorFinancialDocumentFields = [
  {
    id: defineFieldId('document.sponsor_financial.tax_return_most_recent'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'sponsor',
    label: 'Most recent federal tax return or IRS tax transcript',
    helpText:
      'Either a complete copy of the most recent federal tax return (Form 1040 with all schedules and W-2s/1099s) OR an IRS tax return transcript. Transcript is preferred.',
    valueType: 'document_upload',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Most recent tax return or IRS transcript',
      description:
        'Complete federal income tax return for the most recent tax year, OR an IRS Tax Return Transcript (preferred). Required for the I-864 Affidavit of Support.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-864 instructions, Required Documentation — Federal income tax returns',
      expectedCount: 1,
    },
  },
  {
    id: defineFieldId('document.sponsor_financial.tax_return_prior_years'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'sponsor',
    label: 'Prior two years of tax returns or transcripts (optional)',
    helpText:
      'Optional. Submitting all three most recent years strengthens the I-864, particularly when the most recent year is borderline.',
    valueType: 'document_upload',
    required: false,
    validation: [],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Prior two years tax returns/transcripts',
      description:
        'Federal tax returns or IRS transcripts for the two years preceding the most recent year. Optional but recommended.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-864 instructions, Required Documentation — Federal income tax returns',
      expectedCount: { min: 1, max: 2 },
    },
  },
  {
    id: defineFieldId('document.sponsor_financial.w2_or_1099_most_recent'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'sponsor',
    label: 'W-2 and/or 1099 forms for the most recent tax year',
    helpText:
      'Required when submitting a tax return rather than an IRS transcript. Provide every W-2 and 1099 the petitioner received.',
    valueType: 'document_upload',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'W-2 / 1099 forms (most recent year)',
      description:
        'Every W-2 and 1099 issued to the petitioner for the most recent tax year. Not required when an IRS Tax Return Transcript is submitted in lieu of a 1040.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-864 instructions, Required Documentation — Federal income tax returns',
      expectedCount: { min: 1 },
    },
  },
  {
    id: defineFieldId('document.sponsor_financial.recent_pay_stubs'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'sponsor',
    label: 'Recent pay stubs (last six months)',
    helpText:
      'Recommended when the petitioner is currently employed and using current income to qualify under the I-864.',
    valueType: 'document_upload',
    required: sponsorIsEmployedForDocsRule,
    visibility: sponsorIsEmployedForDocsRule,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Recent pay stubs',
      description:
        'Pay stubs from the petitioner\'s current employer covering at least the last six months of pay.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-864 instructions, Required Documentation — Proof of current employment',
      expectedCount: { min: 1 },
    },
  },
  {
    id: defineFieldId('document.sponsor_financial.employer_letter'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'sponsor',
    label: 'Letter from current employer (optional)',
    helpText:
      'Letter on company letterhead stating the petitioner\'s position, length of employment, and current salary. Strengthens the I-864 when the most recent tax return understates current earnings.',
    valueType: 'document_upload',
    required: false,
    visibility: sponsorIsEmployedForDocsRule,
    validation: [],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Employer letter',
      description:
        'Letter on company letterhead stating the petitioner\'s position, employment dates, and current salary. Signed by an authorized HR or payroll representative.',
      acceptedFormats: ['pdf', 'image'],
      expectedCount: 1,
    },
  },
  {
    id: defineFieldId('document.sponsor_financial.self_employment_evidence'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'sponsor',
    label: 'Self-employment evidence',
    helpText:
      'Required when the petitioner reports self-employment status. Provide Schedule C, business 1099s, business returns, or comparable documentation of self-employed income.',
    valueType: 'document_upload',
    required: sponsorIsSelfEmployedForDocsRule,
    visibility: sponsorIsSelfEmployedForDocsRule,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Self-employment evidence',
      description:
        'Documentation supporting self-employed income — Schedule C / Schedule SE attached to the 1040, business 1099s, business income statements, or partnership/corporate returns.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-864 instructions, Required Documentation — Self-employed sponsors',
      expectedCount: { min: 1 },
    },
  },
  {
    id: defineFieldId('document.sponsor_financial.joint_sponsor_i864_packet'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'sponsor',
    label: 'Joint sponsor I-864 evidence packet (if joint sponsor used)',
    helpText:
      'When a joint sponsor is filing a separate I-864, the same financial-evidence packet (tax return/transcript, W-2/1099, pay stubs, employer letter, etc.) is required from the joint sponsor. Stored as a single repeatable group instance per joint sponsor; whether one is needed is decided by the form-engine in Phase 2 (TBD: explicit joint-sponsor flag).',
    valueType: 'document_upload',
    required: false,
    repeatableGroupId: 'document.sponsor_financial.joint_sponsor_packet',
    repeatableBound: DOCUMENT_GROUP_BOUND,
    validation: [],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Joint sponsor financial packet',
      description:
        'Full I-864 financial-evidence packet from the joint sponsor: their own tax return or IRS transcript, W-2/1099 forms, pay stubs, and proof of U.S. citizenship or LPR status. One packet per joint sponsor.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference: 'I-864 instructions — Joint sponsor requirements',
      expectedCount: { min: 1 },
    },
  },
] as const satisfies readonly FieldDefinition[];

export const childDocumentFields = [
  {
    id: defineFieldId('document.children.birth_certificate'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Child birth certificate (concurrent filer)',
    helpText:
      'Required for each beneficiary child applying concurrently for AOS. Long-form civil-authority birth certificate.',
    valueType: 'document_upload',
    required: beneficiaryHasChildrenRule,
    visibility: beneficiaryHasChildrenRule,
    repeatableGroupId: 'document.children.per_child',
    repeatableBound: BENEFICIARY_CHILDREN_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Child birth certificate',
      description:
        'Civil-authority-issued long-form birth certificate for a beneficiary child filing concurrently. One instance per concurrently filing child.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference: 'I-485 instructions — derivative children',
      expectedCount: 1,
    },
  },
  {
    id: defineFieldId('document.children.passport_bio_page'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Child passport biographic page (concurrent filer)',
    helpText:
      'Photocopy of the biographic page of the child\'s passport, when the child is filing concurrently.',
    valueType: 'document_upload',
    required: beneficiaryHasChildrenRule,
    visibility: beneficiaryHasChildrenRule,
    repeatableGroupId: 'document.children.per_child',
    repeatableBound: BENEFICIARY_CHILDREN_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Child passport biographic page',
      description:
        'Photocopy of the passport page showing the child\'s photo, name, date of birth, passport number, and expiration date. One instance per concurrently filing child.',
      acceptedFormats: ['pdf', 'image'],
      expectedCount: 1,
    },
  },
] as const satisfies readonly FieldDefinition[];

export const conditionalScreeningDocumentFields = [
  {
    id: defineFieldId('document.screening.criminal_record_disposition'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Court records / final dispositions for any arrest',
    helpText:
      'Required when the beneficiary answered yes to any Part 8 arrest/citation/charge/detention item. Provide certified court records and final dispositions for every incident, including expunged or sealed cases.',
    valueType: 'document_upload',
    required: beneficiaryWasArrestedRule,
    visibility: beneficiaryWasArrestedRule,
    repeatableGroupId: 'document.screening.criminal_dispositions',
    repeatableBound: DOCUMENT_GROUP_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Court records / final dispositions',
      description:
        'Certified court records and final disposition documents for every arrest, citation, charge, or detention disclosed on I-485 Part 8. Include expunged or sealed records — USCIS still requires disclosure.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-485 instructions — Required initial evidence (court records)',
      expectedCount: { min: 1 },
    },
  },
  {
    id: defineFieldId('document.screening.conviction_disposition'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Conviction final disposition (if applicable)',
    helpText:
      'Required when the beneficiary disclosed any guilty plea or conviction. Submit the final disposition for every conviction — even those expunged, sealed, pardoned, or rehabilitated.',
    valueType: 'document_upload',
    required: beneficiaryWasConvictedRule,
    visibility: beneficiaryWasConvictedRule,
    repeatableGroupId: 'document.screening.conviction_dispositions',
    repeatableBound: DOCUMENT_GROUP_BOUND,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Conviction final disposition',
      description:
        'Certified court documents showing the final disposition (sentence, probation terms, fines, expungement orders) for each conviction.',
      acceptedFormats: ['pdf', 'image'],
      uscisReference:
        'I-485 instructions — Required initial evidence (court records)',
      expectedCount: { min: 1 },
    },
  },
  {
    id: defineFieldId('document.screening.selective_service_registration'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Selective Service registration evidence (optional)',
    helpText:
      'Optional. Male beneficiaries between ages 18 and 26 who lived in the U.S. as something other than a lawful nonimmigrant should provide proof of Selective Service registration if available.',
    valueType: 'document_upload',
    required: false,
    validation: [],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Selective Service registration',
      description:
        'Selective Service registration acknowledgement letter or printout from the Selective Service System website confirming registration.',
      acceptedFormats: ['pdf', 'image'],
      expectedCount: 1,
    },
  },
  {
    id: defineFieldId('document.screening.military_records'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'documents',
    partyRole: 'beneficiary',
    label: 'Military service records (if served)',
    helpText:
      'Optional, but required when the beneficiary served in any country\'s armed forces. Provide DD-214, foreign military service records, or comparable documentation.',
    valueType: 'document_upload',
    required: false,
    validation: [],
    pdfConsumers: [],
    documentSpec: {
      displayName: 'Military service records',
      description:
        'Form DD-214 for U.S. military service, or equivalent service records for foreign military service. TBD: tighten gating once a "served in any military" eligibility field is added.',
      acceptedFormats: ['pdf', 'image'],
      expectedCount: { min: 1 },
    },
  },
] as const satisfies readonly FieldDefinition[];

// ---------------------------------------------------------------------------
// Statements, Signatures, Additional Info
// ---------------------------------------------------------------------------
//
// Interpreter and preparer sections were removed 2026-05-26. This app
// scopes to applicant self-filing or sponsor-assisted filing (no paid
// preparer or interpreter intake). The `used_interpreter` /
// `used_preparer` gate booleans below stay in the catalog (PDF
// widgets still exist) but visibility is hidden so the questions
// never render in intake. The cascade leaves them at default `false`,
// which writes nothing to the AcroForm — matching the prior
// "interpreter section blank" behaviour.

const usedInterpreterFieldId = defineFieldId(
  'application.signatures.used_interpreter',
);
const usedPreparerFieldId = defineFieldId(
  'application.signatures.used_preparer',
);

// Canonical "never visible" — `Array.some([])` is `false`, so the
// rule never fires. Used to hide interpreter / preparer fields whose
// PDF widgets remain in the catalog but whose intake questions were
// removed (2026-05-26).
const NEVER_VISIBLE: RuleExpression = { op: 'or', rules: [] };

const beneficiaryFormConsumers = [
  i485Consumer,
  i130aConsumer,
  i765Consumer,
  i131Consumer,
] as const;

const sponsorFormConsumers = [i130Consumer, i864Consumer] as const;

const allFormConsumers = [
  i485Consumer,
  i130Consumer,
  i130aConsumer,
  i765Consumer,
  i864Consumer,
  i131Consumer,
] as const;

const phoneValidation = [{ type: 'max_length', value: 25 }] as const;
const emailValidation = [
  { type: 'max_length', value: 254 },
  { type: 'pattern', pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$' },
] as const;

export const applicantSignatureFields = [
  {
    id: usedInterpreterFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'signatures',
    label: 'Did the applicant or sponsor use an interpreter?',
    helpText:
      'Single packet-level flag. Hidden in intake (2026-05-26) — this app does not collect interpreter info; the PDF widget stays blank.',
    valueType: 'boolean',
    required: false,
    visibility: NEVER_VISIBLE,
    validation: [{ type: 'required' }],
    pdfConsumers: allFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
  {
    id: usedPreparerFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'signatures',
    label: 'Was the application prepared by someone other than the applicant?',
    helpText:
      'Single packet-level flag. Hidden in intake (2026-05-26) — this app does not collect preparer info; the PDF widget stays blank.',
    valueType: 'boolean',
    required: false,
    visibility: NEVER_VISIBLE,
    validation: [{ type: 'required' }],
    pdfConsumers: allFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
  {
    id: defineFieldId('beneficiary.signature.statement_read_english'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'signatures',
    partyRole: 'beneficiary',
    label: 'Beneficiary read and understood every question in English',
    helpText:
      'Item 1.A. of the applicant statement. XOR with statement_read_via_interpreter; XOR enforced in Phase 2 form-engine.',
    valueType: 'boolean',
    required: false,
    validation: [{ type: 'required' }],
    pdfConsumers: beneficiaryFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
  {
    id: defineFieldId('beneficiary.signature.statement_read_via_interpreter'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'signatures',
    partyRole: 'beneficiary',
    label: 'Interpreter read every question to the beneficiary',
    helpText: 'Item 1.B. of the applicant statement.',
    valueType: 'boolean',
    required: false,
    visibility: NEVER_VISIBLE,
    validation: [{ type: 'required' }],
    pdfConsumers: beneficiaryFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
  {
    id: defineFieldId('beneficiary.signature.preferred_language'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'signatures',
    partyRole: 'beneficiary',
    label: 'Language interpreted for the beneficiary',
    helpText:
      'Item 1.B. language name. Required when statement_read_via_interpreter = true.',
    valueType: 'text',
    required: false,
    visibility: NEVER_VISIBLE,
    validation: [{ type: 'required' }, { type: 'max_length', value: 30 }],
    pdfConsumers: beneficiaryFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
  {
    id: defineFieldId('beneficiary.signature.signed'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'signatures',
    partyRole: 'beneficiary',
    label: 'Beneficiary signed the form',
    helpText:
      'Boolean presence flag for the wet signature. The actual signature is paper-only on the I-485; I-130A and I-765 expose AcroForm signature widgets but the data captured is still presence + date. Slice 39 (2026-05-09) — pdfConsumers removed: this boolean is engine-state only (drives the case-team review UI), not PDF widget content. The signature LINE widgets the inventory JSON listed (Pt4Line6a_Signature etc.) are text-shaped and are filled by hand at print time, not via AcroForm. Without this fix the engine emitted unsupported_widget_type ("Expected check-box widget, got PDFTextField") for every form fill.',
    valueType: 'boolean',
    required: false,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
  },
  {
    id: defineFieldId('beneficiary.signature.date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'signatures',
    partyRole: 'beneficiary',
    label: 'Beneficiary signature date',
    valueType: 'date',
    required: false,
    validation: [{ type: 'required' }],
    pdfConsumers: beneficiaryFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
] as const satisfies readonly FieldDefinition[];

export const sponsorSignatureFields = [
  {
    id: defineFieldId('sponsor.contact.daytime_phone'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'signatures',
    partyRole: 'sponsor',
    label: 'Sponsor / petitioner daytime telephone number',
    valueType: 'text',
    required: false,
    validation: [{ type: 'required' }, ...phoneValidation],
    pdfConsumers: sponsorFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
  {
    id: defineFieldId('sponsor.contact.mobile_phone'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'signatures',
    partyRole: 'sponsor',
    label: 'Sponsor / petitioner mobile telephone number',
    valueType: 'text',
    required: false,
    validation: [...phoneValidation],
    pdfConsumers: sponsorFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
  {
    id: defineFieldId('sponsor.contact.email'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'signatures',
    partyRole: 'sponsor',
    label: 'Sponsor / petitioner email address',
    valueType: 'text',
    required: false,
    validation: [...emailValidation],
    pdfConsumers: sponsorFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
  {
    id: defineFieldId('sponsor.signature.statement_read_english'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'signatures',
    partyRole: 'sponsor',
    label: 'Sponsor / petitioner read and understood every question in English',
    valueType: 'boolean',
    required: false,
    validation: [{ type: 'required' }],
    pdfConsumers: sponsorFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
  {
    id: defineFieldId('sponsor.signature.statement_read_via_interpreter'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'signatures',
    partyRole: 'sponsor',
    label: 'Interpreter read every question to the sponsor / petitioner',
    valueType: 'boolean',
    required: false,
    visibility: NEVER_VISIBLE,
    validation: [{ type: 'required' }],
    pdfConsumers: sponsorFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
  {
    id: defineFieldId('sponsor.signature.preferred_language'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'signatures',
    partyRole: 'sponsor',
    label: 'Language interpreted for the sponsor / petitioner',
    valueType: 'text',
    required: false,
    visibility: NEVER_VISIBLE,
    validation: [{ type: 'required' }, { type: 'max_length', value: 30 }],
    pdfConsumers: sponsorFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
  {
    id: defineFieldId('sponsor.signature.contract_acknowledged'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'signatures',
    partyRole: 'sponsor',
    label: 'Sponsor acknowledged the I-864 binding contract',
    helpText:
      'I-864 Part 8 is a binding affidavit-of-support contract. Acknowledgment is implicit in signing; the catalog stores it as a separate boolean for audit. No I-130 consumer because the petitioner statement is not a financial contract.',
    valueType: 'boolean',
    required: false,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i864Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.signature.signed'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'signatures',
    partyRole: 'sponsor',
    label: 'Sponsor / petitioner signed the form',
    helpText:
      'Boolean presence flag for the sponsor wet signature. Slice 39 (2026-05-09) — pdfConsumers removed: this is engine-state-only (drives the case-team review UI). The signature LINE widget on i-864 (P8_Line9a_ApplicantSignature) is text-shaped and gets filled by hand at print time, not via AcroForm. Without this fix the engine emitted unsupported_widget_type for every i-864 fill.',
    valueType: 'boolean',
    required: false,
    validation: [{ type: 'required' }],
    pdfConsumers: [],
  },
  {
    id: defineFieldId('sponsor.signature.date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'signatures',
    partyRole: 'sponsor',
    label: 'Sponsor / petitioner signature date',
    valueType: 'date',
    required: false,
    validation: [{ type: 'required' }],
    pdfConsumers: sponsorFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
] as const satisfies readonly FieldDefinition[];
// interpreter + preparer field blocks deleted 2026-05-26 — sections removed from intake.

export const additionalInformationFields = [
  {
    id: defineFieldId('application.additional_info.page_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'additional_info',
    label: 'Overflow entry — page reference',
    helpText:
      'Page number on the original USCIS form that the overflow continues. One entry per overflow item across the packet.',
    valueType: 'text',
    required: false,
    repeatableGroupId: 'application.additional_info',
    repeatableBound: ADDITIONAL_INFO_BOUND,
    validation: [{ type: 'max_length', value: 4 }],
    pdfConsumers: allFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
  {
    id: defineFieldId('application.additional_info.part_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'additional_info',
    label: 'Overflow entry — part reference',
    valueType: 'text',
    required: false,
    repeatableGroupId: 'application.additional_info',
    repeatableBound: ADDITIONAL_INFO_BOUND,
    validation: [{ type: 'max_length', value: 4 }],
    pdfConsumers: allFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
  {
    id: defineFieldId('application.additional_info.item_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'additional_info',
    label: 'Overflow entry — item reference',
    valueType: 'text',
    required: false,
    repeatableGroupId: 'application.additional_info',
    repeatableBound: ADDITIONAL_INFO_BOUND,
    validation: [{ type: 'max_length', value: 8 }],
    pdfConsumers: allFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
  {
    id: defineFieldId('application.additional_info.content'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'additional_info',
    label: 'Overflow entry — free-text content',
    helpText:
      'Long-form continuation text. Phase 2 form-engine paginates entries across the per-form overflow slots (5 on most forms, 4 on I-864).',
    valueType: 'text',
    required: false,
    repeatableGroupId: 'application.additional_info',
    repeatableBound: ADDITIONAL_INFO_BOUND,
    validation: [{ type: 'max_length', value: 4000 }],
    pdfConsumers: allFormConsumers.map(consumer => ({
      ...consumer,
      required: false,
    })),
  },
] as const satisfies readonly FieldDefinition[];

const eligibilityCategorySelectionFieldId = defineFieldId(
  'eligibility.category.selection',
);

const requiredWhenCategoryIsOther: RuleExpression = {
  op: 'equals',
  fieldId: eligibilityCategorySelectionFieldId,
  value: 'other',
};

// Categories whose Item 1 selection identifies a derivative
// beneficiary filing alongside a principal applicant. The form-engine
// gates Items 5–9 (principal applicant identity + Item 8/9 receipt
// and priority date) on this set.
const derivativeCategorySelections: readonly string[] = [
  'family-preference',
  'fiance',
  'eb-entrepreneur',
  'eb-worker',
  't-nonimmigrant',
  'u-nonimmigrant',
];

const requiredWhenCategoryIsDerivative: RuleExpression = {
  op: 'one_of',
  fieldId: eligibilityCategorySelectionFieldId,
  values: [...derivativeCategorySelections],
};

export const eligibilityCategoryFields = [
  {
    id: defineFieldId('eligibility.affidavit_of_support.exemption_basis'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility_category',
    partyRole: 'beneficiary',
    label: 'Do you need to submit an Affidavit of Support (Form I-864)?',
    helpText:
      'Marriage-based applicants almost always pick "Yes, I need to submit Form I-864" — the sponsor signs the I-864 as part of your packet. The exemption options apply only in narrow cases: 40 quarters of SSA work credit, U.S. citizen child under 18, VAWA self-petitioners, etc.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          // 1.a — 40 qualifying quarters of SSA work credit.
          ssa_40_quarters: 'Pt3Line1_CB[0]',
          // 1.b — under 18, child of USC, auto-citizenship.
          minor_us_citizen_parent: 'Pt3Line1_CB[1]',
          // 1.c — widow(er) of USC under I-360 category.
          widower_i360: 'Pt3Line1_CB[2]',
          // 1.d — VAWA self-petitioner.
          vawa_self_petitioner: 'Pt3Line1_CB[3]',
          // 1.e — no exemption applies AND not required to submit I-864.
          not_required: 'Pt3Line1_CB[4]',
          // 1.f — no exemption applies AND IS required to submit I-864
          // (the typical marriage-based / family-based filer).
          not_exempt_required: 'Pt3Line1_CB[5]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.removal_proceedings.eoir_filing'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility_category',
    partyRole: 'beneficiary',
    label:
      'Are you filing with the Executive Office for Immigration Review (EOIR) while in removal, exclusion, rescission, or deportation proceedings?',
    helpText:
      'I-485 01/20/25 Pt2Line1_YN (Item 1). Default No for the vast majority of applicants — only check Yes when the applicant is actively in removal proceedings before an Immigration Judge.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          true: 'Pt2Line1_YN[0]',
          false: 'Pt2Line1_YN[1]',
        },
      },
    ],
  },
  {
    id: eligibilityCategorySelectionFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility_category',
    partyRole: 'beneficiary',
    label: 'How do you qualify for a green card?',
    helpText:
      'Marriage to a U.S. citizen — pick "immediate relative" (spouse of a U.S. citizen). Marriage to a permanent resident — pick "family preference" (F2A spouse of LPR). Other paths apply only when a different qualifying relationship or category drives your case.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          'family-immediate': 'Pt2Line3a_CB[0]',
          'family-preference': 'Pt2Line3a_CB[1]',
          fiance: 'Pt2Line3a_CB[2]',
          widower: 'Pt2Line3a_CB[3]',
          vawa: 'Pt2Line3a_CB[4]',
          'eb-entrepreneur': 'Pt2Line3a_CB[5]',
          'eb-worker': 'Pt2Line3a_CB[6]',
          'si-religious-worker': 'Pt2Line3a_CB[7]',
          'si-afghan-iraqi': 'Pt2Line3a_CB[8]',
          sij: 'Pt2Line3a_CB[9]',
          'si-g4-nato6': 'Pt2Line3a_CB[10]',
          'si-broadcaster': 'Pt2Line3a_CB[11]',
          refugee: 'Pt2Line3a_CB[12]',
          asylee: 'Pt2Line3a_CB[13]',
          't-nonimmigrant': 'Pt2Line3a_CB[14]',
          // Pt2Line3a_CB only has [0..14] in i-485 01/20/25 — extra
          // immigrant categories (15..26) don't fit into this widget
          // group on the new edition. Until USCIS adds widgets or the
          // catalog uses a different widget block for the remainder,
          // these projections are dropped. The intake UI can still
          // accept the value; it just won't write to the PDF.
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.category.other_explanation'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility_category',
    partyRole: 'beneficiary',
    label: 'Other eligibility — free-text explanation',
    helpText:
      'I-485 Pt2Line1g_OtherEligibility. Required only when the selected category is "other" (USCIS code 1gD).',
    valueType: 'text',
    required: requiredWhenCategoryIsOther,
    visibility: requiredWhenCategoryIsOther,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 250 },
    ],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('eligibility.category.section_245i'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility_category',
    partyRole: 'beneficiary',
    label: 'Are you eligible for 245(i) adjustment? (rare — most applicants: No)',
    helpText:
      'I-485 01/20/25 Pt2Line1_YN Yes/No. Three-value select (yes, no, not_applicable). Store "not_applicable" when 245(i) is out of scope.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        required: true,
        widgetValueProjection: {
          // i-485 01/20/25 Pt2 Item 4: 245(i) Yes/No widgets are
          // Pt2Line4_CB[0] (x=540, RIGHT) = No and
          // Pt2Line4_CB[1] (x=498, LEFT) = Yes (geometric layout
          // confirmed against the rendered form — USCIS forms put
          // Yes on the left and No on the right, but the [0]/[1]
          // indices don't follow a consistent convention).
          //
          // Earlier this catalog field mapped to Pt2Line1_YN[0/1]
          // (left over from a guess that the 01/20/25 edition moved
          // 245(i) into Item 1). Pt2Line1_YN actually drives the
          // EOIR question at Item 1; writing the 245(i) answer there
          // caused Brandan's Item 1 to show "No" by accident while
          // Item 4 stayed blank.
          //
          // not_applicable → write neither widget.
          yes: 'Pt2Line4_CB[1]',
          no: 'Pt2Line4_CB[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('eligibility.category.applying_as'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility_category',
    partyRole: 'beneficiary',
    label: 'Applying as principal applicant or derivative?',
    helpText:
      "I-485 Pt2Line2_CB[0/1] — Principal Applicant or Derivative Applicant. Principal: the applicant whose immigration category drives the petition (e.g., spouse of U.S. citizen, employment-based principal, asylee). Derivative: a spouse or unmarried child of the principal filing alongside. Default principal for the immediate-relative / immediate-spouse paths; pick derivative only when the applicant is filing under someone else's qualifying petition.",
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          principal: 'Pt2Line2_CB[0]',
          derivative: 'Pt2Line2_CB[1]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'eligibility.category.underlying_petition_receipt_number',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility_category',
    partyRole: 'beneficiary',
    label: 'Underlying petition receipt number',
    helpText:
      'I-485 Pt2Line3_Receipt (principal applicant) or Pt2Line8_ReceiptNumber (derivative applicant). One semantic field; the form-engine routes to Item 3 vs Item 8 based on whether the applicant is the principal of the underlying petition.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 13 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('eligibility.category.priority_date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility_category',
    partyRole: 'beneficiary',
    label: 'Underlying petition priority date',
    helpText:
      'I-485 Pt2Line4_Date (principal applicant) or Pt2Line9_Date (derivative applicant). One semantic field; the form-engine routes to Item 4 vs Item 9 based on principal-vs-derivative role.',
    valueType: 'date',
    required: false,
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'eligibility.category.principal_applicant.last_name',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility_category',
    partyRole: 'beneficiary',
    label: 'Principal applicant family name (last name)',
    helpText:
      'I-485 Pt2Line5a_FamilyName. Required when the applicant is filing as a derivative beneficiary of a principal\'s petition.',
    valueType: 'text',
    required: requiredWhenCategoryIsDerivative,
    visibility: requiredWhenCategoryIsDerivative,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 80 },
    ],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'eligibility.category.principal_applicant.first_name',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility_category',
    partyRole: 'beneficiary',
    label: 'Principal applicant given name (first name)',
    helpText: 'I-485 Pt2Line5b_GivenName.',
    valueType: 'text',
    required: requiredWhenCategoryIsDerivative,
    visibility: requiredWhenCategoryIsDerivative,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 80 },
    ],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'eligibility.category.principal_applicant.middle_name',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility_category',
    partyRole: 'beneficiary',
    label: 'Principal applicant middle name',
    helpText: 'I-485 Pt2Line5c_MiddleName.',
    valueType: 'text',
    required: false,
    visibility: requiredWhenCategoryIsDerivative,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'eligibility.category.principal_applicant.date_of_birth',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility_category',
    partyRole: 'beneficiary',
    label: 'Principal applicant date of birth',
    helpText: 'I-485 Pt2Line7_Date.',
    valueType: 'date',
    required: requiredWhenCategoryIsDerivative,
    visibility: requiredWhenCategoryIsDerivative,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'eligibility.category.principal_applicant.a_number',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'eligibility_category',
    partyRole: 'beneficiary',
    label: 'Principal applicant A-Number',
    helpText:
      'I-485 01/20/25 Pt2Line2_AlienNumber. The new edition reconnected this widget (the 04/01/24 edition had it as XFA-only).',
    valueType: 'text',
    required: false,
    visibility: requiredWhenCategoryIsDerivative,
    validation: [
      { type: 'max_length', value: 9 },
      { type: 'pattern', pattern: '^\\d{7,9}$' },
    ],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

const accommodationRequestedFieldId = defineFieldId(
  'application.accommodation.requested',
);

const accommodationDeafFieldId = defineFieldId(
  'application.accommodation.deaf_or_hard_of_hearing',
);

const accommodationBlindFieldId = defineFieldId(
  'application.accommodation.blind_or_low_vision',
);

const accommodationOtherFieldId = defineFieldId(
  'application.accommodation.other_disability',
);

const requiredWhenAccommodationRequested: RuleExpression = {
  op: 'equals',
  fieldId: accommodationRequestedFieldId,
  value: true,
};

const requiredWhenAccommodationDeaf: RuleExpression = {
  op: 'equals',
  fieldId: accommodationDeafFieldId,
  value: true,
};

const requiredWhenAccommodationBlind: RuleExpression = {
  op: 'equals',
  fieldId: accommodationBlindFieldId,
  value: true,
};

const requiredWhenAccommodationOther: RuleExpression = {
  op: 'equals',
  fieldId: accommodationOtherFieldId,
  value: true,
};

export const accommodationRequestFields = [
  {
    id: accommodationRequestedFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'accommodation',
    partyRole: 'beneficiary',
    label:
      'Are you requesting an accommodation because of your disabilities and/or impairments?',
    helpText:
      'I-485 Pt9Line1_YesNo. Top-level gate for Part 9; widget index is inverted ([0] = No, [1] = Yes). When this is Yes, the applicant must check at least one of Items 2.A, 2.B, or 2.C and provide the paired explanation.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [i485Consumer],
  },
  {
    id: accommodationDeafFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'accommodation',
    partyRole: 'beneficiary',
    label:
      'I am deaf or hard of hearing and request the following accommodation',
    helpText:
      'I-485 Pt9Line2a_Deaf (item 2.A). Single-state checkbox (FieldStateOption A). Independent of Items 2.B and 2.C — an applicant may check more than one accommodation type. Visible only when Item 1 is Yes.',
    valueType: 'boolean',
    required: requiredWhenAccommodationRequested,
    visibility: requiredWhenAccommodationRequested,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId('application.accommodation.deaf_sign_language'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'accommodation',
    partyRole: 'beneficiary',
    label: 'Sign-language interpreter language (for example, ASL)',
    helpText:
      'I-485 Pt9Line2a_Accommodation (item 2.A text). USCIS labels the widget "Enter Language" because the canonical 2.A accommodation is a sign-language interpreter, but the field is free text and accepts any accommodation description.',
    valueType: 'text',
    required: requiredWhenAccommodationDeaf,
    visibility: requiredWhenAccommodationDeaf,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 250 },
    ],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: accommodationBlindFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'accommodation',
    partyRole: 'beneficiary',
    label:
      'I am blind or have low vision and request the following accommodation',
    helpText:
      'I-485 Pt9Line2b_Blind (item 2.B). Single-state checkbox (FieldStateOption B). Independent of Items 2.A and 2.C. Visible only when Item 1 is Yes.',
    valueType: 'boolean',
    required: requiredWhenAccommodationRequested,
    visibility: requiredWhenAccommodationRequested,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'application.accommodation.blind_accommodation_description',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'accommodation',
    partyRole: 'beneficiary',
    label: 'Blind or low-vision accommodation description',
    helpText:
      'I-485 Pt9Line2b_Accommodation (item 2.B text). Free-text description of the requested accommodation (for example, large-print copies, screen-reader compatibility).',
    valueType: 'text',
    required: requiredWhenAccommodationBlind,
    visibility: requiredWhenAccommodationBlind,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 250 },
    ],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: accommodationOtherFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'accommodation',
    partyRole: 'beneficiary',
    label: 'I have another type of disability and/or impairment',
    helpText:
      'I-485 Pt9Line2c_Other (item 2.C). Single-state checkbox (FieldStateOption C). Independent of Items 2.A and 2.B. Visible only when Item 1 is Yes.',
    valueType: 'boolean',
    required: requiredWhenAccommodationRequested,
    visibility: requiredWhenAccommodationRequested,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'application.accommodation.other_disability_description',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'accommodation',
    partyRole: 'beneficiary',
    label:
      'Description of disability or impairment and accommodation requested',
    helpText:
      'I-485 Pt9Line2c_Accommodation (item 2.C text). Single free-text widget that captures both the nature of the disability and the requested accommodation.',
    valueType: 'text',
    required: requiredWhenAccommodationOther,
    visibility: requiredWhenAccommodationOther,
    validation: [
      { type: 'required' },
      { type: 'max_length', value: 500 },
    ],
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

export const beneficiaryBiographicFields = [
  {
    id: defineFieldId('beneficiary.biographic.ethnicity'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'biographic',
    partyRole: 'beneficiary',
    label: 'Ethnicity',
    helpText:
      'I-485 Part 7 item 1 (Pt7Line1_Ethnicity[0..1]) and I-131 Part 3 item 1 (P3_Line1_Ethnicity[0..1]). USCIS "Select only one box". Two values: hispanic_or_latino, not_hispanic_or_latino. Same widget index order on I-485 and I-131 ([0]=Hispanic, [1]=Not Hispanic).',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          hispanic_or_latino: 'Pt7Line1_Ethnicity[0]',
          not_hispanic_or_latino: 'Pt7Line1_Ethnicity[1]',
        },
      },
      {
        ...i131Consumer,
        widgetValueProjection: {
          hispanic_or_latino: 'P3_Line1_Ethnicity[0]',
          not_hispanic_or_latino: 'P3_Line1_Ethnicity[1]',
        },
      },
    ],
  },
  {
    id: defineFieldId('beneficiary.biographic.race'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'biographic',
    partyRole: 'beneficiary',
    label: 'Race',
    helpText:
      'I-485 Part 7 item 2 (Pt7Line2_Race[0..4] — numeric indices) and I-131 Part 3 item 2 (P3_Line2_Race_American/Asian/Black/Hawaiian/White — short named widgets). USCIS "Select all applicable boxes" — independent checkboxes. Five values: american_indian_or_alaska_native, asian, black_or_african_american, native_hawaiian_or_other_pacific_islander, white.',
    valueType: 'multi_select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          asian: 'Pt7Line2_Race[0]',
          white: 'Pt7Line2_Race[1]',
          black_or_african_american: 'Pt7Line2_Race[2]',
          american_indian_or_alaska_native: 'Pt7Line2_Race[3]',
          native_hawaiian_or_other_pacific_islander: 'Pt7Line2_Race[4]',
        },
      },
      {
        ...i131Consumer,
        widgetValueProjection: {
          american_indian_or_alaska_native: 'P3_Line2_Race_American[0]',
          asian: 'P3_Line2_Race_Asian[0]',
          black_or_african_american: 'P3_Line2_Race_Black[0]',
          native_hawaiian_or_other_pacific_islander: 'P3_Line2_Race_Hawaiian[0]',
          white: 'P3_Line2_Race_White[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('beneficiary.biographic.height_feet'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'biographic',
    partyRole: 'beneficiary',
    label: 'Height (feet)',
    helpText:
      'I-485 Part 7 item 3 (Pt7Line3_HeightFeet) and I-131 Part 3 item 3 (P3_Line3_HeightFeet). Integer 1–9.',
    valueType: 'number',
    required: true,
    validation: [
      { type: 'required' },
      { type: 'min_value', value: 1 },
      { type: 'max_value', value: 9 },
    ],
    pdfConsumers: [i485Consumer, i131Consumer],
  },
  {
    id: defineFieldId('beneficiary.biographic.height_inches'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'biographic',
    partyRole: 'beneficiary',
    label: 'Height (inches)',
    helpText:
      'I-485 Part 7 item 3 (Pt7Line3_HeightInches) and I-131 Part 3 item 3 (P3_Line3_HeightInches). Integer 0–11.',
    valueType: 'number',
    required: true,
    validation: [
      { type: 'required' },
      { type: 'min_value', value: 0 },
      { type: 'max_value', value: 11 },
    ],
    pdfConsumers: [i485Consumer, i131Consumer],
  },
  {
    id: defineFieldId('beneficiary.biographic.weight_pounds'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'biographic',
    partyRole: 'beneficiary',
    label: 'Weight (pounds)',
    helpText:
      "I-485 Part 7 item 4 and I-131 Part 3 item 4. Single semantic number; PDF engine zero-pads to three digits and writes one digit per widget (I-485 Pt7Line4_Weight1/2/3 hundreds/tens/ones; I-131 P3_Line4_Pound1/2/3). Domain stores a single integer, not three digits. Bounds 1–999 cover the human range that fits the three-digit PDF widget — floor was 50 (adult-only) until 2026-05-23; children (stepchildren in marriage-AOS) can weigh well below that, so the floor's been dropped to 1.",
    valueType: 'number',
    required: true,
    validation: [
      { type: 'required' },
      { type: 'min_value', value: 1 },
      { type: 'max_value', value: 999 },
    ],
    pdfConsumers: [i485Consumer, i131Consumer],
  },
  {
    id: defineFieldId('beneficiary.biographic.eye_color'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'biographic',
    partyRole: 'beneficiary',
    label: 'Eye color',
    helpText:
      'I-485 Part 7 item 5 (Pt7Line5_Eyecolor[0..8] — USCIS spells the widget basename with a lowercase c). The I-131 Part 3 item 5 widget basename is P3_Line5_EyeColor[0..8] (camel-cased); index ordering on I-131 is not yet traced — projection deferred. Nine values: blue, black, brown, gray, green, hazel, maroon, pink, unknown_or_other.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          blue: 'Pt7Line5_Eyecolor[0]',
          black: 'Pt7Line5_Eyecolor[1]',
          brown: 'Pt7Line5_Eyecolor[2]',
          gray: 'Pt7Line5_Eyecolor[3]',
          green: 'Pt7Line5_Eyecolor[4]',
          hazel: 'Pt7Line5_Eyecolor[5]',
          maroon: 'Pt7Line5_Eyecolor[6]',
          pink: 'Pt7Line5_Eyecolor[7]',
          unknown_or_other: 'Pt7Line5_Eyecolor[8]',
        },
      },
      // I-131 Part 3 eye_color projection deferred — dump file documents
      // the widget array as `[0..8]` without per-index labels. Phase 1
      // PDF engine should not write an I-131 eye_color until the
      // projection lands.
      i131Consumer,
    ],
  },
  {
    id: defineFieldId('beneficiary.biographic.hair_color'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'biographic',
    partyRole: 'beneficiary',
    label: 'Hair color',
    helpText:
      'I-485 Part 7 item 6 (Pt7Line6_Haircolor[0..8] — lowercase c). The I-131 Part 3 item 6 widget basename is P3_Line6_HairColor[0..8] (camel-cased); index ordering on I-131 is not yet traced — projection deferred. Nine values: bald, black, blond, brown, gray, red, sandy, white, unknown_or_other. USCIS prints "Blond" (not "Blonde").',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i485Consumer,
        widgetValueProjection: {
          bald: 'Pt7Line6_Haircolor[0]',
          black: 'Pt7Line6_Haircolor[1]',
          blond: 'Pt7Line6_Haircolor[2]',
          brown: 'Pt7Line6_Haircolor[3]',
          gray: 'Pt7Line6_Haircolor[4]',
          red: 'Pt7Line6_Haircolor[5]',
          sandy: 'Pt7Line6_Haircolor[6]',
          white: 'Pt7Line6_Haircolor[7]',
          unknown_or_other: 'Pt7Line6_Haircolor[8]',
        },
      },
      // I-131 Part 3 hair_color projection deferred — see eye_color above.
      i131Consumer,
    ],
  },
] as const satisfies readonly FieldDefinition[];

export const sponsorBiographicFields = [
  {
    id: defineFieldId('sponsor.biographic.ethnicity'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Ethnicity',
    helpText:
      'I-130 Part 3 item 1 (Pt3Line1_Ethnicity[0..1]). USCIS "Select only one box". Two values: hispanic_or_latino, not_hispanic_or_latino. Note: I-130 widget index order is reversed from I-485 — [0] = Not Hispanic, [1] = Hispanic.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i130Consumer,
        widgetValueProjection: {
          // I-130 reverses the index order from I-485.
          not_hispanic_or_latino: 'Pt3Line1_Ethnicity[0]',
          hispanic_or_latino: 'Pt3Line1_Ethnicity[1]',
        },
      },
    ],
  },
  {
    id: defineFieldId('sponsor.biographic.race'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Race',
    helpText:
      'I-130 Part 3 item 2. USCIS "Select all applicable boxes" — independent checkboxes. Five values: american_indian_or_alaska_native, asian, black_or_african_american, native_hawaiian_or_other_pacific_islander, white. I-130 uses named widgets (Pt3Line2_Race_Black, Pt3Line2_Race_AmericanIndianAlaskaNative, Pt3Line2_Race_White, Pt3Line2_Race_Asian, Pt3Line2_Race_NativeHawaiianOtherPacificIslander) instead of I-485 numeric indices.',
    valueType: 'multi_select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i130Consumer,
        widgetValueProjection: {
          black_or_african_american: 'Pt3Line2_Race_Black[0]',
          american_indian_or_alaska_native:
            'Pt3Line2_Race_AmericanIndianAlaskaNative[0]',
          white: 'Pt3Line2_Race_White[0]',
          asian: 'Pt3Line2_Race_Asian[0]',
          native_hawaiian_or_other_pacific_islander:
            'Pt3Line2_Race_NativeHawaiianOtherPacificIslander[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('sponsor.biographic.height_feet'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Height (feet)',
    helpText:
      'I-130 Part 3 item 3 (Pt3Line3_HeightFeet). Integer 1–9.',
    valueType: 'number',
    required: true,
    validation: [
      { type: 'required' },
      { type: 'min_value', value: 1 },
      { type: 'max_value', value: 9 },
    ],
    pdfConsumers: [i130Consumer],
  },
  {
    id: defineFieldId('sponsor.biographic.height_inches'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Height (inches)',
    helpText:
      'I-130 Part 3 item 3 (Pt3Line3_HeightInches). Integer 0–11.',
    valueType: 'number',
    required: true,
    validation: [
      { type: 'required' },
      { type: 'min_value', value: 0 },
      { type: 'max_value', value: 11 },
    ],
    pdfConsumers: [i130Consumer],
  },
  {
    id: defineFieldId('sponsor.biographic.weight_pounds'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Weight (pounds)',
    helpText:
      "I-130 Part 3 item 4. Single semantic number; PDF engine zero-pads to three digits and writes one digit per widget (Pt3Line4_Pound1 hundreds, Pt3Line4_Pound2 tens, Pt3Line4_Pound3 ones). I-130 widget basename `Pound1/2/3` differs from I-485 `Weight1/2/3`. Domain stores a single integer, not three digits. Bounds 1–999 cover the human range; floor was 50 until 2026-05-23 (see beneficiary.biographic.weight_pounds for rationale).",
    valueType: 'number',
    required: true,
    validation: [
      { type: 'required' },
      { type: 'min_value', value: 1 },
      { type: 'max_value', value: 999 },
    ],
    pdfConsumers: [i130Consumer],
  },
  {
    id: defineFieldId('sponsor.biographic.eye_color'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Eye color',
    helpText:
      'I-130 Part 3 item 5 (Pt3Line5_EyeColor[0..8] — camel-cased on I-130, vs lowercase Eyecolor on I-485). Nine values: blue, black, brown, gray, green, hazel, maroon, pink, unknown_or_other. Widget index order differs from I-485 — I-130 lists Blue/Brown/Hazel/Pink/Maroon/Green/Gray/Black/Unknown.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i130Consumer,
        widgetValueProjection: {
          blue: 'Pt3Line5_EyeColor[0]',
          brown: 'Pt3Line5_EyeColor[1]',
          hazel: 'Pt3Line5_EyeColor[2]',
          pink: 'Pt3Line5_EyeColor[3]',
          maroon: 'Pt3Line5_EyeColor[4]',
          green: 'Pt3Line5_EyeColor[5]',
          gray: 'Pt3Line5_EyeColor[6]',
          black: 'Pt3Line5_EyeColor[7]',
          unknown_or_other: 'Pt3Line5_EyeColor[8]',
        },
      },
    ],
  },
  {
    id: defineFieldId('sponsor.biographic.hair_color'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_identity',
    partyRole: 'sponsor',
    label: 'Hair color',
    helpText:
      'I-130 Part 3 item 6 (Pt3Line6_HairColor[0..8] — camel-cased on I-130, vs lowercase Haircolor on I-485). Nine values: bald, black, blond, brown, gray, red, sandy, white, unknown_or_other. Index order matches I-485.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i130Consumer,
        widgetValueProjection: {
          bald: 'Pt3Line6_HairColor[0]',
          black: 'Pt3Line6_HairColor[1]',
          blond: 'Pt3Line6_HairColor[2]',
          brown: 'Pt3Line6_HairColor[3]',
          gray: 'Pt3Line6_HairColor[4]',
          red: 'Pt3Line6_HairColor[5]',
          sandy: 'Pt3Line6_HairColor[6]',
          white: 'Pt3Line6_HairColor[7]',
          unknown_or_other: 'Pt3Line6_HairColor[8]',
        },
      },
    ],
  },
] as const satisfies readonly FieldDefinition[];

const petitionTypeRelationshipFieldId = defineFieldId(
  'application.petition_type.relationship',
);

const requiredWhenRelationshipIsChild: RuleExpression = {
  op: 'equals',
  fieldId: petitionTypeRelationshipFieldId,
  value: 'child',
};

export const petitionTypeFields = [
  {
    id: petitionTypeRelationshipFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    label: 'Relationship being petitioned for',
    helpText:
      'I-130 Part 1 item 1 — "I am filing this petition for my (Select only one box)". One-of-N radio across four named widgets (Pt1Line1_Spouse, Pt1Line1_Child, Pt1Line1_Parent, Pt1Line1_Siblings). Four values: spouse, child, parent, sibling. The PDF engine projects the single semantic value onto the matching widget by widget basename. For an AOS marriage packet this defaults to "spouse"; the same catalog later serves non-spouse relative petitions.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i130Consumer,
        required: true,
        widgetValueProjection: {
          spouse: 'Pt1Line1_Spouse[0]',
          child: 'Pt1Line1_Child[0]',
          parent: 'Pt1Line1_Parent[0]',
          sibling: 'Pt1Line1_Siblings[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('application.petition_type.child_subtype'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    label: 'Child sub-type',
    helpText:
      'I-130 Part 1 item 2 — "If you are filing this petition for your child or parent, select the box that describes your relationship". One-of-N radio across four named widgets (Pt1Line2_InWedlock, Pt1Line2_Stepchild, Pt1Line2_OutOfWedlock, Pt1Line2_AdoptedChild). Four values: in_wedlock, stepchild, out_of_wedlock, adopted. The PDF prompt mentions "child or parent", but all four sub-type options describe a child; domain gates this field on relationship === "child".',
    valueType: 'select',
    required: requiredWhenRelationshipIsChild,
    visibility: requiredWhenRelationshipIsChild,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i130Consumer,
        required: false,
        widgetValueProjection: {
          in_wedlock: 'Pt1Line2_InWedlock[0]',
          stepchild: 'Pt1Line2_Stepchild[0]',
          out_of_wedlock: 'Pt1Line2_OutOfWedlock[0]',
          adopted: 'Pt1Line2_AdoptedChild[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('application.petition_type.sibling_by_adoption'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    label:
      'If the beneficiary is your brother/sister, are you related by adoption?',
    helpText:
      'I-130 Part 1 item 3 — Yes/No. Two widgets (Pt1Line3_Yes, Pt1Line3_No). PDF prompt is gated on relationship = Brother/Sister, but the widgets exist unconditionally. USCIS requires an answer when the relationship is Brother/Sister; for other relationships the answer is left blank on the PDF. Domain stores a single boolean; the PDF engine writes Yes or No to the matching widget.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('application.petition_type.petitioner_status_through_adoption'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    label:
      'Did you gain lawful permanent resident status or citizenship through adoption?',
    helpText:
      'I-130 Part 1 item 4 — Yes/No. Two widgets (Pt1Line4_Yes, Pt1Line4_No). Always asked, regardless of relationship type. Screens for petitioners who themselves became LPRs or U.S. citizens through adoption (8 USC 1101(b)(1)(E)–(G)), which restricts the relatives they can sponsor. If Yes, USCIS expects an explanation in Part 9 (Additional Information).',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  // Phase B1 — Petitioner naturalization details (I-130 Pt2Line37).
  // Required when petitioner gained citizenship via naturalization.
  {
    id: defineFieldId('application.petitioner.naturalization.certificate_number'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    label: 'Certificate of Naturalization or Citizenship number',
    helpText: 'I-130 Pt2Line37a (item 39.a in printed form).',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 15 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('application.petitioner.naturalization.place_of_issuance'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    label: 'Certificate place of issuance',
    helpText: 'I-130 Pt2Line37b.',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('application.petitioner.naturalization.date_of_issuance'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    label: 'Certificate date of issuance',
    helpText: 'I-130 Pt2Line37c.',
    valueType: 'date',
    required: false,
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  // Phase B3 — Petitioner LPR class/date/place of admission (I-130 Pt2Line40).
  // Required when sponsor.citizenship.status = lawful_permanent_resident.
  {
    id: defineFieldId('application.petitioner.lpr.class_of_admission'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    label: 'LPR class of admission',
    helpText: 'I-130 Pt2Line40a. E.g., "IR-1", "CR-1", "F2A".',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 8 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('application.petitioner.lpr.date_of_admission'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    label: 'LPR date of admission',
    helpText: 'I-130 Pt2Line40b.',
    valueType: 'date',
    required: false,
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('application.petitioner.lpr.place.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    label: 'LPR place of admission — city or town',
    helpText: 'I-130 Pt2Line40d (item 40.c city).',
    valueType: 'text',
    required: false,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('application.petitioner.lpr.place.state'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    label: 'LPR place of admission — state',
    helpText: 'I-130 Pt2Line40e (item 40.d state).',
    valueType: 'select',
    required: false,
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('application.petitioner.lpr.through_marriage'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    label:
      'Did you gain lawful permanent resident status through marriage to a U.S. citizen or LPR?',
    helpText: 'I-130 Pt2Line41. LPR-only.',
    valueType: 'boolean',
    required: false,
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  // Phase A — i-485 immigration status fields (Item 14/15)
  {
    id: defineFieldId('beneficiary.immigration_status.expiration_date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Expiration date of current immigration status',
    helpText:
      'I-485 01/20/25 Pt1Line15_Date. Date the applicant\'s current immigration status expires, or D/S for duration of status.',
    valueType: 'date',
    required: false,
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
  // i-485 last_arrival visa issue date (Item 10)
  {
    id: defineFieldId('beneficiary.last_arrival.visa_issue_date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Date nonimmigrant visa was issued',
    helpText:
      'I-485 01/20/25 Pt1Line10_NonImmDate. Date the nonimmigrant visa used on most recent arrival was issued.',
    valueType: 'date',
    required: false,
    pdfConsumers: [{ ...i485Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

// =====================================================================
// Cross-consumer widget binding sweep
// =====================================================================
// Fills the last Phase 0 sanity-check gaps surfaced by reverse-coverage
// of PDF widgets against the inventory:
//   - sponsor.basis_for_filing (I-864 Part 1 role selector)
//   - beneficiary.safe_address.* (I-485 Part 1 Line 13 — VAWA / SIJ /
//     T / U safe-mailing block, gated on a request flag)
//   - preparer.attorney.state_bar_number / .uscis_online_account_number
//     (G-28 attorney representation widgets across all five forms)

export const sponsorBasisForFilingFields = [
  {
    id: defineFieldId('sponsor.basis.for_filing'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_basis_for_filing',
    partyRole: 'sponsor',
    label: 'I-864 basis for filing',
    helpText:
      'I-864 Part 1 item 1 — single role selector across six checkboxes (P1_Line1a-f_CB[0..5]) plus a sub-radio for 1.e first/second of two joint sponsors (P1_Line1e1_Checkbox[0..1]). Enum values: petitioner (1.a), i140_petitioner (1.b), 5_pct_owner (1.c), joint_sponsor_only (1.d), joint_sponsor_1of2 (1.e + sub-radio[0]), joint_sponsor_2of2 (1.e + sub-radio[1]), substitute_sponsor (1.f). For an AOS marriage packet the typical value is "petitioner" (the U.S. citizen / LPR spouse self-files I-864). The projection below maps each enum to its primary checkbox; the joint-sponsor sub-radio (P1_Line1e1_Checkbox[0..1]) is a secondary widget the form engine handles together with the value.',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i864Consumer,
        required: true,
        widgetValueProjection: {
          petitioner: 'P1_Line1a-f_CB[0]',
          i140_petitioner: 'P1_Line1a-f_CB[1]',
          '5_pct_owner': 'P1_Line1a-f_CB[2]',
          joint_sponsor_only: 'P1_Line1a-f_CB[3]',
          // 1.e + sub-radio: the primary 1.e checkbox is shared between the
          // 1-of-2 and 2-of-2 cases; the disambiguator is the sub-radio
          // P1_Line1e1_Checkbox[N].
          joint_sponsor_1of2: [
            'P1_Line1a-f_CB[4]',
            'P1_Line1e1_Checkbox[0]',
          ],
          joint_sponsor_2of2: [
            'P1_Line1a-f_CB[4]',
            'P1_Line1e1_Checkbox[1]',
          ],
          substitute_sponsor: 'P1_Line1a-f_CB[5]',
        },
      },
    ],
  },
  {
    id: defineFieldId('sponsor.basis.i140_relationship'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_basis_for_filing',
    partyRole: 'sponsor',
    label: 'Relationship to the I-140 beneficiary',
    helpText:
      'I-864 Part 1 item 1.b free-text — relationship between the I-140 petitioning entity and the intending immigrant. Bound to widget P1_Line1b_Relationship[0]. Required only when basis_for_filing = i140_petitioner.',
    valueType: 'text',
    required: {
      op: 'equals',
      fieldId: defineFieldId('sponsor.basis.for_filing'),
      value: 'i140_petitioner',
    },
    visibility: {
      op: 'equals',
      fieldId: defineFieldId('sponsor.basis.for_filing'),
      value: 'i140_petitioner',
    },
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.basis.ownership_entity_name'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_basis_for_filing',
    partyRole: 'sponsor',
    label: 'Name of entity in which sponsor has a 5% ownership interest',
    helpText:
      'I-864 Part 1 item 1.c free-text — the entity the sponsor owns 5%+ of. Bound to widget P1_Line1c_InterestIn[0]. Required only when basis_for_filing = 5_pct_owner.',
    valueType: 'text',
    required: {
      op: 'equals',
      fieldId: defineFieldId('sponsor.basis.for_filing'),
      value: '5_pct_owner',
    },
    visibility: {
      op: 'equals',
      fieldId: defineFieldId('sponsor.basis.for_filing'),
      value: '5_pct_owner',
    },
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.basis.ownership_relationship'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_basis_for_filing',
    partyRole: 'sponsor',
    label: '5% owner relationship to the I-140 beneficiary',
    helpText:
      'I-864 Part 1 item 1.c free-text — relationship between the 5%-owning sponsor and the intending immigrant. Bound to widget P1_Line1c_Relationship[0]. Required only when basis_for_filing = 5_pct_owner.',
    valueType: 'text',
    required: {
      op: 'equals',
      fieldId: defineFieldId('sponsor.basis.for_filing'),
      value: '5_pct_owner',
    },
    visibility: {
      op: 'equals',
      fieldId: defineFieldId('sponsor.basis.for_filing'),
      value: '5_pct_owner',
    },
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.basis.substitute_relationship'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_basis_for_filing',
    partyRole: 'sponsor',
    label: 'Substitute sponsor relationship to the intending immigrant',
    helpText:
      'I-864 Part 1 item 1.f free-text — relationship between the substitute sponsor and the intending immigrant when the original petitioner is deceased. Bound to widget P1_Line1f_Relationship[0]. Required only when basis_for_filing = substitute_sponsor.',
    valueType: 'text',
    required: {
      op: 'equals',
      fieldId: defineFieldId('sponsor.basis.for_filing'),
      value: 'substitute_sponsor',
    },
    visibility: {
      op: 'equals',
      fieldId: defineFieldId('sponsor.basis.for_filing'),
      value: 'substitute_sponsor',
    },
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.petition.immigrant_is_in_us'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_basis_for_filing',
    partyRole: 'sponsor',
    label: 'Principal immigrant is currently in the United States',
    helpText:
      'I-864 P3_Line1_Checkbox[0] (Yes) / P3_Line1_Checkbox[1] (No). USCIS Part 3 Item 1. For Adjustment of Status (I-485), the immigrant is always present in the United States — this checkbox is always Yes. Auto-set to true for AOS filings.',
    valueType: 'boolean',
    required: false,
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.petition.total_immigrants_count'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'sponsor_basis_for_filing',
    partyRole: 'sponsor',
    label: 'Total number of immigrants on this I-864',
    helpText:
      'I-864 P3_Line28_TotalNumberofImmigrants. Part 3 Item 28 — total count of immigrants being sponsored on this single I-864 (principal immigrant + any accompanying relatives). For a straightforward marriage-based AOS with one immigrant, this is 1. Auto-set to 1 unless edited.',
    valueType: 'number',
    required: false,
    pdfConsumers: [{ ...i864Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

// Safe address (VAWA / SIJ / T / U) — removed 2026-05-26. This app
// supports marriage-based AOS only, so the safe-address path was
// never reachable; the section was a dead end in the sidebar that
// confused real applicants. The I-485 Pt1Line13_* widgets remain in
// the PDF template and simply stay blank.

// Preparer-attorney widget fields removed 2026-05-26 along with the
// preparer section. The AttorneyStateBarNumber / USCISOnlineAcctNumber
// header widgets remain in the PDF template and stay blank.

const beneficiaryWasEverInProceedingsFieldId = defineFieldId(
  'beneficiary.proceedings.was_ever_in_proceedings',
);

const requiredWhenBeneficiaryWasEverInProceedings: RuleExpression = {
  op: 'equals',
  fieldId: beneficiaryWasEverInProceedingsFieldId,
  value: true,
};

export const beneficiaryProceedingsFields = [
  {
    id: beneficiaryWasEverInProceedingsFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Has the beneficiary EVER been in immigration proceedings?',
    helpText:
      'I-130 Pt4Line28_Yes/No (printed item 53). Gates the proceeding-type, location, and date sub-fields. Asked of every I-130 petitioner regardless of AOS vs consular processing.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('beneficiary.proceedings.type'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Type of immigration proceedings',
    helpText:
      'I-130 Pt4Line54 (printed item 54). Bound to the four checkbox widgets Pt4Line54_Removal, _Exclusion (Exclusion / Deportation), _Rescission, _JudicialProceedings (Other Judicial Proceedings). PDF engine writes whichever checkbox matches the selected value.',
    valueType: 'select',
    required: requiredWhenBeneficiaryWasEverInProceedings,
    visibility: requiredWhenBeneficiaryWasEverInProceedings,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i130Consumer,
        required: false,
        widgetValueProjection: {
          removal: 'Pt4Line54_Removal[0]',
          exclusion: 'Pt4Line54_Exclusion[0]',
          rescission: 'Pt4Line54_Rescission[0]',
          judicial_proceedings: 'Pt4Line54_JudicialProceedings[0]',
        },
      },
    ],
  },
  {
    id: defineFieldId('beneficiary.proceedings.place.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'City or town where proceedings occurred',
    helpText:
      'I-130 Pt4Line55a_CityOrTown (printed item 55.A). Required when the beneficiary has been in proceedings.',
    valueType: 'text',
    required: requiredWhenBeneficiaryWasEverInProceedings,
    visibility: requiredWhenBeneficiaryWasEverInProceedings,
    validation: [{ type: 'required' }, { type: 'max_length', value: 50 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.proceedings.place.state'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'State where proceedings occurred',
    helpText:
      'I-130 Pt4Line55b_State (printed item 55.B). U.S. state choice list.',
    valueType: 'select',
    required: requiredWhenBeneficiaryWasEverInProceedings,
    visibility: requiredWhenBeneficiaryWasEverInProceedings,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('beneficiary.proceedings.date'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'immigration_history',
    partyRole: 'beneficiary',
    label: 'Date of proceedings',
    helpText:
      'I-130 Pt4Line56_Date (printed item 56). Date the proceedings occurred or were initiated.',
    valueType: 'date',
    required: requiredWhenBeneficiaryWasEverInProceedings,
    visibility: requiredWhenBeneficiaryWasEverInProceedings,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

const sponsorHasFiledPriorI130FieldId = defineFieldId(
  'sponsor.prior_i130_filings.has_filed_prior',
);

const requiredWhenSponsorFiledPriorI130: RuleExpression = {
  op: 'equals',
  fieldId: sponsorHasFiledPriorI130FieldId,
  value: true,
};

export const sponsorPriorI130FilingsFields = [
  {
    id: sponsorHasFiledPriorI130FieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    partyRole: 'sponsor',
    label:
      'Has the petitioner EVER previously filed an I-130 for this beneficiary or any other alien?',
    helpText:
      'I-130 Part4Line1_Yes/No (printed Part 5 item 1; widget name retains the "Part4Line1" prefix from a prior edition). USCIS uses this question to detect serial petitioning and marriage fraud, so it is mandatory on every I-130 regardless of AOS vs consular processing.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: true }],
  },
  {
    id: defineFieldId('sponsor.prior_i130_filings.beneficiary_name.last'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    partyRole: 'sponsor',
    label: 'Prior I-130 beneficiary family/last name',
    helpText:
      'I-130 Pt5Line2a_FamilyName (printed Part 5 item 2.A). Only the most-recent prior beneficiary fits in the printed slot; additional prior filings overflow to Part 10 additional information.',
    valueType: 'text',
    required: requiredWhenSponsorFiledPriorI130,
    visibility: requiredWhenSponsorFiledPriorI130,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.prior_i130_filings.beneficiary_name.first'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    partyRole: 'sponsor',
    label: 'Prior I-130 beneficiary given/first name',
    helpText: 'I-130 Pt5Line2b_GivenName (printed Part 5 item 2.B).',
    valueType: 'text',
    required: requiredWhenSponsorFiledPriorI130,
    visibility: requiredWhenSponsorFiledPriorI130,
    validation: [{ type: 'required' }, { type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.prior_i130_filings.beneficiary_name.middle'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    partyRole: 'sponsor',
    label: 'Prior I-130 beneficiary middle name',
    helpText: 'I-130 Pt5Line2c_MiddleName (printed Part 5 item 2.C).',
    valueType: 'text',
    required: false,
    visibility: requiredWhenSponsorFiledPriorI130,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.prior_i130_filings.place_of_filing.city'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    partyRole: 'sponsor',
    label: 'City or town where prior I-130 was filed',
    helpText: 'I-130 Pt5Line3a_CityOrTown (printed Part 5 item 3.A).',
    valueType: 'text',
    required: requiredWhenSponsorFiledPriorI130,
    visibility: requiredWhenSponsorFiledPriorI130,
    validation: [{ type: 'required' }, { type: 'max_length', value: 50 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.prior_i130_filings.place_of_filing.state'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    partyRole: 'sponsor',
    label: 'State where prior I-130 was filed',
    helpText:
      'I-130 Pt5Line3b_State (printed Part 5 item 3.B). U.S. state choice list.',
    valueType: 'select',
    required: requiredWhenSponsorFiledPriorI130,
    visibility: requiredWhenSponsorFiledPriorI130,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.prior_i130_filings.date_filed'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    partyRole: 'sponsor',
    label: 'Date prior I-130 was filed',
    helpText: 'I-130 Pt5Line4_DateFiled (printed Part 5 item 4).',
    valueType: 'date',
    required: requiredWhenSponsorFiledPriorI130,
    visibility: requiredWhenSponsorFiledPriorI130,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
  {
    id: defineFieldId('sponsor.prior_i130_filings.result'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'petition_type',
    partyRole: 'sponsor',
    label: 'Result of prior I-130 (e.g., approved, denied, withdrawn)',
    helpText:
      'I-130 Pt5Line5_Result (printed Part 5 item 5). The PDF widget is a free-text field with a 33-character limit; the form gives "approved, denied, withdrawn" as examples but does not constrain the input.',
    valueType: 'text',
    required: requiredWhenSponsorFiledPriorI130,
    visibility: requiredWhenSponsorFiledPriorI130,
    validation: [{ type: 'required' }, { type: 'max_length', value: 33 }],
    pdfConsumers: [{ ...i130Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

// ---------------------------------------------------------------------
// I-131 Application for Travel Document (advance parole).
// ---------------------------------------------------------------------

const i131RequestedTypeFieldId = defineFieldId(
  'application.travel_document.requested_type',
);

const i131PreviousDocumentReceivedFieldId = defineFieldId(
  'application.travel_document.previous_document_received',
);

const requiredWhenI131PreviousDocumentReceived: RuleExpression = {
  op: 'equals',
  fieldId: i131PreviousDocumentReceivedFieldId,
  value: true,
};

const advanceParoleApplicationTypes: readonly string[] = [
  'advance_parole_i485_pending',
  'advance_parole_i589_pending',
  'advance_parole_i821_initial',
  'advance_parole_deferred_departure',
  'advance_parole_i821d_approved',
  'advance_parole_i914_approved',
  'advance_parole_i918_approved',
  'advance_parole_parolee',
  'advance_parole_i817_approved',
  'advance_parole_i687_pending',
  'advance_parole_v_status',
  'advance_parole_cnmi_long_term',
  'advance_parole_other',
];

const requiredWhenI131RequestedTypeIsAdvanceParole: RuleExpression = {
  op: 'one_of',
  fieldId: i131RequestedTypeFieldId,
  values: [...advanceParoleApplicationTypes],
};

export const i131ApplicationTypeFields = [
  {
    id: i131RequestedTypeFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_document',
    partyRole: 'beneficiary',
    label: 'Travel document requested',
    helpText:
      'I-131 Part 1 application-type selector. The PDF carries 13 individual checkbox widgets (form1[0].P1[0].CB_AppType[0..4], form1[0].P2[0].CB_AppType[0..12], form1[0].P3[0].CB_AppType[0..8], form1[0].P4[0].CB_AppType[0..9]) and free-text receipt-number widgets (P1_Line4..P1_Line8C). Item 1 = re-entry permit (LPR). Items 2-3 = refugee travel document (asylee/refugee). Item 4 = TPS travel authorization. Items 5.A-5.M = advance parole (the AOS marriage relevant case is 5.A "pending Form I-485"). Items 6, 7, 8.A-C, 9, 10.A-D, 11 = initial parole / parole-in-place / re-parole (out of scope for AOS marriage; documented in phase-0-plan).',
    valueType: 'select',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [
      {
        ...i131Consumer,
        required: true,
        // 13-way advance-parole projection. Per the I-131 2025-01 dump
        // (docs/rebuild/i-131-2025-01-applicant-and-travel-field-dump.md
        // Part 1 — Application Type), 5.A lives on the P1 subform while
        // 5.B–5.M live on the P2 subform. The CB_AppType[N] indices are
        // not sequential — USCIS authoring chose semantic ordering over
        // index ordering — so each enum-to-widget pairing must be
        // resolved by name, not by computed index.
        widgetValueProjection: {
          advance_parole_i485_pending: 'P1[0].CB_AppType[4]',
          advance_parole_i589_pending: 'P2[0].CB_AppType[12]',
          advance_parole_i821_initial: 'P2[0].CB_AppType[10]',
          advance_parole_deferred_departure: 'P2[0].CB_AppType[7]',
          advance_parole_i821d_approved: 'P2[0].CB_AppType[8]',
          advance_parole_i914_approved: 'P2[0].CB_AppType[9]',
          advance_parole_i918_approved: 'P2[0].CB_AppType[1]',
          advance_parole_parolee: 'P2[0].CB_AppType[0]',
          advance_parole_i817_approved: 'P2[0].CB_AppType[2]',
          advance_parole_i687_pending: 'P2[0].CB_AppType[3]',
          advance_parole_v_status: 'P2[0].CB_AppType[4]',
          advance_parole_cnmi_long_term: 'P2[0].CB_AppType[11]',
          advance_parole_other: 'P2[0].CB_AppType[5]',
        },
      },
    ],
  },
  {
    id: defineFieldId(
      'application.travel_document.requested_type_receipt_number',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_document',
    partyRole: 'beneficiary',
    label: 'I-485 receipt number (optional)',
    helpText:
      'I-131 Part 1 receipt-number free-text widgets. The widget written depends on requested_type: 5.A → P1_Line5A (pending I-485), 5.B → P1_Line5B (pending I-589), 5.C → P1_Line5C (pending I-821), 5.E → P1_Line5E (approved I-821D), 5.F → P1_Line5F (approved I-914), 5.G → P1_Line5G (approved I-918), 5.H → P1_Line5H (parolee COA), 5.I → P1_Line5I (approved I-817), 5.J → P1_Line5J (pending I-687), 5.K → P1_Line5K (approved V status), 5.L → P1_Line5L (CNMI long-term), 5.M → P1_Line5M (other explanation). Phase 1 PDF engine selects the widget by requested_type and writes the receipt number there.',
    valueType: 'text',
    required: false,
    visibility: requiredWhenI131RequestedTypeIsAdvanceParole,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i131Consumer, required: false }],
  },
  {
    id: i131PreviousDocumentReceivedFieldId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_document',
    partyRole: 'beneficiary',
    label:
      'Were you previously issued a re-entry permit, refugee travel document, advance parole document, or TPS travel authorization?',
    helpText:
      'I-131 Part 4 item 2.A and 3.A Yes/No widgets (P4_Line2a_YesNo, P4_Line3a_YesNo, P4_Line4_YesNo, and the underlying P4_Line1_YesNo physical-presence question). True when the applicant has ever previously received a USCIS-issued travel document.',
    valueType: 'boolean',
    required: true,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i131Consumer, required: true }],
  },
  {
    id: defineFieldId(
      'application.travel_document.previous_document_issue_date',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_document',
    partyRole: 'beneficiary',
    label: 'Date the previous travel document was issued',
    helpText:
      'I-131 Part 4 item 2.B (P4_Line2b_DateIssued) and item 3.B (P4_Line3b_DateIssued). Conditional on previous_document_received = true.',
    valueType: 'date',
    required: requiredWhenI131PreviousDocumentReceived,
    visibility: requiredWhenI131PreviousDocumentReceived,
    validation: [],
    pdfConsumers: [{ ...i131Consumer, required: false }],
  },
  {
    id: defineFieldId(
      'application.travel_document.previous_document_disposition',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_document',
    partyRole: 'beneficiary',
    label: 'Disposition of the previous travel document',
    helpText:
      'I-131 Part 4 item 2.C (P4_Line2c_Disposition) and item 3.C (P4_Line3c_Disposition). Free-text describing what happened to the prior document (e.g., "expired", "returned to USCIS", "lost").',
    valueType: 'text',
    required: requiredWhenI131PreviousDocumentReceived,
    visibility: requiredWhenI131PreviousDocumentReceived,
    validation: [{ type: 'max_length', value: 80 }],
    pdfConsumers: [{ ...i131Consumer, required: false }],
  },
] as const satisfies readonly FieldDefinition[];

export const i131TripDetailsFields = [
  {
    id: defineFieldId(
      'application.travel_document.trip.proposed_departure_date',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_document',
    partyRole: 'beneficiary',
    label: 'Date of intended departure from the United States',
    helpText:
      'I-131 Part 7 item 1 (P7_Line1_DateOfDeparture). USCIS uses this to gauge AP processing urgency.',
    valueType: 'date',
    required: requiredWhenI131RequestedTypeIsAdvanceParole,
    visibility: requiredWhenI131RequestedTypeIsAdvanceParole,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i131Consumer, required: true }],
  },
  {
    id: defineFieldId('application.travel_document.trip.purpose_description'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_document',
    partyRole: 'beneficiary',
    label: 'Purpose of the proposed trip',
    helpText:
      'I-131 Part 7 item 2 (P7_Line2_Purpose). Free-text. Overflow goes to Part 13 additional information per the printed instruction.',
    valueType: 'text',
    required: requiredWhenI131RequestedTypeIsAdvanceParole,
    visibility: requiredWhenI131RequestedTypeIsAdvanceParole,
    validation: [{ type: 'required' }, { type: 'max_length', value: 500 }],
    pdfConsumers: [{ ...i131Consumer, required: true }],
  },
  {
    id: defineFieldId('application.travel_document.trip.countries_to_visit'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_document',
    partyRole: 'beneficiary',
    label: 'List of countries the applicant intends to visit',
    helpText:
      'I-131 Part 7 item 3 (P7_Line3_ListCountries). Free-text list per USCIS (no enum). Overflow goes to Part 13 additional information.',
    valueType: 'text',
    required: requiredWhenI131RequestedTypeIsAdvanceParole,
    visibility: requiredWhenI131RequestedTypeIsAdvanceParole,
    validation: [{ type: 'required' }, { type: 'max_length', value: 500 }],
    pdfConsumers: [{ ...i131Consumer, required: true }],
  },
  {
    id: defineFieldId('application.travel_document.trip.intended_use'),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_document',
    partyRole: 'beneficiary',
    label: 'Number of trips the applicant intends to use the document for',
    helpText:
      'I-131 Part 7 item 4 (P7_Line4_CB[0] = One Trip, P7_Line4_CB[1] = More than one trip). Two-value select.',
    valueType: 'select',
    required: requiredWhenI131RequestedTypeIsAdvanceParole,
    visibility: requiredWhenI131RequestedTypeIsAdvanceParole,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i131Consumer, required: true }],
  },
  {
    id: defineFieldId(
      'application.travel_document.trip.expected_length_days',
    ),
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    sectionId: 'travel_document',
    partyRole: 'beneficiary',
    label: 'Expected length of trip (in days)',
    helpText:
      'I-131 Part 7 item 5 (P7_Line5_ExpectedLengthTrip). Integer day count.',
    valueType: 'number',
    required: requiredWhenI131RequestedTypeIsAdvanceParole,
    visibility: requiredWhenI131RequestedTypeIsAdvanceParole,
    validation: [{ type: 'required' }],
    pdfConsumers: [{ ...i131Consumer, required: true }],
  },
] as const satisfies readonly FieldDefinition[];

const aosV1FieldDefinitionsRaw = [
  ...beneficiaryLegalNameFields,
  ...beneficiaryOtherNamesFields,
  ...beneficiaryBirthNameFields,
  ...beneficiaryBirthFields,
  ...beneficiaryDemographicFields,
  ...beneficiaryIdentifierFields,
  ...beneficiaryCurrentPhysicalAddressFields,
  ...beneficiaryIntendedAddressFields,
  ...beneficiaryForeignAddressFields,
  ...beneficiaryMostRecentForeignAddressFields,
  ...beneficiaryMailingAddressFields,
  ...beneficiaryContactFields,
  ...sponsorLegalNameFields,
  ...sponsorOtherNamesFields,
  ...sponsorBirthFields,
  ...sponsorDemographicFields,
  ...sponsorIdentifierFields,
  ...sponsorMailingAddressFields,
  ...sponsorCurrentPhysicalAddressFields,
  ...sponsorAddressHistoryFields,
  ...beneficiaryMaritalHistoryFields,
  ...beneficiaryCurrentSpouseFields,
  ...beneficiaryPriorMarriagesFields,
  ...sponsorMaritalHistoryFields,
  ...sponsorCurrentSpouseFields,
  ...sponsorPriorMarriagesFields,
  ...beneficiaryChildrenCountFields,
  ...beneficiaryChildrenFields,
  ...sponsorHouseholdChildrenFields,
  ...sponsorSponsoredImmigrantsFields,
  ...beneficiaryParentsFields,
  ...sponsorParentsFields,
  ...beneficiarySsnFields,
  ...beneficiaryImmigrationHistoryFields,
  ...beneficiaryPriorImmigrantVisaFields,
  ...beneficiaryAddressHistoryFields,
  ...beneficiaryEmploymentHistoryFields,
  ...sponsorEmploymentHistoryFields,
  ...sponsorCurrentEmploymentFields,
  ...sponsorIncomeFields,
  ...sponsorTaxHistoryFields,
  ...sponsorAssetsFields,
  ...eligibilityGeneralFields,
  ...eligibilityCriminalFields,
  ...eligibilitySecurityFields,
  ...eligibilityPublicChargeFields,
  ...eligibilityImmigrationViolationFields,
  ...eligibilityMiscellaneousFields,
  ...beneficiaryIdentityDocumentFields,
  ...beneficiaryStatusDocumentFields,
  ...marriageRelationshipDocumentFields,
  ...sponsorIdentityDocumentFields,
  ...sponsorFinancialDocumentFields,
  ...childDocumentFields,
  ...conditionalScreeningDocumentFields,
  ...applicantSignatureFields,
  ...sponsorSignatureFields,
  ...additionalInformationFields,
  ...eligibilityCategoryFields,
  ...accommodationRequestFields,
  ...beneficiaryBiographicFields,
  ...sponsorBiographicFields,
  ...petitionTypeFields,
  ...sponsorBasisForFilingFields,
  ...beneficiaryProceedingsFields,
  ...sponsorPriorI130FilingsFields,
  ...i131ApplicationTypeFields,
  ...i131TripDetailsFields,
] as const satisfies readonly FieldDefinition[];

export const aosV1FieldDefinitions: readonly FieldDefinition[] =
  aosV1FieldDefinitionsRaw.map(hydrateFieldWidgets);
